import type { InsightQuestionCollectionPolicy } from './api.types';
/**
 * Public request/response contracts for the Segmently CLI API facade.
 *
 * These types describe the stable `/api/cli/v1/*` boundary. They should not
 * expose Builder Service, Agent, Stripe, or Firestore implementation details.
 */

import type { StrategyBlockInstruction } from '@segmently/schema-models/strategy';
import type { ArticleSiteSettings, FunnelVariable } from '@segmently/schema-models/v2';
import type { PaddleCheckoutPolicy } from '@segmently/schema-models/payments';
import type { BlockSubtypeInput } from './blockSubtype.types';
import type {
  BlockLibraryExampleDoc,
  BlockLibraryExampleInput,
  LibraryAdaptationMode,
  LibraryAdaptationResolvedEvidenceSummary,
  LibraryAdaptationState,
  LibraryAdaptationTaskRouterPreflight,
  LibraryEvidenceRef,
  LibraryEvidenceSelection,
  LibraryVariableConflictSummary,
  LibraryVariableResolution,
} from './blockLibrary.types';
import type {
  WebABPublicationRecord,
  WebABPublicationVariantSummary,
} from './webABAllocationEpoch';
import type {
  ActivationIntent,
  ActivationItem,
  ActivationProjection,
  IntentChannel,
  IntentGoal,
  IntentPlatform,
  IntentProductAnalytics,
} from './launchActivation.types';
import {
  INTENT_CHANNELS,
  INTENT_GOALS,
  INTENT_PLATFORMS,
  INTENT_PRODUCT_ANALYTICS,
} from './launchActivation.types';

export type CliApiVersion = '1';

export type CliServiceTokenScope =
  | 'projects:read'
  | 'projects:write'
  | 'funnels:read'
  | 'funnels:write'
  | 'product:read'
  | 'product:write'
  | 'themes:read'
  | 'themes:write'
  | 'analytics:read'
  | 'analytics:write'
  | 'domains:read'
  | 'domains:write'
  | 'launch:read'
  | 'launch:write'
  | 'assets:write'
  | 'content-plan:read'
  | 'content-plan:write'
  | 'social:read'
  | 'social:publish'
  | 'stripe:read'
  | 'stripe:write'
  | 'paddle:read'
  | 'paddle:write'
  | 'generate:write'
  | 'tasks:read'
  | 'tasks:write'
  | 'publish:read'
  | 'publish:write';

export type CliScreenTypeVariableType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'date'
  | 'enum'
  | 'enum[]';

export type CliScreenTypeConditionOperator =
  | 'lt'
  | 'gt'
  | 'lte'
  | 'gte'
  | 'eq'
  | 'neq'
  | 'in'
  | 'notIn'
  | 'containsAll'
  | 'containsAny'
  | 'exists'
  | 'notExists';

export type CliScreenContentContract = 'SimplifiedV2Content';

export interface CliResponseEnvelope<TData> {
  apiVersion: CliApiVersion;
  data: TData;
  warnings: CliWarning[];
}

export interface CliWarning {
  code: string;
  message: string;
}

export interface CliProjectSummary {
  id: string;
  name: string;
  projectName?: string;
  description?: string;
  role?: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CliProjectDetail extends CliProjectSummary {
  settings?: Record<string, unknown>;
  raw?: Record<string, unknown>;
}

/** One acquisition channel on the project doc (consumed by audience generation). */
export interface CliProjectTrafficChannel {
  channel: string;
  label: string;
  /** Observed traffic share, 0-100. */
  percentage: number;
  priority: number;
}

export interface CliProjectUpdateRequest {
  projectName?: string;
  name?: string;
  description?: string;
  category?: string;
  platform?: string;
  platforms?: string[];
  targetCountries?: string[];
  toneOfVoice?: string;
  designDescription?: string;
  previousUserStepDescription?: string;
  url?: string;
  trafficChannels?: CliProjectTrafficChannel[];
}

export interface CliProjectUpdateData {
  projectId: string;
  project: CliProjectDetail;
  updated: boolean;
  changedFields: string[];
}

export type CliProjectMemberRole = 'owner' | 'admin' | 'member' | 'editor' | 'viewer';

export interface CliProjectMemberSummary {
  uid: string;
  email: string;
  role: CliProjectMemberRole;
  projectId: string;
  projectName: string;
  memberPath: string;
  userProjectPath: string;
}

export interface CliProjectMemberGrantRequest {
  email: string;
  role?: CliProjectMemberRole;
}

export interface CliProjectMemberGrantData {
  member: CliProjectMemberSummary;
  created: boolean;
  updatedPaths: string[];
}

export interface CliGuideContentItem {
  id?: string | null;
  title?: string | null;
  description?: string | null;
  subtitle?: string | null;
  fullArticleLink?: string | null;
  image?: string | null;
  video?: string | null;
}

export interface CliGuideDocument {
  id: string;
  name: string;
  key: string;
  fullArticleLink?: string | null;
  content: CliGuideContentItem[];
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CliGuideInput {
  id?: string;
  name: string;
  key: string;
  fullArticleLink?: string | null;
  content?: CliGuideContentItem[];
  createdAt?: string | null;
}

export interface CliGuideListData {
  guides: CliGuideDocument[];
  total: number;
}

export interface CliGuideExportData {
  schemaVersion: 'segmently.cli.guides.v1';
  exportedAt: string;
  guideCount: number;
  guides: CliGuideDocument[];
}

export interface CliGuideMutationData {
  guide: CliGuideDocument;
  created?: boolean;
  updated?: boolean;
}

export interface CliGuideDeleteData {
  id: string;
  key: string;
  deleted: true;
}

export type CliGuideImportMode = 'upsert';

export interface CliGuideImportRequest {
  schemaVersion?: string;
  mode?: CliGuideImportMode;
  dryRun?: boolean;
  guides: CliGuideInput[];
}

export interface CliGuideImportOperation {
  action: 'create' | 'update' | 'skip';
  id?: string;
  key: string;
  name: string;
  reason?: string;
}

export interface CliGuideImportData {
  schemaVersion: 'segmently.cli.guides.v1';
  importedAt: string;
  dryRun: boolean;
  mode: CliGuideImportMode;
  created: number;
  updated: number;
  skipped: number;
  operations: CliGuideImportOperation[];
}

export type CliContentPlanPromptArtifactKind = 'markdown' | 'json';

export interface CliContentPlanPromptArtifactSummary {
  filename: string;
  kind: CliContentPlanPromptArtifactKind;
  gcsPath: string;
  size?: number | null;
  updatedAt?: string | null;
  contentType?: string | null;
}

export interface CliContentPlanPromptArtifactValidation {
  valid: boolean;
  validator: string;
  errors: string[];
}

export interface CliContentPlanPromptArtifactListData {
  prefix: string;
  total: number;
  artifacts: CliContentPlanPromptArtifactSummary[];
}

export interface CliContentPlanPromptArtifactData {
  filename: string;
  kind: CliContentPlanPromptArtifactKind;
  content: string | unknown;
  checksum: string;
  size: number;
  validation?: CliContentPlanPromptArtifactValidation;
}

export interface CliContentPlanPromptArtifactExportItem extends CliContentPlanPromptArtifactData {}

export interface CliContentPlanPromptArtifactExportData {
  schemaVersion: 'segmently.cli.content-plan-prompts.v1';
  exportedAt: string;
  prefix: string;
  artifactCount: number;
  artifacts: CliContentPlanPromptArtifactExportItem[];
}

export interface CliContentPlanPromptArtifactApplyRequest {
  filename: string;
  content: string | unknown;
  dryRun?: boolean;
}

export interface CliContentPlanPromptArtifactApplyData {
  filename: string;
  kind: CliContentPlanPromptArtifactKind;
  dryRun: boolean;
  updated: boolean;
  checksum: string;
  size: number;
  validation: CliContentPlanPromptArtifactValidation;
}

export interface CliContentPlanPromptArtifactImportRequest {
  schemaVersion?: string;
  dryRun?: boolean;
  artifacts: CliContentPlanPromptArtifactApplyRequest[];
}

export interface CliContentPlanPromptArtifactImportData {
  schemaVersion: 'segmently.cli.content-plan-prompts.v1';
  importedAt: string;
  dryRun: boolean;
  artifactCount: number;
  updated: number;
  operations: CliContentPlanPromptArtifactApplyData[];
}

export interface CliContentPlanPromptCacheClearData {
  cleared: true;
  prefix: 'contentPlan';
  clearedAt: string;
}

export type CliPromptArtifactKind = 'markdown' | 'json';

export interface CliPromptArtifactSummary {
  filename: string;
  kind: CliPromptArtifactKind;
  scope: string;
  gcsPath: string;
  size?: number | null;
  updatedAt?: string | null;
  contentType?: string | null;
}

export interface CliPromptArtifactValidation {
  valid: boolean;
  validator: string;
  errors: string[];
}

export interface CliPromptArtifactListData {
  schemaVersion: 'segmently.cli.prompts.v1';
  prefix: string;
  allowedPrefixes: string[];
  total: number;
  artifacts: CliPromptArtifactSummary[];
}

export interface CliPromptArtifactData {
  schemaVersion: 'segmently.cli.prompts.v1';
  filename: string;
  kind: CliPromptArtifactKind;
  scope: string;
  content: string | unknown;
  checksum: string;
  size: number;
  validation?: CliPromptArtifactValidation;
}

export interface CliPromptArtifactExportItem extends CliPromptArtifactData {}

export interface CliPromptArtifactExportData {
  schemaVersion: 'segmently.cli.prompts.v1';
  exportedAt: string;
  prefix: string;
  allowedPrefixes: string[];
  artifactCount: number;
  artifacts: CliPromptArtifactExportItem[];
}

export interface CliPromptArtifactApplyRequest {
  filename: string;
  content: string | unknown;
  dryRun?: boolean;
}

export interface CliPromptArtifactApplyData {
  schemaVersion: 'segmently.cli.prompts.v1';
  filename: string;
  kind: CliPromptArtifactKind;
  scope: string;
  dryRun: boolean;
  updated: boolean;
  checksum: string;
  size: number;
  validation: CliPromptArtifactValidation;
}

export interface CliPromptArtifactImportRequest {
  schemaVersion?: string;
  dryRun?: boolean;
  artifacts: CliPromptArtifactApplyRequest[];
}

export interface CliPromptArtifactImportData {
  schemaVersion: 'segmently.cli.prompts.v1';
  importedAt: string;
  dryRun: boolean;
  artifactCount: number;
  updated: number;
  operations: CliPromptArtifactApplyData[];
}

export interface CliPromptCacheClearData {
  schemaVersion: 'segmently.cli.prompts.v1';
  cleared: true;
  prefix: string;
  clearedAt: string;
}

export interface CliFunnelSummary {
  id: string;
  projectId: string;
  name: string;
  type?: string | null;
  status?: string | null;
  screenGraphType?: string | null;
  folderId?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CliFunnelDetail extends CliFunnelSummary {
  defaultLanguage?: string | null;
  languages?: string[];
  raw?: Record<string, unknown>;
}

export interface CliFunnelFolderSummary {
  id: string;
  projectId: string;
  name: string;
  parentFolderId?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CliFunnelFolderCreateRequest {
  name: string;
  parentFolderId?: string | null;
}

export interface CliFunnelFolderListData {
  folders: CliFunnelFolderSummary[];
}

export interface CliFunnelFolderCreateData {
  folder: CliFunnelFolderSummary;
}

export interface CliFunnelFolderDetailData {
  folder: CliFunnelFolderSummary;
  funnels: CliFunnelSummary[];
}

export interface CliFunnelFolderCleanupRequest {
  folderIds?: string[];
  names?: string[];
  namePrefixes?: string[];
  deleteContents?: boolean;
  dryRun?: boolean;
  limit?: number;
}

export interface CliFunnelFolderCleanupOperation {
  folder: CliFunnelFolderSummary;
  funnelCount: number;
  deletedFolder: boolean;
  deletedFunnels: number;
  skippedReason?: string;
}

export interface CliFunnelFolderCleanupData {
  projectId: string;
  dryRun: boolean;
  deleteContents: boolean;
  matchedFolderCount: number;
  deletedFolderCount: number;
  deletedFunnelCount: number;
  operations: CliFunnelFolderCleanupOperation[];
}

export interface CliFunnelMoveRequest {
  folderId?: string | null;
}

export interface CliFunnelMoveData {
  funnel: CliFunnelDetail;
}

export interface CliFunnelVersionSummary {
  id: string;
  projectId: string;
  funnelId: string;
  name?: string | null;
  schemaVersion?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export type CliFunnelPlatform = 'ios' | 'android' | 'web';

export interface CliFunnelCreateRequest {
  name: string;
  defaultLanguage?: string;
  languages?: string[];
  folderId?: string | null;
  platform?: CliFunnelPlatform;
  projectThemeId?: string | null;
  sourceThemeId?: string | null;
  versionName?: string;
}

export interface CliFunnelCreateData {
  funnel: CliFunnelDetail;
  version: CliFunnelVersionSummary;
  editorUrl: string;
}

export interface CliFunnelCloneRequest {
  name: string;
  sourceProjectId?: string | null;
  versionName?: string;
  folderId?: string | null;
}

export interface CliFunnelCloneSourceRef {
  projectId: string;
  funnelId: string;
  versionId: string;
}

export interface CliFunnelCloneData extends CliFunnelCreateData {
  source: CliFunnelCloneSourceRef;
  clonedAt: string;
  screensCloned: number;
  themeSettingsCloned: number;
}

export type CliStrategyCompileThemeMode = 'strategy-current' | 'project-default';

export interface CliStrategyCompileRequest {
  name?: string;
  versionName?: string;
  folderId?: string | null;
  projectThemeId?: string | null;
  sourceThemeId?: string | null;
  themeMode?: CliStrategyCompileThemeMode;
  defaultLanguage?: string;
  languages?: string[];
  dryRun?: boolean;
  /**
   * Apply deterministic `repair-plan/v1` autofixes to the compiled
   * FlowDocument before the final healthcheck and optional persistence.
   * AI retries are never dispatched from the compile path.
   */
  repair?: boolean;
  /**
   * B7 severity profile. `blocking` is the default for new compiles after D62;
   * explicit `warning` preserves the compatibility path.
   */
  healthProfile?: import('./strategyAutoRepair.types').StrategyHealthProfile;
  /**
   * C9-S4: skip blocks not reachable from the launch block. Excluded block ids
   * are recorded in the transform manifest (`excludedBlockEntityIds`); push
   * default-skips them and an absorb-lift never deletes them from the strategy.
   */
  excludeUnreachable?: boolean;
  /**
   * Spec A: which strategy version to compile. Defaults to the strategy's
   * `currentVersion` (latest baked). Orthogonal to the funnel `versionId`.
   */
  strategyVersion?: string;
  /**
   * Spec A: force a brand-new onboarding funnel instead of minting a new version
   * under the strategy's canonical `lastCompiledTo` funnel (A3-5).
   */
  newFunnel?: boolean;
}

/** Spec A (A7): bake the working head into an immutable strategy version. */
export interface CliStrategyBakeRequest {
  name?: string;
  description?: string;
  /** Optional reviewed manifest; Bake refuses drift instead of substituting new pins. */
  expectedInputManifest?: Record<string, unknown>;
  /** Re-bake even if the strategy is already baked (default false → idempotent no-op). */
  force?: boolean;
}

export interface CliStrategyBakeData {
  projectId: string;
  strategyId: string;
  versionId: string;
  status: 'baked';
  parentVersion: string;
  name: string;
  contentHash: string;
  blockCount: number;
  screenCount: number;
  bakedAt: string;
  /** True when the strategy was already baked and this call was a no-op. */
  reused: boolean;
}

export interface CliStrategyCompileData {
  compiledAt: string;
  projectId: string;
  strategyId: string;
  /** Resolved staged health profile used for this compile. */
  healthProfile?: import('./strategyAutoRepair.types').StrategyHealthProfile;
  /** Spec A (A7-2): the resolved strategy version this compile read from. */
  strategyVersionId?: string;
  /** R7/P1 (no-dead-end): true when this compile auto-baked the draft head into strategyVersionId. */
  autoBaked?: boolean;
  /** Display name of the auto-baked version (e.g. "v2"); set only when autoBaked is true. */
  autoBakedVersionName?: string;
  blockCount: number;
  screenCount: number;
  launchScreenId: string;
  warnings: string[];
  /** C9-S7: serialized transform-manifest size in bytes. */
  manifestBytes?: number;
  /** C9-S7: version screensGraph byte budget the manifest counts against. */
  manifestBudgetBytes?: number;
  /** C9-S7: manifestBytes as a percentage of the budget (one decimal). */
  manifestBudgetPct?: number;
  flowDocument: CliFlowDocument;
  funnel?: CliFunnelDetail;
  version?: CliFunnelVersionSummary;
  editorUrl?: string;
  /** Per-screen rows (all would_create) for the two-step compile confirm UI. */
  plan?: CliStrategyPushPlanEntry[];
  /** Flow healthcheck over the compiled document (informational, never blocks compile). */
  healthcheck?: CliFunnelHealthcheckSummary;
  /**
   * Deterministic verification suite report (`strategy-health-report/v1`,
   * B2). Same validator set as the pipeline task-doc persistence — surfaces
   * may render it but never re-derive findings.
   */
  strategyHealthReport?: CliStrategyHealthReport;
  /** Present only when the request enabled compile-side deterministic repair. */
  compileRepair?: CliStrategyCompileRepairData;
}

export interface CliStrategyCompileRepairData {
  applyVersion: string;
  requested: true;
  healthFindingsBefore: number;
  healthFindingsAfter: number;
  planSummary: {
    findingsTotal: number;
    autofixes: number;
    retries: number;
    surfaced: number;
    unrouted: number;
  };
  summary: {
    autofixesTotal: number;
    applied: number;
    skipped: number;
    deferred: number;
  };
  applied: Array<{
    fix: NonNullable<CliRepairPlannedAction['fix']>;
    target: CliRepairActionTarget;
    findings: string[];
    evidence: string;
  }>;
  skipped: Array<{
    fix: NonNullable<CliRepairPlannedAction['fix']>;
    target: CliRepairActionTarget;
    findings: string[];
    reason: 'ambiguous' | 'not-found';
    detail: string;
  }>;
  deferred: { total: number; retries: number; surfaced: number };
  truncated?: { applied?: number; skipped?: number };
}

/** One finding of the `strategy-health-report/v1` verification suite. */
export interface CliStrategyHealthFinding {
  /** Stable check id, e.g. 'layout.screenOverlap', 'router.pathTrace'. */
  checkId: string;
  /** Machine-readable defect shape within the check, e.g. 'collision-pair'. */
  kind: string;
  severity: CliFunnelAuditIssueSeverity;
  message: string;
  screenId?: string;
  blockKey?: string;
  actionKey?: string;
  variableKey?: string;
  details?: Record<string, unknown>;
}

export interface CliStrategyHealthFindingTarget {
  blockKey?: string;
  /** Screen id in the owning block's local namespace, suitable for the nested canvas route. */
  screenId?: string;
}

/**
 * Resolve persisted and legacy health findings to the block canvas route.
 * Strategy-head reports namespace screens as `blockKey::localId`; compiled
 * reports use `blockKey__localId`. New reports also persist blockKey, while
 * this parser keeps older reports navigable.
 */
export function resolveCliStrategyHealthFindingTarget(
  finding: Pick<CliStrategyHealthFinding, 'blockKey' | 'screenId'>,
): CliStrategyHealthFindingTarget {
  const rawScreenId = finding.screenId?.trim() || undefined;
  let blockKey = finding.blockKey?.trim() || undefined;
  let separator: '::' | '__' | undefined;
  let separatorIndex = -1;

  if (rawScreenId) {
    for (const candidate of ['::', '__'] as const) {
      const index = rawScreenId.indexOf(candidate);
      if (index > 0 && (separatorIndex < 0 || index < separatorIndex)) {
        separator = candidate;
        separatorIndex = index;
      }
    }
    if (!blockKey && separatorIndex > 0) {
      blockKey = rawScreenId.slice(0, separatorIndex);
    }
  }

  let screenId = rawScreenId;
  if (rawScreenId && blockKey) {
    for (const candidate of ['::', '__'] as const) {
      const prefix = `${blockKey}${candidate}`;
      if (rawScreenId.startsWith(prefix)) {
        screenId = rawScreenId.slice(prefix.length) || undefined;
        separator = candidate;
        break;
      }
    }
  }
  if (rawScreenId && !finding.blockKey && separator && separatorIndex > 0) {
    screenId = rawScreenId.slice(separatorIndex + separator.length) || undefined;
  }

  return {
    ...(blockKey ? { blockKey } : {}),
    ...(screenId ? { screenId } : {}),
  };
}

/**
 * Persistable projection of `strategy-health-report/v1` (source of truth:
 * strategy-blocks/utils/strategyHealthReport.ts). Written to the parent task
 * doc after generation and to the funnel version doc after compile; findings
 * are truncated per check (summary.byCheck keeps full counts,
 * summary.truncatedChecks names what was cut — truncation is never silent).
 */
export interface CliStrategyHealthReport {
  reportVersion: string;
  input: { kind: string; launchScreenId: string | null };
  findings: CliStrategyHealthFinding[];
  summary: {
    findingsTotal: number;
    emittedFindings: number;
    bySeverity: Record<string, number>;
    byCheck: Record<string, number>;
    truncatedChecks: string[];
    skippedChecks: string[];
  };
  /** B0 analyzer counters carried verbatim (`strategy-diagnostics-report/v1`). */
  diagnosticsSummary: Record<string, number>;
  /** Headless per-goal route proof evidence (flow-document inputs with a task router). */
  routerPathTrace?: {
    status: string;
    goalsScreenId?: string;
    distinctPaths: number;
    goals: Array<Record<string, unknown>>;
  };
}

export interface CliStrategyLiftRequest {
  name?: string;
  targetStrategyId?: string;
  replaceExisting?: boolean;
  dryRun?: boolean;
  resolutions?: CliStrategySyncResolutions;
  /** Spec D4: lift syncs the onboarding theme onto the strategy by default; opt out here. */
  skipThemeSync?: boolean;
}

export interface CliStrategyLiftData {
  liftedAt: string;
  projectId: string;
  funnelId: string;
  versionId: string;
  strategyId?: string;
  /** R7/P2 (no-dead-end): true when this write auto-reopened a baked/published head as draft. */
  autoReopened?: boolean;
  strategy?: {
    id: string;
    name: string;
    strategyType?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
  };
  blockCount: number;
  screenCount: number;
  warnings: string[];
  blocks: Array<Record<string, unknown>>;
  looseScreens?: CliStrategyLooseScreenEntry[];
  foreignTagIgnored?: CliStrategyForeignTagEntry[];
  variableDiff?: CliStrategyVariableDiffEntry[];
  /** Spec D4: true when the onboarding theme was synced onto the strategy as part of this lift. */
  themeSynced?: boolean;
  /** Spec D4 (U10 GET): a drifted draft head was auto-baked into a checkpoint before the merge. */
  autoBaked?: boolean;
  autoBakedVersionId?: string;
}

export type CliStrategyPushScreenAction = 'would_create' | 'would_replace' | 'would_preserve' | 'would_delete';

// ── Strategy sync decision surface (all additive to the public CLI contract) ──

export type CliStrategySyncChangeKind = 'copy' | 'edge' | 'asset';
export type CliStrategyScreenConflictResolution = 'funnel' | 'strategy';
export type CliStrategyVariableResolution = 'preserve' | 'overwrite' | 'fork' | 'drop';

export interface CliStrategyAssetSlotResolution {
  /** Default true — union both sides' experiments as alternates. */
  merge?: boolean;
  /** Winner written to selectedExperimentId and mirrored into screen content. */
  selectedExperimentId?: string;
}

export interface CliStrategyLooseScreenResolution {
  action: 'keep-loose' | 'existing-block' | 'new-block';
  /** existing-block: target block entity id. */
  blockEntityId?: string;
  /** new-block: display name for the created block. */
  newBlockName?: string;
}

/**
 * User decisions collected in the sync confirm dialog (or --resolutions-file).
 * All maps are keyed by the ids reported in the corresponding dry-run payload.
 */
export interface CliStrategySyncResolutions {
  /** Conflict rows only, keyed by global screen id. Default: 'funnel'. */
  screens?: Record<string, CliStrategyScreenConflictResolution>;
  variables?: Record<string, CliStrategyVariableResolution>;
  /** screenId (global) -> slotKey -> resolution. */
  assetSlots?: Record<string, Record<string, CliStrategyAssetSlotResolution>>;
  /** Lift/pull only, keyed by global screen id. */
  looseScreens?: Record<string, CliStrategyLooseScreenResolution>;
}

export interface CliStrategyVariableDiffEntry {
  variableId: string;
  name?: string;
  type?: string;
  status: 'added' | 'removed' | 'changed' | 'unchanged';
  referenced: boolean;
  referencedByScreenIds: string[];
  defaultResolution: CliStrategyVariableResolution;
}

export interface CliStrategyAssetSlotDiffEntry {
  screenId: string;
  slotKey: string;
  status: 'source-only' | 'target-only' | 'both-equal' | 'both-diverged';
  sourceExperimentIds: string[];
  targetExperimentIds: string[];
  sourceOnlyExperimentIds: string[];
  targetOnlyExperimentIds: string[];
  conflictingExperimentIds: string[];
  sourceSelectedExperimentId?: string;
  targetSelectedExperimentId?: string;
  /** False = slot key has no content mapping; row is metadata-only. */
  hasMappedContentPath: boolean;
}

export interface CliStrategyForkedVariableEntry {
  variableId: string;
  forkedId: string;
}

export interface CliStrategyLooseScreenEntry {
  screenId: string;
  screenName?: string;
  screenType?: string;
  /** Candidate blocks ranked by edge adjacency (max 3); UI shows them first in the placement picker. */
  suggestedBlockEntityIds?: string[];
}

export interface CliStrategyForeignTagEntry {
  screenId: string;
  blockTag: string;
  strategyTag: string;
}

export interface CliStrategyPushPlanEntry {
  screenId: string;
  blockEntityId: string;
  action: CliStrategyPushScreenAction;
  edgesUpdated?: boolean;
  conflict?: boolean;
  screenName?: string;
  changeKinds?: CliStrategySyncChangeKind[];
  /** Why a replace happened without a clean three-way diff. */
  reason?: 'no-baseline' | 'not-simplifiable';
  /** Conflict rows: which side won under the applied resolution. */
  resolvedWith?: CliStrategyScreenConflictResolution;
  assetSlots?: CliStrategyAssetSlotDiffEntry[];
}

export interface CliStrategyBlockPullRequest {
  funnelId?: string;
  versionId?: string;
  allowEmpty?: boolean;
  dryRun?: boolean;
  resolutions?: CliStrategySyncResolutions;
  /** Spec D4: pull syncs the onboarding theme onto the strategy by default; opt out here. */
  skipThemeSync?: boolean;
}

export interface CliStrategyBlockPullData {
  pulledAt: string;
  projectId: string;
  strategyId: string;
  blockId: string;
  blockEntityId: string;
  funnelId: string;
  versionId: string;
  dryRun: boolean;
  /** R7/P2 (no-dead-end): true when this write auto-reopened a baked/published head as draft. */
  autoReopened?: boolean;
  screenCount: number;
  previousScreenCount: number;
  plan: Array<{ screenId: string; action: CliStrategyPushScreenAction }>;
  firstScreenId?: string;
  edges: Array<Record<string, unknown>>;
  warnings: string[];
  screens?: Array<Record<string, unknown>>;
  /** Unassigned funnel screens that a resolution can place into a block. */
  looseScreens?: CliStrategyLooseScreenEntry[];
  variableDiff?: CliStrategyVariableDiffEntry[];
  /** Spec D4: true when the onboarding theme was synced onto the strategy as part of this pull. */
  themeSynced?: boolean;
  /** Spec D4 (U10 GET): a drifted draft head was auto-baked into a checkpoint before the merge. */
  autoBaked?: boolean;
  autoBakedVersionId?: string;
}

export interface CliStrategyPushRequest {
  funnelId?: string;
  versionId?: string;
  /** Block document ids or entity ids; omitted = whole strategy. */
  blockIds?: string[];
  dryRun?: boolean;
  resolutions?: CliStrategySyncResolutions;
  /** Opt-in gate: healthcheck errors on the merged document → 422, zero writes. */
  gateOnHealthcheck?: boolean;
}

export interface CliStrategyPushData {
  pushedAt: string;
  projectId: string;
  strategyId: string;
  funnelId: string;
  versionId: string;
  dryRun: boolean;
  /** R7/P2 (no-dead-end): true when the fork write-back auto-reopened a baked/published head as draft. */
  autoReopened?: boolean;
  blockScope: string[];
  created: number;
  replaced: number;
  preserved: number;
  deleted: number;
  plan: CliStrategyPushPlanEntry[];
  launchScreenId?: string;
  warnings: string[];
  variableDiff?: CliStrategyVariableDiffEntry[];
  /** Variables forked during this push and written back to the strategy. */
  forkedVariables?: CliStrategyForkedVariableEntry[];
  assetDiff?: CliStrategyAssetSlotDiffEntry[];
  /** Flow healthcheck over the merged document (blocks only with gateOnHealthcheck). */
  healthcheck?: CliFunnelHealthcheckSummary;
  /** Spec D4 (U10): how the merge-snapshot gate routed this write. */
  syncGate?: CliStrategySyncGateOutcome;
  /**
   * C9-S5: present when syncGate.action='branched' AND published surfaces still
   * point at the frozen branchedFrom version — the operator ship chain
   * (repoint → publish placement → republish AB) with ready inputs.
   */
  shipHint?: CliStrategyPushShipHint;
}

/** C9-S5: one placement that must be repointed at the branch to ship the push. */
export interface CliStrategyPushShipHintPlacement {
  webPlacementId: string;
  alias?: string;
  name?: string;
  status: 'published' | 'draft';
  /** Published AB tests routing traffic through this placement. */
  abTestIds: string[];
  /** Ready `web-placements apply` manifest repointing funnelRef at the branch. */
  repointManifest: {
    webPlacement: {
      id: string;
      name?: string;
      alias?: string;
      funnelRef: { funnelId: string; versionId: string };
    };
  };
}

/** C9-S5: operator ship chain emitted after a branched push. */
export interface CliStrategyPushShipHint {
  branchedFrom: string;
  targetVersionId: string;
  placements: CliStrategyPushShipHintPlacement[];
  abTests: Array<{ abTestId: string; alias?: string; name?: string; status: string }>;
  /** Ready commands in execution order: repoint → publish placement(s) → republish AB(s). */
  commands: string[];
}

export type CliStrategyThemeSyncDirection = 'strategy-to-onboarding' | 'onboarding-to-strategy';

export interface CliStrategyThemeSyncRequest {
  strategyId?: string;
  versionId?: string;
  direction?: CliStrategyThemeSyncDirection;
  dryRun?: boolean;
}

export interface CliStrategyThemeSyncData {
  syncedAt: string;
  projectId: string;
  funnelId: string;
  versionId?: string | null;
  strategyId: string;
  direction: CliStrategyThemeSyncDirection;
  dryRun: boolean;
  source: 'strategy' | 'onboarding';
  target: 'strategy' | 'onboarding';
  sourceThemeId?: string | null;
  themeName?: string | null;
}

// ── Spec D4: U10 merge-snapshot gates + candidate pipeline ──

/**
 * Spec D4 (U10): drift marker stamped on a funnel version after EVERY
 * strategy→funnel write (push/compile/candidate). A later push compares the
 * version's current canonical content hash against `contentHash` — mismatch
 * means unreproducible manual edits exist and the merge must checkpoint first.
 */
export interface CliStrategySyncMarker {
  contentHash: string;
  syncedAt: string;
  source: 'push' | 'compile' | 'candidate';
  strategyVersionId?: string;
}

/** Spec D4 (U10): merge-snapshot gate outcome echoed on strategy→onboarding writes. */
export interface CliStrategySyncGateOutcome {
  /**
   * in-place — clean editable target, merged directly;
   * branched — frozen target was NEVER written, the merge landed on a new editable version;
   * checkpointed — manual drift was preserved in a version copy before merging in place.
   */
  action: 'in-place' | 'branched' | 'checkpointed';
  /** The version the write actually landed on (differs from the requested one when branched). */
  targetVersionId: string;
  /** action='branched': the frozen version the editable branch was minted from. */
  branchedFrom?: string;
  /** action='checkpointed': the version copy holding the pre-merge manual edits. */
  checkpointVersionId?: string;
}

export type CliCandidateStatus = 'preparing' | 'ready' | 'stale' | 'failed' | 'discarded' | 'published';

export type CliCandidateStep =
  | 'gate'
  | 'absorb'
  | 'theme-sync'
  | 'weights'
  | 'recompute'
  | 'diff'
  | 'regen'
  | 'compile';

export interface CliCandidateStepLogEntry {
  step: CliCandidateStep;
  status: 'running' | 'done' | 'error';
  at: string;
  detail?: string;
}

export interface CliCandidateWeightUpdate {
  audienceId: string;
  actorId: string;
  weight: number;
}

/** The lineage pair + artifacts a candidate was computed from (G2 staleness compares toAnalysisId vs the lineage head). */
export interface CliCandidateBasis {
  fromAnalysisId: string;
  toAnalysisId: string;
  strategyId: string;
  strategyVersionId?: string;
  funnelId?: string;
}

export interface CliCandidateAffectedBlock {
  blockId: string;
  blockName?: string;
  insightIds: string[];
}

/** Blocks whose just-absorbed manual polish is replaced by the regen (G1 card disclosure). */
export interface CliCandidateReplacedPolishEntry {
  blockId: string;
  blockName?: string;
  changeKinds: string[];
  /** Version copy holding the pre-merge polish (recovery pointer — polish never lost). */
  checkpointVersionId?: string;
}

/** Spec D4 (§5.1): the candidate record persisted on the experiment doc; UI polls it while preparing. */
export interface CliCandidateRecord {
  status: CliCandidateStatus;
  attempt: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  basis: CliCandidateBasis;
  weightsApplied: Array<{ audienceId: string; actorId: string; from?: number; to: number }>;
  affectedBlocks: CliCandidateAffectedBlock[];
  replacedPolish: CliCandidateReplacedPolishEntry[];
  result?: {
    funnelVersionId: string;
    strategyVersionId?: string;
    editorUrl?: string;
    compiledAt: string;
  };
  stepLog: CliCandidateStepLogEntry[];
  error?: { step: CliCandidateStep; message: string };
}

export interface CliCandidatePrepareRequest {
  /** Card path: run the full pipeline for this experiment. Absent → dialog path (an experiment record is created). */
  experimentId?: string;
  fromAnalysisId?: string;
  toAnalysisId?: string;
  strategyId?: string;
  weights?: CliCandidateWeightUpdate[];
  /** Re-prepare over an existing candidate (rebuild / crash recovery); bumps attempt. */
  force?: boolean;
}

export interface CliCandidatePrepareData {
  experimentId: string;
  candidate: CliCandidateRecord;
}

export interface CliCandidateStatusData {
  experimentId: string;
  candidate: CliCandidateRecord | null;
}

export interface CliStrategySummary {
  id: string;
  projectId: string;
  name: string;
  description?: string | null;
  strategyType?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  /**
   * SG-A: the RAW persisted setting. ABSENT on a legacy head that never chose a
   * mode, which keeps it distinguishable from a head that explicitly chose
   * `parallel`. A corrupted stored value is still surfaced rather than hidden:
   * a corrupt STRING is echoed byte-for-byte; a corrupt non-string is
   * JSON-encoded so this field stays a string (`42` -> `"42"`, `{}` -> `"{}"`).
   */
  blockGenerationMode?: string;
  /**
   * SG-A: the mode generation will actually use — always present. `null` ONLY
   * when the stored value is invalid; a corrupted head is never normalised to
   * `parallel` (see `blockGenerationModeError`).
   */
  effectiveBlockGenerationMode?: import('./strategyGenerationMode.types').StrategyBlockGenerationMode | null;
  /** SG-A: why `effectiveBlockGenerationMode` is null. Present only on a corrupted head. */
  blockGenerationModeError?: string;
  raw?: Record<string, unknown>;
}

export interface CliStrategyCreateRequest {
  strategyId?: string;
  name: string;
  description?: string;
  strategyType?: string;
  toneOfVoice?: string;
  designDescription?: string;
  sourceData?: Record<string, unknown>;
  sourceDataMapping?: unknown[];
  strategyVariables?: Record<string, unknown>;
  variables?: Record<string, unknown>;
  /** SG-A: strategy-owned block dispatch mode. Omit to leave the head without the field (= parallel). */
  blockGenerationMode?: import('./strategyGenerationMode.types').StrategyBlockGenerationMode;
  replaceExisting?: boolean;
}

export type CliStrategyUpdateRequest = Partial<Omit<CliStrategyCreateRequest, 'strategyId' | 'replaceExisting'>>;

export type { CliStrategyHeadWriteRequest } from './strategyHeadWrite';

export interface CliStrategyData {
  strategy: CliStrategySummary;
  /** Phase V (V3): present when requested via ?staleness=1 and the strategy has an inputManifest. */
  staleness?: import('./strategyManifest.types').CliStrategyStaleness;
  /** R7/P2 (no-dead-end): true when this write auto-reopened a baked/published head as draft. */
  autoReopened?: boolean;
  /** Explicit read-only S2 inspection for one immutable strategy version. */
  causalProtocol?: CliStrategyCausalProtocolInspection;
}

export interface CliStrategyCausalProtocolInspection {
  schemaVersion: 1;
  strategyId: string;
  strategyVersionId: string;
  strategyVersionContentHash: string;
  protocolHash: string;
  certificationStatus: 'certified' | 'limited' | 'blocked';
  pins: {
    decisionContextHash: string;
    causalGraphHash: string;
    experimentContractHash: string;
    promptRef: string;
    modelRef: string;
    variablePackageRefs: string[];
  };
  lineage: Record<string, unknown>;
  inputDiff: Record<string, unknown>;
  limitations: string[];
  theory: Array<Record<string, unknown>>;
  recommendations: Array<Record<string, unknown>>;
  requiresHumanReview: true;
}

export interface CliStrategyCreateData extends CliStrategyData {}

export interface CliStrategyBlockSummary {
  id: string;
  projectId: string;
  strategyId: string;
  entityId?: string | null;
  name: string;
  blockType?: string | null;
  customSubtype?: string | null;
  instructions?: StrategyBlockInstruction[];
  order?: number | null;
  firstScreenId?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  raw?: Record<string, unknown>;
}

export interface CliStrategyBlockListData {
  projectId: string;
  strategyId: string;
  blocks: CliStrategyBlockSummary[];
}

export interface CliStrategyBlockData {
  projectId: string;
  strategyId: string;
  block: CliStrategyBlockSummary;
  /** R7/P2 (no-dead-end): true when this write auto-reopened a baked/published head as draft. */
  autoReopened?: boolean;
}

export interface CliStrategyBlockPatchRequest extends Record<string, unknown> {
  customSubtype?: string;
  instructions?: StrategyBlockInstruction[];
}

export interface CliBlockSubtypeInput extends BlockSubtypeInput {}

export interface CliBlockSubtypeRecord extends CliBlockSubtypeInput {
  id: string;
  usageCount?: number;
  createdBy?: string | null;
  createdAt?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

export interface CliBlockSubtypeListData {
  projectId: string;
  subtypes: CliBlockSubtypeRecord[];
}

export interface CliBlockSubtypeApplyData {
  projectId: string;
  subtype: CliBlockSubtypeRecord;
  created: boolean;
}

export type CliBlockSubtypePromotionStatus =
  | 'dry_run'
  | 'pending_schema'
  | 'completed'
  | 'already_promoted';

export interface CliBlockSubtypePromotionRequest {
  projectId: string;
  subtypeId: string;
  targetType: string;
  /** Dry-run is the default and is guaranteed to perform zero writes. */
  dryRun: boolean;
  /** Must be exactly true for a mutating request. */
  apply?: boolean;
  /** Exact confirmation text returned by the corresponding dry-run. */
  confirmation?: string;
  /** Receipt returned by a freshly recomputed dry-run. */
  dryRunChecksum?: string;
}

export interface CliBlockSubtypePromotionCollision {
  surface: 'schema_enum' | 'prompt_registry' | 'promotion_registry';
  code: 'target_exists' | 'prompt_exists' | 'target_claimed';
  blocking: boolean;
  detail: string;
}

export interface CliBlockSubtypePromotionOtherProjectUsage {
  projectId: string;
  affectedBlocks: number;
  samplePaths: string[];
}

export interface CliBlockSubtypePromotionData {
  promotionId: string;
  projectId: string;
  subtypeId: string;
  subtypeSlug: string;
  targetType: string;
  status: CliBlockSubtypePromotionStatus;
  dryRun: boolean;
  schemaPresent: boolean;
  dryRunChecksum: string;
  confirmation: string;
  collisions: CliBlockSubtypePromotionCollision[];
  promptDraft: {
    path: string;
    content: string;
    checksum: string;
    existingActiveVersion: number | null;
  };
  strategistTeachingDraft: {
    path: string;
    content: string;
    checksum: string;
    existingActiveVersion: number | null;
  };
  affected: {
    sourceProjectBlocks: number;
    sourceProjectPaths: string[];
    otherProjects: CliBlockSubtypePromotionOtherProjectUsage[];
    otherProjectsTruncated: boolean;
  };
  schemaChecklist: string[];
  promptRegistryVersion?: number;
  strategistTeachingRegistryVersion?: number;
  writes: {
    auditDocuments: number;
    targetClaimWrites: number;
    promptRegistryVersions: number;
    migratedBlocks: number;
  };
}

export type CliBlockSubtypePromotionRollbackStatus =
  | 'dry_run'
  | 'rolled_back'
  | 'already_rolled_back';

export interface CliBlockSubtypePromotionRollbackRequest {
  projectId: string;
  subtypeId: string;
  targetType: string;
  /** Dry-run is the default and is guaranteed to perform zero writes. */
  dryRun: boolean;
  apply?: boolean;
  confirmation?: string;
  dryRunChecksum?: string;
}

export interface CliBlockSubtypePromotionRollbackData {
  promotionId: string;
  projectId: string;
  subtypeId: string;
  subtypeSlug: string;
  targetType: string;
  status: CliBlockSubtypePromotionRollbackStatus;
  dryRun: boolean;
  dryRunChecksum: string;
  confirmation: string;
  playbookRestore: {
    path: string;
    currentActiveVersion: number | null;
    restoreActiveVersion: number | null;
  };
  strategistTeachingDraft: {
    path: string;
    content?: string;
    checksum?: string;
    existingActiveVersion: number | null;
  };
  affected: {
    journalBlocks: number;
    restorableBlocks: number;
    alreadyRestoredBlocks: number;
    sourceProjectPaths: string[];
  };
  writes: {
    auditDocuments: number;
    targetClaimWrites: number;
    promptPointerRestores: number;
    promptRegistryVersions: number;
    restoredBlocks: number;
  };
}

export type CliScreenExampleFidelity = 'strict-template' | 'pattern' | 'inspiration';

export interface CliScreenExampleInput {
  name: string;
  screenTypes: string[];
  blockTypes?: string[];
  fidelity: CliScreenExampleFidelity;
  teaches: string[];
  outputPattern: Record<string, unknown>;
  forbiddenCopyBehavior: string[];
  enabled: boolean;
}

export interface CliScreenExampleRecord extends CliScreenExampleInput {
  id: string;
  createdBy?: string | null;
  createdAt?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

export interface CliScreenExampleListData {
  projectId: string;
  examples: CliScreenExampleRecord[];
}

export interface CliScreenExampleApplyData {
  projectId: string;
  example: CliScreenExampleRecord;
  created: boolean;
}

export interface CliScreenExampleDeleteData {
  projectId: string;
  exampleId: string;
  deleted: true;
}

export interface CliStrategyBlockCloneRequest {
  name?: string;
  entityId?: string;
  copyScreens?: boolean;
  preserveEdges?: boolean;
  positionOffset?: { x?: number; y?: number };
}

export type CliStrategyBlockCloneStaleSeverity = 'none' | 'warning' | 'stale';

export interface CliStrategyBlockCloneStaleImpact {
  schemaVersion: 'strategy-block-clone-stale-impact/v1';
  operation: 'clone';
  createdAt: string;
  severity: CliStrategyBlockCloneStaleSeverity;
  reasons: string[];
  sourceBlockId: string;
  sourceEntityId?: string | null;
  clonedBlockId: string;
  clonedEntityId?: string | null;
  boundaryEdges: {
    status: 'none' | 'cleared' | 'preserved_requires_validation';
    preserveEdges: boolean;
    clearedByDefault: boolean;
    sourceCount: number;
    preservedCount: number;
    clearedCount: number;
    sourceTargets: string[];
    preservedTargets: string[];
    clearedTargets: string[];
  };
  downstream: {
    status: 'none' | 'potentially_stale' | 'requires_validation';
    selection: 'none' | 'outgoing_edge_targets';
    potentiallyAffectedBlockIds: string[];
    potentiallyAffectedEntityIds: string[];
    unresolvedTargetIds: string[];
    reason: string;
  };
  screens: {
    status: 'not_copied' | 'copied_requires_update' | 'copied_and_regenerated_later';
    copiedScreens: number;
    reason: string;
  };
  assets: {
    status: 'not_copied' | 'stale_cloned_screen_intents' | 'not_detected';
    reason: string;
  };
  retryScope: {
    recommended: Array<
      | 'proposal_review'
      | 'boundary_handoff_repair'
      | 'asset_plan_rebuild'
      | 'screen_regeneration'
      | 'downstream_validation'
    >;
  };
}

export interface CliStrategyBlockCloneData {
  projectId: string;
  strategyId: string;
  sourceBlockId: string;
  block: CliStrategyBlockSummary;
  clonedScreens: number;
  staleImpact?: CliStrategyBlockCloneStaleImpact;
  /** R7/P2 (no-dead-end): true when this write auto-reopened a baked/published head as draft. */
  autoReopened?: boolean;
}

// ============================================================================
// Block Examples Library — from-library instantiation
// ============================================================================

export type CliBlockLibraryScope = 'project' | 'system';

/** JSON-safe library record returned by the CLI facade (never a Firestore path). */
export type CliBlockLibraryExample = Omit<
  BlockLibraryExampleDoc,
  'createdAt' | 'updatedAt'
> & {
  createdAt: string;
  updatedAt?: string;
};

export interface CliBlockLibraryExampleSummary {
  id: string;
  schemaVersion: 2;
  name: string;
  description?: string;
  tags?: string[];
  scopeMetadata: BlockLibraryExampleInput['scopeMetadata'];
  screensCount: number;
  contractSummary: BlockLibraryExampleInput['contractSummary'];
  sourceRef: BlockLibraryExampleInput['sourceRef'];
  sourceChecksum: string;
  systemExampleId?: string;
  enabled: boolean;
  dataSemantics: {
    hasBlockDataUsage: boolean;
    mappedScreens: number;
    dataMappingEntries: number;
  };
  assetIntentCount: number;
  assetExampleCount: number;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface CliBlockLibraryListData {
  scope: CliBlockLibraryScope;
  projectId?: string;
  count: number;
  examples: CliBlockLibraryExampleSummary[];
}

export interface CliBlockLibraryGetData {
  scope: CliBlockLibraryScope;
  projectId?: string;
  example: CliBlockLibraryExample;
}

export interface CliBlockLibraryValidateRequest {
  example: BlockLibraryExampleInput;
}

export interface CliBlockLibraryValidateData {
  valid: boolean;
  errors: string[];
  sourceChecksum?: string;
  example?: BlockLibraryExampleInput;
}

export type CliBlockLibraryMutationOperation =
  | 'create'
  | 'update'
  | 'publish'
  | 'disable'
  | 'delete'
  | 'capture'
  | 'import';

export interface CliBlockLibraryMutationReceipt {
  schemaVersion: 'block-library-mutation-receipt/v1';
  operation: CliBlockLibraryMutationOperation;
  scope: CliBlockLibraryScope;
  projectId?: string;
  exampleId: string;
  sourceChecksum?: string;
  /** Catalogue-wide revision observed by dry-run. Any intervening write invalidates the receipt. */
  catalogRevision: number;
  /** Full canonical state of the target observed by dry-run. */
  target: CliBlockLibraryMutationReadback;
  /** Binds operation, scope/id, desired payload, catalogue revision, and target state. */
  dryRunChecksum: string;
}

export type CliBlockLibraryMutationReadback =
  | { exists: false }
  | { exists: true; checksum: string };

export interface CliBlockLibraryWriteRequest {
  example: BlockLibraryExampleInput;
  /** Mutations are dry-run unless apply=true. */
  apply?: boolean;
  /** Required on apply; copied from a fresh dry-run response. */
  dryRunChecksum?: string;
}

export interface CliBlockLibraryUpdateRequest {
  patch: Partial<BlockLibraryExampleInput>;
  apply?: boolean;
  dryRunChecksum?: string;
}

export interface CliBlockLibraryStateMutationRequest {
  apply?: boolean;
  dryRunChecksum?: string;
}

export interface CliBlockLibraryCaptureRequest {
  exampleId: string;
  name?: string;
  description?: string;
  tags?: string[];
  enabled?: boolean;
  locale?: string;
  apply?: boolean;
  dryRunChecksum?: string;
}

export interface CliBlockLibraryImportRequest {
  projectId: string;
  projectExampleId: string;
  systemExampleId: string;
  apply?: boolean;
  dryRunChecksum?: string;
}

export interface CliBlockLibraryMutationData {
  dryRun: boolean;
  applied: boolean;
  receipt: CliBlockLibraryMutationReceipt;
  /** Independent target read after apply, or the current target state for a dry-run. */
  readback: CliBlockLibraryMutationReadback;
  /** Exact validated payload reviewed by dry-run and bound by the receipt. */
  candidate?: BlockLibraryExampleInput;
  example?: CliBlockLibraryExample;
  deleted?: boolean;
}

export interface CliBlockLibraryCaptureData extends CliBlockLibraryMutationData {
  strategyId: string;
  blockId: string;
  captureSummary: {
    screens: number;
    contractInputs: number;
    contractOutputs: number;
    instructions: number;
    variables: string[];
    blueprintBytes: number;
    previewBytes: number;
    payloadBytes: number;
    assetExamples: number;
    hasAssetPlans: boolean;
  };
}

/**
 * Local authoring/eval adapter request. Builder owns the concrete compiler
 * input; shared CLI transport deliberately keeps the dependency one-way.
 */
export interface CliBlockLibraryPromptPacketRequest {
  input: Record<string, unknown>;
}

/** Exact JSON packet returned by Builder's update-from-library compiler. */
export type CliBlockLibraryPromptPacketData = Record<string, unknown>;

/** Per-example-variable mechanical mapping decision for the from-library insert. */
export type CliBlockLibraryVariableMappingEntry =
  | { action: 'bind'; targetVarId: string }
  | { action: 'create' }
  | { action: 'drop' };

export interface CliBlockFromLibraryRequest {
  source: 'project' | 'system';
  exampleId: string;
  /** Staged is the primary product path; immediate/as-is are explicit secondary modes. */
  mode?: LibraryAdaptationMode;
  position?: { x: number; y: number };
  name?: string;
  /**
   * Example-variable-id → mapping decision. Absent = server auto-resolution
   * from computeContractFit (exact-id + compatible → bind; else create).
   * `bind` is validated server-side: type match AND referencedValues ⊆ target
   * options — violations 400 naming the incompatible values.
   */
  variableMapping?: Record<string, CliBlockLibraryVariableMappingEntry>;
  /**
   * Deprecated compatibility alias. `false` maps to mode=as-is and `true`
   * maps to mode=immediate only when mode is absent.
   */
  adapt?: boolean;
  /** Initial staged selection. Values are always resolved by Builder. */
  evidence?: LibraryEvidenceSelection;
  /** Explicit decisions for incompatible variables; required before immediate adaptation can run. */
  variableResolutions?: Record<string, LibraryVariableResolution>;
  /** Required for durable replay protection; clients may generate a UUID. */
  idempotencyKey?: string;
  /**
   * Explicit model for the adaptation task (provider derives from the model
   * id — never a silent provider switch). Debug/e2e runs pin
   * `gemini-3.5-flash-lite`; omitted = server default.
   */
  model?: string;
}

export interface CliBlockFromLibraryVariableDiff {
  added: string[];
  boundExisting: string[];
  preservedExisting: string[];
  dropped: string[];
}

export interface CliBlockFromLibraryData {
  projectId: string;
  strategyId: string;
  blockId: string;
  entityId: string;
  name: string;
  mode: LibraryAdaptationMode;
  screensCreated: number;
  variableDiff: CliBlockFromLibraryVariableDiff;
  adaptationState?: LibraryAdaptationState;
  /** Present when adapt=true: the spawned block-update adaptation task. */
  adaptTaskId?: string;
  /** True when this write auto-reopened a baked/published head as draft. */
  autoReopened?: boolean;
}

export interface CliLibraryAdaptationPreflightRequest {
  evidence?: LibraryEvidenceSelection;
  operatorInstruction?: string;
  variableResolutions?: Record<string, LibraryVariableResolution>;
}

export interface CliLibraryAdaptationPreflightData {
  projectId: string;
  strategyId: string;
  blockId: string;
  contextFingerprint: string;
  source: LibraryAdaptationState['source'];
  evidence: LibraryAdaptationResolvedEvidenceSummary;
  evidenceItems: Array<{ kind: string; id: string; label: string }>;
  /** Safe identities/labels the operator may choose for selected-only mode. */
  availableEvidenceItems: Array<{ kind: LibraryEvidenceRef['kind']; id: string; label: string }>;
  topology: {
    neighbours: 'both' | 'predecessor-only' | 'successor-only' | 'none';
    predecessor?: { entityId?: string; name: string; blockType: string; screensCount?: number };
    successor?: { entityId?: string; name: string; blockType: string; screensCount?: number };
  };
  variableMappingCount: number;
  hasVariableConflicts: boolean;
  variableConflicts: LibraryVariableConflictSummary[];
  hasAssetIntents: boolean;
  hasTargetTheme: boolean;
  /** D-LIVE2-6: can the strategy's own task router still be completed? */
  taskRouter?: LibraryAdaptationTaskRouterPreflight;
  state: LibraryAdaptationState;
}

export interface CliAdaptBlockFromLibraryRequest extends CliLibraryAdaptationPreflightRequest {
  model: string;
  idempotencyKey: string;
  /** Optional optimistic pin from a just-reviewed preflight. */
  expectedContextFingerprint?: string;
}

export interface CliAdaptBlockFromLibraryData extends CliLibraryAdaptationPreflightData {
  taskId: string;
  state: LibraryAdaptationState;
}

export interface CliStrategyScreenSummary {
  id: string;
  projectId: string;
  strategyId: string;
  blockId: string;
  name: string;
  screenType?: string | null;
  order?: number | null;
  position?: Record<string, unknown> | null;
  tags?: string[];
  aiContent?: Record<string, unknown> | null;
  simplified?: Record<string, unknown>;
  simplifiedError?: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  raw?: Record<string, unknown>;
}

export interface CliStrategyScreenListData {
  projectId: string;
  strategyId: string;
  blockId: string;
  screens: CliStrategyScreenSummary[];
}

export interface CliStrategyScreenData {
  projectId: string;
  strategyId: string;
  blockId: string;
  screen: CliStrategyScreenSummary;
  /** R7/P2 (no-dead-end): true when this write auto-reopened a baked/published head as draft. */
  autoReopened?: boolean;
}

export interface CliStrategyScreenPatchRequest {
  patch?: Record<string, unknown>;
  simplified?: Record<string, unknown>;
  simplifiedContent?: Record<string, unknown>;
  lang?: string;
  returnSimplified?: boolean;
  /**
   * C9-S2 anti-wipe escape hatch: raw `patch.content` REPLACES the whole content
   * subtree, so a patch that would drop existing top-level content keys is
   * rejected with 400 unless this is explicitly true.
   */
  replaceContent?: boolean;
}

export interface CliStrategyScreenCloneRequest {
  screenId?: string;
  name?: string;
  positionOffset?: { x?: number; y?: number };
  lang?: string;
  returnSimplified?: boolean;
}

export interface CliStrategyScreenCloneData {
  projectId: string;
  strategyId: string;
  blockId: string;
  sourceScreenId: string;
  screen: CliStrategyScreenSummary;
  /** R7/P2 (no-dead-end): true when this write auto-reopened a baked/published head as draft. */
  autoReopened?: boolean;
}

export interface CliStrategyBlockAssetExperimentSummary {
  experimentId: string;
  status?: string | null;
  promptTemplate?: string | null;
  negativePrompt?: string | null;
  rationale?: string | null;
  sourceExperimentId?: string | null;
  aspectRatio?: string | null;
  conceptIndex?: number | null;
  conceptLabel?: string | null;
  generatedUrl?: string | null;
  generationModel?: string | null;
  generatedAt?: string | null;
  error?: string | null;
}

export interface CliStrategyBlockAssetSlotSummary {
  screenId: string;
  screenType?: string | null;
  slotKey: string;
  role?: string | null;
  promptTemplate?: string | null;
  selectedExperimentId?: string | null;
  selectedGeneratedUrl?: string | null;
  status?: string | null;
  experimentCount: number;
  experiments: CliStrategyBlockAssetExperimentSummary[];
}

export interface CliStrategyBlockAssetSlotListData {
  projectId: string;
  strategyId: string;
  blockId: string;
  slots: CliStrategyBlockAssetSlotSummary[];
  slotCount: number;
}

export interface CliStrategyBlockAssetSlotData {
  projectId: string;
  strategyId: string;
  blockId: string;
  slot: CliStrategyBlockAssetSlotSummary & {
    raw?: Record<string, unknown>;
  };
}

export interface CliStrategyBlockAssetSlotMutationRequest {
  screenId?: string | null;
  dryRun?: boolean;
  patch?: Record<string, unknown>;
  selectedExperimentId?: string | null;
}

export interface CliStrategyBlockAssetSlotMutationData {
  appliedAt: string;
  projectId: string;
  strategyId: string;
  blockId: string;
  screenId: string;
  slotKey: string;
  dryRun: boolean;
  changedFields: string[];
  slot: CliStrategyBlockAssetSlotSummary & {
    raw?: Record<string, unknown>;
  };
}

export interface CliStrategyBlockAssetSlotPromptRegenerateRequest {
  screenId?: string | null;
  experimentId?: string | null;
  sourceExperimentId?: string | null;
  sourcePackageId?: string | null;
  issueReport?: Record<string, unknown> | null;
  userInstruction?: string | null;
  model?: string | null;
  conceptCount?: number | null;
  promptAuthoringMode?: 'initial' | 'revision' | 'repair' | null;
  designerFreedomMode?: 'guided' | 'balanced' | 'open' | 'exploratory' | 'minimal' | 'plain';
  designerDiagnosticProfile?: string | null;
  renderContext?: Record<string, unknown> | null;
  selectRegenerated?: boolean;
  dryRun?: boolean;
}

export interface CliStrategyBlockAssetSlotPromptRegenerateData {
  success: boolean;
  taskId: string;
  projectId: string;
  strategyId: string;
  blockId: string;
  screenId: string;
  slotKey: string;
  experimentId: string;
  experimentIds?: string[];
  selectedConceptIndex?: number;
  conceptCount?: number;
  promptAuthoringMode?: 'initial' | 'revision' | 'repair';
  sourceExperimentId?: string | null;
  model: string;
  provider: string;
  dryRun: boolean;
}

export interface CliStrategyBlockAssetSlotGenerateRequest extends CliContentPlanPostAssetSlotGenerateRequest {
  screenId?: string | null;
  allowFailedPackage?: boolean | null;
}

export interface CliStrategyBlockAssetSlotGenerateData {
  projectId: string;
  strategyId: string;
  blockId: string;
  screenId: string;
  slotKey: string;
  experimentId: string;
  generationPackage: CliContentPlanPostAssetSlotGenerationPackage;
}

export interface CliGenerateStrategyRequest {
  strategyId: string;
  userId?: string;
  name: string;
  description?: string;
  strategyType: string;
  toneOfVoice?: string;
  designDescription?: string;
  sourceData: Record<string, unknown>;
  strategyVariables?: Record<string, unknown>;
  insightVariableGenerationId?: string;
  variableIds?: string[];
  useProjectVariables?: boolean;
  provider?: string;
  model?: string;
  maxTokens?: number;
  evalMaxIterations?: number;
  evalEnabled?: boolean;
  generateScreens?: boolean;
  /** Post-generation health pipeline policy. Defaults to fix for new screen-generating runs (D62). */
  autoRepair?: import('./strategyAutoRepair.types').StrategyAutoRepairMode;
  /** B7 health severity profile. Defaults to blocking for new generation runs (D62). */
  healthProfile?: import('./strategyAutoRepair.types').StrategyHealthProfile;
  /**
   * SG-A: block dispatch mode for this run. Omit to let the executor resolve the
   * strategy's saved setting (absent there = parallel). An invalid value is a 400.
   */
  blockGenerationMode?: import('./strategyGenerationMode.types').StrategyBlockGenerationMode;
  /** Phase V (D7): pin this InsightsAnalysis snapshot; defaults to the project's latest completed analysis. */
  insightsAnalysisId?: string;
  /**
   * W13 (IS1): `--insight-ids` / `--include-insight-sources`. Absent `insightIds`
   * = today's top-N projection; an EXPLICIT empty list is refused, never widened.
   */
  insightSelection?: import('./api.types').AgentInsightSelection;
  /** Phase V (D28): fork lineage — the strategy this one is forked from. */
  predecessorStrategyId?: string;
  /** Spec A (A7-4): anchor the fork at a specific predecessor strategy version node (version tree). */
  predecessorVersion?: string;
  /** Phase V (D28): free-text hypothesis under test in this fork. */
  forkHypothesis?: string;
  /** Spec C (C5, D52): rendered what-changed summary forwarded into the root prompts (capped server-side). */
  changeDiff?: string;
  /** Spec C (C5, Mode 1): guided-chain in-place revise — a baked head auto-reopens first (autoReopened echoed). */
  reviseInPlace?: boolean;
  /**
   * Block Examples Library participation (Phase 3): selected examples join the
   * generation. Caps are SERVER-enforced (`LIBRARY_SELECTION_CAPS`) — the CLI
   * `--file` body is spread unchecked, so UI-only caps would be bypassable.
   */
  libraryBlockSelections?: BlockLibrarySelection[];
}

/** One selected library example participating in strategy generation. */
export interface BlockLibrarySelection {
  source: 'project' | 'system';
  exampleId: string;
  /**
   * include = verbatim materialization; adapt = strict-template + AI rebind
   * (THE default — «блок = шаблон»); pattern = style/structure reference only.
   */
  mode: 'include' | 'adapt' | 'pattern';
}

/** SERVER-enforced caps: include+adapt (materialized) combined, and pattern. */
export const LIBRARY_SELECTION_CAPS = { materialized: 3, pattern: 5 } as const;

export interface CliGenerateTaskData {
  success?: boolean;
  taskId?: string;
  error?: string;
  [key: string]: unknown;
}

export interface CliTaskDebugDoc {
  id: string;
  path: string;
  exists: boolean;
  data: Record<string, unknown> | null;
}

export interface CliTaskDebugArtifact extends CliTaskDebugDoc {
  artifact?: Record<string, unknown>;
  collection?: string;
  error?: string;
}

export interface CliTaskDebugPhaseSummary {
  key: string;
  status?: string;
  startedAt?: string | null;
  completedAt?: string | null;
  updatedAt?: string | null;
  error?: unknown;
  message?: unknown;
  data?: unknown;
}

export interface CliTaskDebugReferenceSummary {
  source: string;
  url?: string;
  name?: string;
  role?: string;
  instruction?: string;
  mimeType?: string;
  order?: number;
  aiCallId?: string;
  taskId?: string;
  path?: string;
}

export interface CliTaskDebugSummary {
  taskType?: string;
  status?: string;
  phase?: string;
  provider?: string;
  model?: string;
  authorId?: string;
  userId?: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  request?: Record<string, unknown>;
  result?: Record<string, unknown>;
  aiCallCount: number;
  aiCallsWithFullPrompt: string[];
  artifactPaths: string[];
  nearbyImageTaskIds: string[];
  referenceImageCount: number;
  referenceImages: CliTaskDebugReferenceSummary[];
}

export interface CliTaskDebugChildTask {
  task: CliTaskDebugDoc;
  aiCalls: CliTaskDebugDoc[];
  providerUsage?: CliTaskDebugProviderUsage;
}

/** Persisted per-provider-call evidence; never a replacement for billing settlement. */
export interface CliTaskDebugProviderUsage {
  status: 'complete' | 'partial' | 'unavailable' | 'not_instrumented';
  calls: CliTaskDebugDoc[];
  knownUsage: Record<string, number>;
  invocations: Array<{ invocationId: string; calls: number; partial: boolean; knownUsage: Record<string, number> }>;
  historyTruncated: boolean;
  legacyHistoryUnknown: boolean;
  issues: string[];
}

export interface CliTaskDebugExportData {
  _schema: 'segmently-content-plan-task-debug/v1';
  /** Additive discriminator; the _schema string is kept for existing consumers. */
  bundleKind?: 'content-plan' | 'strategy-pipeline' | 'generic';
  exportedAt: string;
  env: {
    projectId: string;
    taskId: string;
    taskPath: string;
  };
  summary: CliTaskDebugSummary;
  task: CliTaskDebugDoc;
  phaseSummary: CliTaskDebugPhaseSummary[];
  aiCalls: CliTaskDebugDoc[];
  providerUsage?: CliTaskDebugProviderUsage;
  /** Read/rehydration/tree integrity only; semantic quality and billing have separate gates. */
  evidence?: { status: 'complete' | 'partial'; issues: string[] };
  /** Pipeline child tasks (e.g. generate-strategy phases), each with rehydrated ai_calls. */
  childTasks?: CliTaskDebugChildTask[];
  /** SG-A: per-block/attempt state docs (`block_generation` subcollection) of a sequential_by_branch run. */
  blockGeneration?: CliTaskDebugDoc[];
  artifacts: CliTaskDebugArtifact[];
  topics: CliTaskDebugDoc[];
  posts: CliTaskDebugArtifact[];
  nearbyImageTasks: CliTaskDebugDoc[];
}

export interface CliTaskDebugExportOptions {
  nearbyWindowMinutes?: number;
  scanPosts?: boolean;
}

/** Result of the admin agent-loop smoke run (Spec E, E1). */
export interface CliAgentLoopSmokeData {
  projectId: string;
  taskId: string;
  model: string;
  provider: string;
  /** Final model text (e.g. "SMOKE OK: ..."). */
  content: string;
  stopReason: 'complete' | 'max_iterations' | 'budget_exceeded';
  iterations: number;
  trajectory: Array<{
    iteration: number;
    toolCalls: string[];
    usage: { inputTokens: number; outputTokens: number };
  }>;
  usage: { inputTokens: number; outputTokens: number };
}

/** Result of an admin analyst review run (Spec E E2a case 1 + E2b cases 2/4). */
export interface CliAnalystReviewData {
  projectId: string;
  taskId: string;
  /** Experiment-registry card id (kind 'analyst') the review was persisted to. */
  experimentId: string;
  /** Review mode: 'release' (case 1) | 'decision-brief' (case 2) | 'anomaly-watch' (case 4) | 'campaign-brief' (D). */
  mode: 'release' | 'decision-brief' | 'anomaly-watch' | 'campaign-brief';
  model: string;
  provider: string;
  headline: string;
  narrative: string;
  findingsCount: number;
  duplicateFindingsCount: number;
  /** E-Q6 honesty: true when a cap tripped and the report is partial. */
  truncated: boolean;
  stopReason: 'complete' | 'max_iterations' | 'budget_exceeded';
  iterations: number;
  /** Canonical V1–V5 vitals block (release/anomaly-watch modes). */
  vitals?: Record<string, unknown>;
  /** Canonical decision grounding (decision-brief mode). */
  decisionContext?: Record<string, unknown>;
  /** D: canonical campaign-economics grounding (campaign-brief mode). */
  campaignContext?: Record<string, unknown>;
  /** E3: W attempts refused by the L3 gate (proactive fixes OFF). */
  wGate?: { blocked: true; attempts: Array<{ tool: string; summary: string }> };
  /** E3: W actions the agent performed (proactive fixes ON). */
  actionsTaken?: Array<{ type: 'candidate_prepared' | 'draft_created'; experimentId: string; dedupKey?: string }>;
  usage: { inputTokens: number; outputTokens: number };
}

/** One advisory finding of the cognitive quiz pretest (J4-I4). */
export interface CliStrategyPretestFinding {
  screenId: string;
  blockId: string;
  stemExcerpt: string;
  /** Doc §-reference, e.g. `survey-methodology-and-measurement.md §3`. */
  lawRef: string;
  severity: 'advisory' | 'info';
  suggestion: string;
}

/**
 * Result of an admin cognitive quiz pretest run (J4-I4) — the stored advisory
 * report envelope for one baked strategy version. ADVISORY only: consumed by
 * the `strategy.pretest` preflight row and the `strategy.pretestReview`
 * human_decision readiness milestone; never a publish gate.
 */
export interface CliStrategyPretestData {
  projectId: string;
  taskId: string;
  strategyId: string;
  strategyVersionId: string;
  status: 'complete' | 'failed';
  verdict?: 'clean' | 'advisories';
  counts: { advisory: number; info: number };
  screensAudited: number;
  findingsCount: number;
  model: string;
  provider: string;
  /** Firestore path of the persisted report doc (re-runs overwrite it). */
  reportPath: string;
  findings?: CliStrategyPretestFinding[];
  /** Present when status is 'failed' — re-run the pretest. */
  error?: string;
  usage: { inputTokens: number; outputTokens: number };
}

/** One advisory finding of the AI block review (strategy-blocks B3-I1). */
export interface CliBlockReviewFinding {
  criterion:
    | 'navigationCompleteness'
    | 'insightGroundingConsistency'
    | 'copyQuality'
    | 'briefCompliance'
    | 'instructionCompliance';
  severity: 'advisory' | 'warning' | 'critical';
  screenId?: string;
  /** Verbatim payload quote proving the finding. */
  evidence: string;
  /** Actionable directive for a regenerating agent — never replacement copy. */
  repairInstruction: string;
}

/**
 * Result of an admin AI block review run (strategy-blocks B3-I1) — the stored
 * advisory report envelope for one strategy block. ADVISORY only: feeds the
 * B3 auto-repair retry rail and human review; never a generation/publish gate.
 */
export interface CliBlockReviewData {
  projectId: string;
  taskId: string;
  strategyId: string;
  blockId: string;
  /** null = the strategy head was reviewed. */
  strategyVersionId: string | null;
  status: 'complete' | 'failed';
  verdict?: 'pass' | 'repair' | 'surface';
  scores?: Partial<Record<CliBlockReviewFinding['criterion'], number>>;
  summary?: string;
  counts: { advisory: number; warning: number; critical: number };
  screensReviewed: number;
  findingsCount: number;
  model: string;
  provider: string;
  /** Firestore path of the persisted report doc (re-runs overwrite it). */
  reportPath: string;
  findings?: CliBlockReviewFinding[];
  /** D54 payload for the retry rail — present when the verdict is 'repair'. */
  repairInstructions?: string;
  /** Present when status is 'failed' — re-run the review. */
  error?: string;
  usage: { inputTokens: number; outputTokens: number };
}

/** Contract version of the strategy repair run report (strategy-blocks B3-I2). */
export const CLI_REPAIR_REPORT_VERSION = 'repair-report/v1';
/** Manual dry-run/apply binding contract (strategy-blocks B3-I3 hardening). */
export const CLI_REPAIR_PLAN_RECEIPT_VERSION = 'repair-plan-receipt/v1';

export type CliRepairActionKind = 'autofix' | 'retry-screen' | 'retry-block' | 'surface';
export type CliRepairExecutedOutcome = 'applied' | 'skipped' | 'retried' | 'surfaced';

export interface CliRepairActionTarget {
  blockKey?: string;
  screenId?: string;
  actionKey?: string;
  variableKey?: string;
}

/** One planned repair action (dry-run projection of `repair-plan/v1`). */
export interface CliRepairPlannedAction {
  kind: CliRepairActionKind;
  /** Present when kind === 'autofix'. */
  fix?: 'recomputePositions' | 'addDefaultEdge' | 'declareRouterVariable' | 'wireConvergenceEdge' | 'restorePrimaryCtaLabel';
  target: CliRepairActionTarget;
  /** Stable finding keys this action addresses (planner `findingKey` convention). */
  findings: string[];
  /** First ≤600 chars of the D54 retry instructions (full text on the child task doc). */
  instructionsExcerpt?: string;
  /** Present when kind === 'surface'. */
  reason?: string;
  /** PC80-07: finding keys this rail may actually resolve (retry actions only). */
  dispatchedFindings?: string[];
}

/** One executed repair action with its per-action outcome (apply mode). */
export interface CliRepairExecutedAction {
  kind: CliRepairActionKind;
  target: CliRepairActionTarget;
  findings: string[];
  outcome: CliRepairExecutedOutcome;
  reason?: string;
  /** Child task id of the dispatched retry (block-update / screen-repair). */
  taskId?: string;
  /** Child task id of the mandatory reviewer re-run after a successful retry. */
  reviewTaskId?: string;
  before?: { healthFindings: number; validationErrors?: number };
  after?: { healthFindings: number; validationErrors?: number };
  /** Finding keys still present after the scoped re-verify (≤40, surfaced — never re-retried). */
  survivedFindings?: string[];
  /**
   * Live-2 fix wave 8 (W5.2, F-LIVE-13): this action CHANGED the strategy.
   *
   * A retry whose child task ended non-error wrote its block or screen through
   * `executeBlockScreenGeneration` — that is a mutation of the saved draft even
   * when the finding it was dispatched for survived the re-verify and the action
   * is therefore reported `surfaced`. Set only by `dispatchRetry`, and only when
   * the child completed and no owner-edit refusal fired; deterministic autofixes
   * are already visible as `outcome: 'applied'`.
   *
   * A surviving retry is NEVER reclassified as `applied` — that would turn
   * `repairComplete` green on an unresolved finding. This flag answers a
   * different question: «did this run touch the strategy at all?»
   */
  mutated?: boolean;
  /** PC80-07: findings this action never dispatched, because the rail cannot resolve them. */
  unresolvedFindings?: CliRepairUnresolvedFinding[];
}

/**
 * PC80-07 repair scope of ONE finding, decided BEFORE dispatch:
 * - `copy_only`       — the target screen's own copy resolves it,
 * - `block_structure` — the block's screen set or a screen's interaction changes,
 * - `source_schema`   — the authoritative value lives in the block contract,
 *   the variable declaration or the source data,
 * - `cross_block`     — the resolution spans more than the reviewed block.
 */
export type CliRepairScopeClassification = 'copy_only' | 'block_structure' | 'source_schema' | 'cross_block';

/**
 * One finding the run did NOT resolve, with the reason it could not. Produced
 * by plan-time scope routing (`scope-routing`), a post-parse repair gate
 * (`repair-gate`), or the scoped re-verify after a retry (`survived-retry`).
 * A run that emits any of these is never `repairComplete`.
 */
export interface CliRepairUnresolvedFinding {
  findingId: string;
  classification: CliRepairScopeClassification;
  reason: string;
  origin: 'scope-routing' | 'repair-gate' | 'survived-retry';
  /** Rule id of the deterministic classification table (C1…C10). */
  rule?: string;
  blockKey?: string;
  screenId?: string;
}

/**
 * Closure verdict of a repair run (PC80-07). `passed` requires zero
 * deterministic findings AND zero unresolved/surviving findings AND zero
 * surfaced actions — a completed run with surviving findings reads `warning`
 * with `repairComplete: false`, never clean.
 */
export interface CliRepairFinalHealth {
  status: CliFunnelAuditStatus;
  repairComplete: boolean;
  healthFindings: number;
  unresolvedFindings: number;
  survivedFindings: number;
  surfacedActions: number;
}

/** Reviewer report consumed by the run (staleness rule: createdAt < block.updatedAt → excluded). */
export interface CliRepairReviewerInput {
  blockId: string;
  verdict: 'pass' | 'repair' | 'surface';
  findingsUsed: number;
  stale?: boolean;
  createdAt?: string;
}

export interface CliRepairBudgetUsage {
  perBlockRetries: number;
  /** Retries dispatched per blockKey during this run. */
  dispatched: Record<string, number>;
  /**
   * Finding keys that already rode a retry; seeds a resumed run's
   * anti-oscillation ledger. New values use
   * `scope:<encodedBlockKey>::<findingKey>`; bare v1 values remain valid as
   * conservative cross-block seeds.
   */
  attemptedFindings?: string[];
  /** Actions the ledger refused (budget/oscillation guards). */
  blocked?: Array<{ blockKey: string; reason: 'budget-exhausted' | 'findings-already-attempted'; findings: string[] }>;
}

/**
 * Immutable receipt over the exact manual repair scope, content snapshot,
 * plan, reviewer inputs, ledger state, and execution policy. Manual apply
 * must echo `checksum`; pipeline-owned autoRepair does not use this operator
 * approval gate.
 */
export interface CliRepairPlanReceipt {
  receiptVersion: typeof CLI_REPAIR_PLAN_RECEIPT_VERSION;
  checksum: `sha256:${string}`;
  scope: {
    strategyId: string;
    blockId?: string;
    screenId?: string;
  };
}

/** Manual apply proof persisted with the resulting repair report. */
export interface CliRepairPlanApproval {
  approvedPlanChecksum: `sha256:${string}`;
  matched: true;
}

/** Public/manual repair request. Dry-run is default; apply requires the fresh receipt. */
export interface CliStrategyRepairRequest {
  blockId?: string;
  screenId?: string;
  apply?: boolean;
  approvedPlanChecksum?: `sha256:${string}`;
  resetAttemptLedger?: boolean;
  perBlockRetries?: number;
  provider?: string;
  model?: string;
}

/**
 * Strategy repair run report (strategy-blocks B3-I2, `repair-report/v1`) —
 * persisted on the run's own task doc. Dry-run carries plannedActions only;
 * apply adds executedActions + verdict deltas. A baked/published strategy
 * REFUSES in BOTH modes (D51/B3-AC-012): status 'refused' + refusal payload,
 * zero content reads beyond the strategy head, zero writes.
 */
export interface CliRepairReport {
  reportVersion: typeof CLI_REPAIR_REPORT_VERSION;
  projectId: string;
  strategyId: string;
  /** Present when the run was scoped to one block. */
  blockId?: string;
  /** Present when the run was scoped to one screen within blockId. */
  screenId?: string;
  mode: 'dry-run' | 'apply';
  status: 'completed' | 'refused' | 'failed';
  refusal?: { code: 'STRATEGY_BAKED'; strategyStatus: string };
  /** Present when status is 'failed'. */
  error?: string;
  planSummary?: { findingsTotal: number; autofixes: number; retries: number; surfaced: number; unrouted: number };
  plannedActions?: CliRepairPlannedAction[];
  executedActions?: CliRepairExecutedAction[];
  reviewerInputs?: CliRepairReviewerInput[];
  /** Present after a non-refused plan was assembled. */
  planReceipt?: CliRepairPlanReceipt;
  /** Present only on a manual apply whose fresh receipt matched. */
  planApproval?: CliRepairPlanApproval;
  /** Whole-strategy health finding counts around the run (apply mode). */
  verdictDeltas?: { healthFindingsBefore: number; healthFindingsAfter?: number };
  /** PC80-07: every finding this run did not resolve, with its scope reason. */
  unresolvedFindings?: CliRepairUnresolvedFinding[];
  /** PC80-07 closure verdict — never `passed` while an unresolved finding survives. */
  finalHealth?: CliRepairFinalHealth;
  budgetUsage: CliRepairBudgetUsage;
  /** Counts of entries dropped by the report caps — truncation is never silent. */
  truncated?: { plannedActions?: number; executedActions?: number; survivedFindings?: number; findingKeys?: number; attemptedFindings?: number; unresolvedFindings?: number };
  startedAt: string;
  finishedAt: string;
  model: string;
  provider: string;
}

/** CLI envelope of a strategy repair run (`internal strategy repair`). */
export interface CliStrategyRepairData {
  projectId: string;
  taskId: string;
  strategyId: string;
  report: CliRepairReport;
  usage?: { inputTokens: number; outputTokens: number };
}

/** Result of an operator verdict on one analyst finding (Spec E E2b). */
export interface CliAnalystFeedbackData {
  projectId: string;
  experimentId: string;
  dedupKey: string;
  verdict: 'accepted' | 'dismissed';
  feedbackBy: string;
  feedbackAt: string;
  note?: string;
}

/**
 * Per-project agent settings (Spec E E3 — the Spec D L3 "proactive fixes" grant).
 * `proactiveFixes` gates the analyst agent's W-tier tools; X is never grantable.
 */
export interface CliAgentSettingsData {
  projectId: string;
  proactiveFixes: boolean;
  updatedAt?: string;
  updatedBy?: string;
}

/** One scanned axis of the segment scan with its coverage-gate verdict. */
export interface CliSegmentScanAxis {
  axis: string;
  /** Share of scope journeys carrying a non-null value (0..1). */
  coverage: number;
  cardinality: number;
  status: 'scanned' | 'unavailable' | 'excluded_cardinality';
  reason?: string;
}

/** One segment cell with its deterministic statistics (Spec E E2c Phase A). */
export interface CliSegmentScanFinding {
  axis: string;
  value: string;
  valueB: string | null;
  label: string | null;
  grain: 'journey' | 'user';
  n: number;
  coverage: number;
  conversions: number;
  rawRate: number;
  shrunkRate: number;
  interval: [number, number];
  complementRate: number | null;
  rawP: number | null;
  /** Benjamini-Hochberg adjusted p across all tested cells of the scan. */
  adjustedP: number | null;
  revenue: number | null;
  flag: 'discovery' | 'not_significant' | 'insufficient_data';
}

/** Result of the deterministic admin segment scan (Spec E E2c Phase A — no AI spend). */
export interface CliSegmentScanData {
  projectId: string;
  outcome: 'offer_converted' | 'purchase_recorded' | 'trial_started';
  grain: 'journey' | 'user';
  scope: {
    onboardingId?: string;
    versionId?: string;
    from?: string;
    to?: string;
  };
  scopeTotals: {
    n: number;
    conversions: number;
    rate: number | null;
    journeys: number;
    users: number;
  };
  axes: CliSegmentScanAxis[];
  findings: CliSegmentScanFinding[];
  totalCells: number;
  discoveries: number;
  /** E-Q6 honesty: true when findings were cut to the top-K. */
  truncated: boolean;
  capped?: { droppedCrosses: string[]; cellBudget: number };
  honesty: {
    coverageThreshold: number;
    minCellSize: number;
    fdrQ: number;
    outcomeEvents: number;
    insufficientOutcome: boolean;
    minOutcomeEvents: number;
  };
}

/**
 * Result of the deterministic admin research-report generation (Spec E E2c
 * Phase B — no AI spend). Summary envelope only; the full ResearchReport JSON
 * lives gzipped in the builds bucket at `gcsPath` and is fetched via
 * `internal research get` / the insights research-reports route.
 */
export interface CliResearchReportData {
  projectId: string;
  reportId: string;
  seriesId: string;
  previousReportId: string | null;
  /** backend_tasks doc id recording the generation run. */
  taskId: string;
  /** Experiment-registry card id (type 'research') — equals reportId. */
  cardId: string;
  window: { from: string; to: string };
  preset?: string;
  sectionIds: string[];
  dataHealth: {
    status: 'pass' | 'warn' | 'fail';
    checks: Array<{ checkId: string; status: 'pass' | 'warn' | 'fail'; value: number | null }>;
  };
  scopeTotals: { journeys: number; users: number; conversions: number };
  sectionsIndex: Array<{
    id: string;
    status: 'ok' | 'degraded' | 'unavailable';
    rowCount: number;
    truncated: boolean;
  }>;
  gcsPath: string;
  /** Raw (pre-gzip) report JSON size. */
  bytes: number;
  /** Screens flagged anomalous_loss by the peer benchmarks — CLI quick view. */
  anomalies: Array<{
    screenId: string;
    exitRate: number | null;
    peerMeanExitRate: number | null;
    pValue: number | null;
  }>;
  queryJobs: string[];
}

/**
 * Result of the admin research-panel run (Spec E E2c Phase C — aiMetered).
 * Summary envelope only; full persona/scientist outputs are GCS sidecars
 * under `sidecarBasePath` and the card carries the embedded PanelSummary.
 */
export interface CliResearchPanelData {
  projectId: string;
  reportId: string;
  seriesId: string;
  /** backend_tasks doc id — also the panelRunId in sidecar paths. */
  taskId: string;
  /** Experiment-registry card id (type 'research') — equals reportId. */
  cardId: string;
  status: 'ok' | 'degraded';
  model: string;
  personaSummaries: Array<{
    personaId: string;
    status: 'ok' | 'failed';
    promptSource: 'registry' | 'override' | null;
    promptVersion: string | null;
    headline: string | null;
    hypothesisCount: number;
    proposedExperimentCount: number;
  }>;
  scientist: {
    status: 'ok' | 'failed';
    verdictCounts: Record<string, number>;
  };
  draftExperimentIds: string[];
  rejectedProposals: Array<{
    personaId: string;
    proposalRef: string;
    reason: 'underpowered' | 'dedup' | 'dismissed';
    detail?: string;
  }>;
  deepDiveScreenIds: string[];
  sidecarBasePath: string;
}

/**
 * Result of the deterministic admin audience-evidence pack run (Phase AC v1 —
 * no AI spend). Summary envelope only; the full pack JSON lives gzipped in
 * the builds bucket at `gcsPath` (traits project-wide, outcomes scope-bound).
 */
export interface CliAudienceEvidenceData {
  projectId: string;
  packId: string;
  /** backend_tasks doc id recording the run. */
  taskId: string;
  gcsPath: string;
  /** Raw (pre-gzip) pack JSON size. */
  bytes: number;
  outcome: 'offer_converted' | 'purchase_recorded' | 'trial_started';
  window: { from: string; to: string };
  scope: { onboardingId?: string; versionId?: string };
  scopeTotals: {
    journeys: number;
    pastFirstScreen: number;
    paywallReached: number;
    conversions: number;
    conversionRate: number | null;
    revenue: number | null;
  };
  /** Section row counts — the CLI quick view of pack density. */
  counts: {
    traitKeys: number;
    traitOutcomeRows: number;
    acquisitionRows: number;
    priceRows: number;
    geoRows: number;
    vocabularyEntries: number;
    segmentDiscoveries: number;
  };
  topTraitKeys: Array<{ traitKey: string; usersWithKey: number; keyCoverage: number }>;
  topBuyerCohorts: Array<{
    traitKey: string;
    value: string;
    label: string | null;
    users: number;
    buyers: number;
    buyerRate: number | null;
    shrunkBuyerRate: number | null;
  }>;
  dataHealth: {
    journeys: number;
    geoFill: number | null;
    labelFill: number | null;
    untaggedShare: number | null;
    priceFill: number | null;
    maxStartedAt: string | null;
    maxMaterializedAt: string | null;
  };
  notes: string[];
}

/**
 * Result of an admin-run audience-chain generation task (Phase AC v1 —
 * aiMetered). The run executes inline; `result` is the handler payload
 * persisted on the backend_tasks doc.
 */
export interface CliAudienceGenerationRunData {
  projectId: string;
  taskId: string;
  taskType: 'generate-audiences' | 'generate-actors' | 'conduct-interview';
  status: 'completed' | 'failed';
  provider: string;
  model: string;
  /** Present when the run was grounded in a warehouse evidence pack. */
  evidencePackId?: string;
  grounded: boolean;
  error?: string;
  result?: unknown;
}

export interface CliThemeSummary {
  id: string;
  name: string;
  sourceThemeId?: string | null;
  isActive?: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
  order?: number | null;
  raw?: Record<string, unknown>;
}

export interface CliThemeListData {
  themes: CliThemeSummary[];
  activeThemeId?: string | null;
}

export interface CliProjectThemeImportRequest {
  sourceThemeIds: string[];
  activeSourceThemeId?: string | null;
}

export interface CliProjectThemeImportData {
  themes: CliThemeSummary[];
  activeThemeId?: string | null;
  importedCount: number;
}

export interface CliProjectThemeSetActiveData {
  theme: CliThemeSummary;
  activeThemeId: string;
}

// ── Figma → Theme V2 import artifact (client-side projection, Figma-blind server) ──
//
// The `/figma-theme` skill extracts Figma variables + geometry, normalizes them to a
// provider-agnostic VisualDesignAnalysis, runs the SAME projection the screenshot
// pipeline uses, and POSTs ONLY the finished projected theme. The server never parses
// Figma URLs, calls the Figma REST API, or runs MCP — it validates and persists.
//
// This DTO is intentionally decoupled from the canonical `ThemeV2` import in
// `@modules/themes` so `shared-types` stays a leaf. The rich artifact (carrying a real
// `ThemeV2`) lives in `@modules/themes` and is used only by the skill's repo script.

export type CliThemeImportTarget = 'project' | 'onboarding' | 'global';

export type CliThemeImportSourceKind = 'figma-frame';

/** High-level color roles — the only strongly-typed part of the projected theme. */
export interface CliThemeV2ProjectedColors {
  buttonsBackground: string;
  titlesSubtitles: string;
  buttonsTitle: string;
}

/**
 * JSON-safe structural mirror of a runtime ThemeV2 (without identity/lifecycle
 * fields). The server validates these against the live ThemeV2 contract before
 * persisting; complex V2 subtrees stay `Record<string, unknown>` so shared-types
 * does not couple to `@segmently/schema-models/v2` or `@modules/themes`.
 */
export interface CliThemeV2ProjectedThemeInput {
  name?: string;
  colors: CliThemeV2ProjectedColors;
  fonts: Record<string, unknown>;
  optionsListSettings: Record<string, unknown>;
  layoutSettings?: Record<string, unknown>;
  background: Record<string, unknown>;
  headerBar: Record<string, unknown>;
  actionBar: Record<string, unknown>;
  flowDocument: Record<string, unknown>;
  generatedMetadata?: Record<string, unknown>;
}

/** Compact, audit-only provenance. The server rejects oversized/raw-MCP payloads. */
export interface CliThemeImportArtifactProvenance {
  fileKey?: string;
  fileName?: string;
  pageName?: string;
  frameName?: string;
  nodeIds?: string[];
  /** Audit-only Figma URL; never fetched by the server. */
  sourceUrl?: string;
  variableRefs?: string[];
  styleRefs?: string[];
  /** Average confidence of the normalized tokens (1.0 for exact Figma variables). */
  confidenceSummary?: number;
}

export interface CliThemeImportArtifactRequest {
  /** Artifact schema version the skill wrote (server enforces compatibility). */
  schemaVersion: string;
  /** Generator (skill) version stamped into provenance. */
  generatorVersion: string;
  /** Minimum CLI version required to submit this artifact, if any. */
  minCliVersion?: string;
  /** Stable hash of (normalized source + projectedTheme), excluding timestamps. */
  artifactHash: string;
  sourceKind: CliThemeImportSourceKind;
  target: CliThemeImportTarget;
  /** Optional display name override. */
  name?: string;
  /** Project target only: set the imported theme active (deactivates others). */
  setActive?: boolean;
  /** Required when target === 'onboarding'. */
  onboardingId?: string;
  /** The fully projected, validate-and-persist Theme V2. No server projection. */
  projectedTheme: CliThemeV2ProjectedThemeInput;
  provenance?: CliThemeImportArtifactProvenance;
  warnings?: string[];
}

export interface CliThemeImportArtifactData {
  theme: { id: string; name: string };
  target: CliThemeImportTarget;
  isActive: boolean;
  sourceKind: CliThemeImportSourceKind;
  artifactHash: string;
  /** True when an identical artifactHash was already imported to this target. */
  alreadyImported?: boolean;
  /** Echoed for the onboarding target. */
  onboardingId?: string;
  warnings: string[];
}

/** Where an onboarding takes its theme from: the project's own themes or the system list (`themesV2`). */
export type CliOnboardingThemeSourceKind = 'project' | 'system';

/**
 * `POST /cli/v1/admin/projects/:projectId/funnels/:funnelId/theme` (strict admin; `themes set-onboarding`).
 * Replaces the onboarding's theme settings (the theme and its screen templates) with a full copy of the
 * picked theme. Existing screens are never restyled.
 */
export interface CliOnboardingThemeSwitchRequest {
  source: CliOnboardingThemeSourceKind;
  themeId: string;
  dryRun?: boolean;
}

export interface CliOnboardingThemeSwitchData {
  onboardingId: string;
  source: CliOnboardingThemeSourceKind;
  themeId: string;
  themeName: string;
  /** Screen templates the new theme carries (its `flowDocument.screens`). */
  templateCount: number;
  dryRun: boolean;
  /** Top-level theme-settings fields whose values differ from the onboarding's current ones. */
  changedFields: string[];
}

/**
 * `POST /cli/v1/admin/themes/system` (strict admin; `themes add-system`). Copies a project theme or an
 * onboarding's theme into the system list (`themesV2`); the source is never changed.
 */
export interface CliSystemThemeAddRequest {
  projectId: string;
  projectThemeId?: string;
  onboardingId?: string;
  name: string;
  order?: number;
  dryRun?: boolean;
}

export interface CliSystemThemeProvenance {
  from: 'project-theme' | 'onboarding';
  projectId: string;
  projectThemeId?: string;
  onboardingId?: string;
  sourceThemeId: string | null;
  byUid: string;
}

export interface CliSystemThemeAddData {
  /** Null on a dry run: nothing was created. */
  themeId: string | null;
  name: string;
  order: number | null;
  /** A system theme with the same name (trimmed, case-insensitive) already exists; the add is not blocked. */
  sameNameExists: boolean;
  dryRun: boolean;
  provenance: CliSystemThemeProvenance;
}

export interface CliFunnelGraphPosition {
  x: number;
  y: number;
}

export interface CliFunnelGraphVariableInput {
  id: string;
  name?: string;
  type: CliScreenTypeVariableType;
}

export interface CliFunnelGraphContainerSizeInput {
  width: number;
  height: number;
  /** Catalog preset traceability ref: "platformId/formatId[/slotKey]" (schema 2.20.0) */
  presetRef?: string;
}

export interface CliFunnelGraphScreenInput {
  key: string;
  type: string;
  name?: string;
  position: CliFunnelGraphPosition;
  containerSize?: CliFunnelGraphContainerSizeInput;
  content?: Record<string, unknown>;
  variable?: CliFunnelGraphVariableInput;
}

export interface CliFunnelGraphConditionInput {
  key: string;
  operator: CliScreenTypeConditionOperator;
  value?: string | number | boolean | Array<string | number | boolean> | null;
  /** Connector to the next condition; any case — stored as the schema's lowercase. */
  logic?: 'AND' | 'OR' | 'and' | 'or';
}

export interface CliFunnelGraphEdgeInput {
  from: string;
  to: string;
  action: string;
  label?: string;
  conditions?: CliFunnelGraphConditionInput[];
}

export interface CliFunnelGraphDryRunRequest {
  launchScreenKey?: string;
  screens?: CliFunnelGraphScreenInput[];
  variables?: CliFunnelGraphVariableInput[];
  edges?: CliFunnelGraphEdgeInput[];
}

export type CliFunnelGraphApplyRequest = CliFunnelGraphDryRunRequest;

export interface CliFunnelGraphValidationIssue {
  severity: 'error' | 'warning';
  code: string;
  message: string;
  path?: string;
}

export interface CliFunnelGraphDryRunData {
  valid: boolean;
  projectId: string;
  funnelId: string;
  versionId: string;
  summary: {
    screenCount: number;
    variableCount: number;
    edgeCount: number;
    conditionCount: number;
  };
  errors: CliFunnelGraphValidationIssue[];
  warnings: CliFunnelGraphValidationIssue[];
}

export interface CliFunnelGraphAppliedScreen {
  key: string;
  id: string;
  type: string;
}

export interface CliFunnelGraphApplyData {
  appliedAt: string;
  projectId: string;
  funnelId: string;
  versionId: string;
  launchScreenId: string;
  screens: CliFunnelGraphAppliedScreen[];
  summary: CliFunnelGraphDryRunData['summary'];
}

export interface CliFunnelScreenSummary {
  id: string;
  name?: string;
  screenType?: string;
  position?: CliFunnelGraphPosition;
  isLaunchScreen: boolean;
  boundVariableIds: string[];
}

export interface CliFunnelScreenListData {
  projectId: string;
  funnelId: string;
  versionId: string;
  screens: CliFunnelScreenSummary[];
  total: number;
}

export interface CliFunnelScreenGetData {
  projectId: string;
  funnelId: string;
  versionId: string;
  screen: Record<string, unknown>;
}

export interface CliFunnelScreenEdgeReference {
  screenId: string;
  actionPath: string;
  edgeIndex: number;
  nextScreenId?: string;
  transitionKind?: string;
  conditionCount: number;
}

export interface CliFunnelScreenConditionReference {
  screenId: string;
  actionPath: string;
  edgeIndex: number;
  conditionIndex: number;
  key?: string;
  operator?: string;
  value?: unknown;
}

export interface CliFunnelScreenVariableReference {
  id: string;
  name?: string;
  type?: CliScreenTypeVariableType | string;
  boundScreenIds: string[];
  screenBindings?: Record<string, string>;
}

export interface CliFunnelScreenInspectData {
  projectId: string;
  funnelId: string;
  versionId: string;
  screen: CliFunnelScreenSummary;
  incomingEdges: CliFunnelScreenEdgeReference[];
  outgoingEdges: CliFunnelScreenEdgeReference[];
  boundVariables: CliFunnelScreenVariableReference[];
  conditionReferences: CliFunnelScreenConditionReference[];
  warnings: CliFunnelGraphValidationIssue[];
}

export type CliFunnelScreenCloneEdgeMode = 'clear' | 'copy-outgoing';

export interface CliFunnelScreenCloneRequest {
  newScreenId?: string;
  name?: string;
  position?: CliFunnelGraphPosition;
  offset?: CliFunnelGraphPosition;
  edgeMode?: CliFunnelScreenCloneEdgeMode;
  dryRun?: boolean;
}

export interface CliFunnelScreenCloneData {
  projectId: string;
  funnelId: string;
  versionId: string;
  sourceScreenId: string;
  screenId: string;
  dryRun: boolean;
  edgeMode: CliFunnelScreenCloneEdgeMode;
  screen: Record<string, unknown>;
}

export type CliFunnelScreenPatchOperation =
  | {
      op: 'setBackgroundImageUrl';
      url: string;
      locales?: string[];
    }
  | {
      op: 'convertSelection';
      to: 'ListSinglePick';
      variableMode?: 'new-enum';
      variableId?: string;
      variableName?: string;
    }
  | {
      /**
       * Safely update one allowlisted screen content leaf setting by dotted path.
       *
       * This is intentionally narrower than a generic document patch: the
       * builder-service implementation only accepts `content.*` paths, blocks
       * navigation/action/identity/html fields, rejects parent object/array writes,
       * validates scalar values, and never writes outside the requested screen
       * document.
       */
      op: 'setFieldValue';
      path: string;
      value: unknown;
    }
  | {
      /**
       * Re-point a FlexibleLayout section's `parentSectionId` to a ProductCatalog
       * section. Used to link a native Purchase `Button` to a different catalog —
       * e.g. a CustomEmbed's child ProductCatalog so the embed's `selectProduct()`
       * drives the native button (Scheme B). Searches both `flexibleLayout.sections`
       * and `flexibleLayout.stickyContainer.sections`; the parent may be a top-level
       * ProductCatalog or one nested in a CustomEmbed's `embedContent.childSections`.
       */
      op: 'setSectionParent';
      sectionId: string;
      parentSectionId: string;
    }
  | {
      /**
       * Safely update one allowlisted `LayoutSection.layout` scalar field inside a
       * FlexibleLayout screen. The Builder service resolves the existing section
       * by stable section id and writes the full sections array through the
       * screen document clone, preserving the FlexibleLayout invariant that
       * callers never patch deep array paths directly.
       */
      op: 'setFlexibleSectionLayoutField';
      sectionId: string;
      field: string;
      value: unknown;
    };

export interface CliFunnelScreenPatchRequest {
  dryRun?: boolean;
  operations: CliFunnelScreenPatchOperation[];
}

export interface CliFunnelScreenPatchData {
  projectId: string;
  funnelId: string;
  versionId: string;
  screenId: string;
  dryRun: boolean;
  screen: Record<string, unknown>;
  changedPaths: string[];
  variablesCreated: CliFunnelScreenVariableReference[];
  warnings: CliFunnelGraphValidationIssue[];
}

export interface CliFunnelScreenRewireRequest {
  fromScreenId: string;
  toScreenId: string;
  incoming?: boolean;
  dryRun?: boolean;
}

export interface CliFunnelScreenRewireData {
  projectId: string;
  funnelId: string;
  versionId: string;
  fromScreenId: string;
  toScreenId: string;
  dryRun: boolean;
  rewiredEdges: CliFunnelScreenEdgeReference[];
  updatedScreenIds: string[];
}

export interface CliFunnelScreenDeleteRequest {
  dryRun?: boolean;
  forceIfUnreachable?: boolean;
}

export interface CliFunnelScreenDeleteData {
  projectId: string;
  funnelId: string;
  versionId: string;
  screenId: string;
  dryRun: boolean;
  deleted: boolean;
  incomingEdgeCount: number;
  outgoingEdgeCount: number;
}

export interface CliFlowDocument {
  schemaVersion: string;
  defaultLanguage: string;
  languages: string[];
  launchScreenId: string;
  screens: Record<string, Record<string, unknown>>;
  metadata: Record<string, unknown>;
  variables: Record<string, unknown>;
  /** Flow-level animation defaults (schema 2.17) — present only when the version carries them */
  defaultTransition?: Record<string, unknown>;
  defaultScreenAnimation?: Record<string, unknown>;
  /** Per-StepKind transition defaults (local FlowDocument extension, theme Calm Paywall) */
  perKindTransitions?: Record<string, unknown>;
}

export type CliFunnelLocaleExportFormat = 'xlsx' | 'json';
export type CliFunnelLocaleImportFormat = 'xlsx';
export type CliFunnelLocaleAction = '' | 'keep' | 'set' | 'clear';

export interface CliFunnelLocaleBundleMetadata {
  projectId: string;
  funnelId: string;
  versionId: string;
  funnelName?: string;
  versionName?: string;
  schemaVersion: string;
  sourceLocale: string;
  targetLocales: string[];
  defaultLanguage: string;
  languages: string[];
  launchScreenId: string;
}

export interface CliFunnelLocaleScreenContext {
  screenId: string;
  screenOrder: number;
  screenName?: string;
  screenType?: string;
  sourceText: string;
}

export interface CliFunnelLocaleEntry {
  screenId: string;
  screenOrder: number;
  screenName?: string;
  screenType?: string;
  entryId: string;
  entryOrder: number;
  entryRole: string;
  path: string;
  kind: 'text' | 'roller_option' | 'content_slot_text' | 'variable_option_label';
  sourceLocale: string;
  sourceValue: string;
  screenContext: string;
  notes?: string;
  locked?: boolean;
  maxLength?: number;
  targetValues: Record<string, string>;
  actions: Record<string, CliFunnelLocaleAction>;
}

export interface CliFunnelLocaleBundle {
  schemaVersion: 'cli.funnel-locales.v1';
  exportedAt: string;
  metadata: CliFunnelLocaleBundleMetadata;
  screens: CliFunnelLocaleScreenContext[];
  entries: CliFunnelLocaleEntry[];
  aiInstructions: string[];
}

export interface CliFunnelLocaleExportRequest {
  sourceLocale?: string;
  targetLocales?: string[];
  format?: CliFunnelLocaleExportFormat;
}

export interface CliFunnelLocaleExportData {
  exportedAt: string;
  projectId: string;
  funnelId: string;
  versionId: string;
  format: CliFunnelLocaleExportFormat;
  filename: string;
  contentType: string;
  contentBase64: string;
  bundle?: CliFunnelLocaleBundle;
  summary: {
    screenCount: number;
    entryCount: number;
    sourceLocale: string;
    targetLocales: string[];
  };
}

export interface CliFunnelLocaleImportRequest {
  format: CliFunnelLocaleImportFormat;
  filename?: string;
  contentBase64: string;
  dryRun?: boolean;
}

export interface CliFunnelLocaleImportData {
  importedAt: string;
  projectId: string;
  funnelId: string;
  versionId: string;
  dryRun: boolean;
  applied: boolean;
  summary: {
    rowCount: number;
    changedEntryCount: number;
    changedScreenCount: number;
    changedVariableCount: number;
    addedLocales: string[];
    targetLocales: string[];
  };
}

export interface CliFunnelDiffRef {
  funnelId: string;
  versionId: string;
}

export type CliFunnelDiffProjectionMode = 'semantic' | 'strict';

export interface CliFunnelDiffRequest {
  source: CliFunnelDiffRef;
  target: CliFunnelDiffRef;
  mode?: CliFunnelDiffProjectionMode;
  includeDetails?: boolean;
  includeUnchanged?: boolean;
  includeLayout?: boolean;
  includeMetadata?: boolean;
}

export type CliFunnelDiffEntityStatus = 'added' | 'removed' | 'changed' | 'unchanged';
export type CliFunnelDiffChangeKind = 'added' | 'removed' | 'changed';

export interface CliFunnelDiffChange {
  path: string;
  kind: CliFunnelDiffChangeKind;
  before?: unknown;
  after?: unknown;
  beforeCount?: number;
  afterCount?: number;
}

export interface CliFunnelDiffScreenChange {
  key: string;
  type?: string;
  name?: string;
  status: CliFunnelDiffEntityStatus;
  changeCount: number;
  changes?: CliFunnelDiffChange[];
}

export interface CliFunnelDiffEntityChange {
  key: string;
  status: CliFunnelDiffEntityStatus;
  changeCount: number;
  changes?: CliFunnelDiffChange[];
}

export interface CliFunnelDiffData {
  comparedAt: string;
  projectId: string;
  source: CliFunnelDiffRef;
  target: CliFunnelDiffRef;
  filter: {
    mode: CliFunnelDiffProjectionMode;
    includeLayout: boolean;
    includeMetadata: boolean;
  };
  summary: {
    screensAdded: number;
    screensRemoved: number;
    screensChanged: number;
    screensUnchanged: number;
    edgesAdded: number;
    edgesRemoved: number;
    edgesChanged: number;
    variablesAdded: number;
    variablesRemoved: number;
    variablesChanged: number;
    launchScreenChanged: boolean;
    changedScreenKeys: string[];
  };
  launchScreen?: CliFunnelDiffChange;
  screens: CliFunnelDiffScreenChange[];
  edges: CliFunnelDiffEntityChange[];
  variables: CliFunnelDiffEntityChange[];
}

export type CliFunnelAuditStatus = 'passed' | 'warning' | 'failed';
export type CliFunnelAuditIssueSeverity = 'info' | 'warning' | 'error';

/**
 * `default` keeps the historical audit contract byte-stable (new flow-health
 * findings surface as warnings only). `healthcheck` is the strict profile:
 * flow-health findings escalate to errors and email capture auto-arms when a
 * paywall exists ("zero repairs" launch criterion).
 */
export type CliFunnelAuditProfile = 'default' | 'healthcheck';

export interface CliFunnelAuditRequest {
  requiredVariables?: string[];
  requirePaywall?: boolean;
  profile?: CliFunnelAuditProfile;
  /** 'auto' (default) = require email capture only when a Paywall screen exists. */
  requireEmailCapture?: boolean | 'auto';
}

export interface CliFunnelUnwiredHandleEntry {
  screenId: string;
  screenType: string;
  handleLabel: string;
  sourceActionKey: string;
}

export interface CliFunnelUnresolvedPaywallProductEntry {
  productId: string;
  screenIds: string[];
  knownProductIds: string[];
}

export interface CliFunnelCompleteWithEdgesEntry {
  screenId: string;
  actionKey: string;
  edgeCount: number;
}

export interface CliFunnelEmailCaptureEvidence {
  required: boolean;
  satisfied: boolean;
  variableId?: string;
  screenId?: string;
}

/**
 * A transition condition that filters an enum variable on option ids which are
 * no longer defined by that variable — a stale/old reference left behind when
 * the enum's options were regenerated with new ids. The branch mis-routes (the
 * dead ids can never be selected) or, when `allStale`, can never fire at all.
 */
export interface CliFunnelStaleConditionEntry {
  screenId: string;
  /** Content path of the offending condition, e.g. embed.callback.action.edges.0.conditions.0. */
  path: string;
  variableId: string;
  operator: string;
  staleOptionIds: string[];
  validOptionIds: string[];
  /** true when EVERY option id in the condition value is stale → branch never matches. */
  allStale: boolean;
}

/**
 * A transition condition keyed on a variable id/name that does not exist in this
 * funnel version — typically an old variable that was renamed or removed while
 * the condition kept the stale key. The condition can never evaluate as intended.
 */
export interface CliFunnelUnknownConditionVariableEntry {
  screenId: string;
  path: string;
  variableKey: string;
  operator: string;
}

export interface CliFunnelAuditIssue {
  id: string;
  severity: CliFunnelAuditIssueSeverity;
  message: string;
  path?: string;
  evidence?: Record<string, unknown>;
}

export interface CliFunnelAuditData {
  auditedAt: string;
  projectId: string;
  funnelId: string;
  versionId: string;
  status: CliFunnelAuditStatus;
  summary: {
    screenCount: number;
    variableCount: number;
    transitionCount: number;
    reachableScreenCount: number;
    unreachableScreenCount: number;
    deadEndScreenCount: number;
    issueCount: number;
    errors: number;
    warnings: number;
  };
  launchScreenId?: string | null;
  unreachableScreens: string[];
  deadEndScreens: string[];
  invalidTransitions: Array<{
    from: string;
    action: string;
    to: string;
  }>;
  missingVariables: string[];
  paywallScreens: Array<{
    id: string;
    productCount: number;
  }>;
  issues: CliFunnelAuditIssue[];
  /** Flow-health evidence (present when the corresponding check ran). */
  profile?: CliFunnelAuditProfile;
  unwiredHandles?: CliFunnelUnwiredHandleEntry[];
  unresolvedPaywallProducts?: CliFunnelUnresolvedPaywallProductEntry[];
  unboundCollectScreens?: string[];
  completeWithEdgesTriggers?: CliFunnelCompleteWithEdgesEntry[];
  emailCapture?: CliFunnelEmailCaptureEvidence;
  staleConditionOptions?: CliFunnelStaleConditionEntry[];
  unknownConditionVariables?: CliFunnelUnknownConditionVariableEntry[];
  /** Deterministic verification report (`strategy-health-report/v1`; healthcheck profile). */
  strategyHealthReport?: CliStrategyHealthReport;
}

/**
 * Compact healthcheck attachment mirrored into compile/push responses so the
 * CLI can print a loud stderr block without a second audit round-trip.
 */
export interface CliFunnelHealthcheckSummary {
  status: CliFunnelAuditStatus;
  errors: number;
  warnings: number;
  issues: CliFunnelAuditIssue[];
}

// ── Custom Screen (V2 WebEmbed / CustomEmbed) ────────────────────────────────
//
// Focused read/update/healthcheck/image surface for a single WebEmbed screen.
// `dataSources` / `childSections` are full `LayoutSection` JSON passed through
// untouched; `getConfig` mirrors the editor "Get Config" envelope produced by
// `buildEmbedSdkConfig` so agents see the exact same SDK contract.

export interface CliCustomScreenCondition {
  key: string;
  operator: CliScreenTypeConditionOperator;
  value?: string;
  logic?: 'and' | 'or';
}

export interface CliCustomScreenEdge {
  to: string;
  conditions: CliCustomScreenCondition[];
}

/** Mirror of the editor's `EmbedSdkConfig` (kept structural to avoid coupling
 *  shared-types to the frontend reactFlow module). */
export interface CliCustomScreenSdkConfig {
  screen: { id: string; name?: string; type: string };
  dataSources: Array<Record<string, unknown>>;
  variables: Array<Record<string, unknown>>;
  sdk: Record<string, Record<string, string>>;
}

export interface CliCustomScreenGetData {
  fetchedAt: string;
  projectId: string;
  funnelId: string;
  versionId: string;
  screenId: string;
  screenType: string;
  name?: string;
  position?: CliFunnelGraphPosition | null;
  embed: {
    html: string;
    isIframe: boolean;
    childSections: Array<Record<string, unknown>>;
  };
  callback: {
    edges: CliCustomScreenEdge[];
  };
  getConfig: CliCustomScreenSdkConfig;
}

export interface CliCustomScreenEdgeInput {
  to: string;
  conditions?: CliFunnelGraphConditionInput[];
}

export interface CliCustomScreenApplyRequest {
  screenId: string;
  /** When true, create a new WebEmbed screen instead of patching an existing one. */
  create?: boolean;
  type?: 'WebEmbed';
  name?: string;
  position?: CliFunnelGraphPosition;
  html?: string;
  isIframe?: boolean;
  /** Full `LayoutSection[]` to set as the embed data sources (replace). */
  dataSources?: Array<Record<string, unknown>>;
  /** Replace the embed callback edges (single output, multiple conditional routes). */
  edges?: CliCustomScreenEdgeInput[];
  /** Mark this screen as the funnel launch screen. */
  launch?: boolean;
}

export interface CliCustomScreenApplyData {
  appliedAt: string;
  projectId: string;
  funnelId: string;
  versionId: string;
  screenId: string;
  screenType: string;
  created: boolean;
  updatedFields: string[];
  edgeCount: number;
  conditionCount: number;
}

// ── FlexibleLayout CustomEmbed section apply/get ────────────────────────────
// A CustomEmbed section lives INSIDE a FlexibleLayout screen at
// `content.flexibleLayout.sections[i].embedContent` (vs the whole-screen WebEmbed
// `content.embed`). These set/read ONE section's embed html + child data sources
// (e.g. a ProductCatalog) and its `section.{id}.callback` edges, leaving sibling
// sections untouched. `custom-screen apply` refuses non-WebEmbed screens, so this
// is the FlexibleLayout-aware sibling surface.

export interface CliCustomScreenSectionApplyRequest {
  /** The FlexibleLayout host screen id. */
  screenId: string;
  /** The CustomEmbed section id to patch. Omit together with `createSection` to append a new one. */
  sectionId?: string;
  /** When true, create a new CustomEmbed section instead of patching an existing one. */
  createSection?: boolean;
  /** Section label (create mode / rename). */
  label?: string;
  /** Render order for a newly created section (defaults to end of the list). */
  order?: number;
  html?: string;
  isIframe?: boolean;
  /** Full `LayoutSection[]` to set as `embedContent.childSections` (replace) — e.g. a ProductCatalog. */
  dataSources?: Array<Record<string, unknown>>;
  /** Replace the section callback edges (`section.{id}.callback`). */
  edges?: CliCustomScreenEdgeInput[];
  /** Optional `SectionLayout` to set on the section (create/update). */
  layout?: Record<string, unknown>;
}

export interface CliCustomScreenSectionApplyData {
  appliedAt: string;
  projectId: string;
  funnelId: string;
  versionId: string;
  screenId: string;
  screenType: string;
  sectionId: string;
  sectionCreated: boolean;
  updatedFields: string[];
  edgeCount: number;
  conditionCount: number;
}

export interface CliCustomScreenSectionSummary {
  id: string;
  order: number;
  kind: string;
  label?: string;
  /** Present for CustomEmbed sections. */
  embed?: {
    html: string;
    isIframe: boolean;
    childSections: Array<Record<string, unknown>>;
  };
}

export interface CliCustomScreenSectionGetData {
  fetchedAt: string;
  projectId: string;
  funnelId: string;
  versionId: string;
  screenId: string;
  screenType: string;
  /** The requested CustomEmbed section, or all CustomEmbed sections when no id is given. */
  sections: CliCustomScreenSectionSummary[];
}

export interface CliCustomScreenHealthCheckRequest {
  screenId: string;
}

export interface CliCustomScreenVariableReference {
  screenId: string;
  actionKey: string;
  routeIndex: number;
}

export interface CliCustomScreenHealthCheckData {
  checkedAt: string;
  projectId: string;
  funnelId: string;
  versionId: string;
  screenId: string;
  status: CliFunnelAuditStatus;
  summary: {
    dataSourceCount: number;
    variableCount: number;
    edgeCount: number;
    conditionCount: number;
    issueCount: number;
    errors: number;
    warnings: number;
  };
  /** Variables this custom screen produces (FunnelVariable.boundScreenIds includes it). */
  boundVariables: string[];
  /** For each variable the screen produces: every edge in the funnel that references it. */
  referencedBy: Record<string, CliCustomScreenVariableReference[]>;
  issues: CliFunnelAuditIssue[];
}

/** One uploaded local image the CLI has already pushed to the CDN. */
export interface CliCustomScreenImageMapping {
  originalPath: string;
  assetUrl: { origin: string; small?: string };
  filename?: string;
}

export interface CliCustomScreenImageScanRequest {
  screenId: string;
  /** Image dimension to register as a data source (defaults to default language). */
  locale?: string;
  /** When present, rewrite the HTML + create Media data sources from these uploads. */
  apply?: CliCustomScreenImageMapping[];
}

export interface CliCustomScreenImageScanData {
  scannedAt: string;
  projectId: string;
  funnelId: string;
  versionId: string;
  screenId: string;
  /** Local/relative image paths found in the HTML (the CLI uploads these from disk). */
  localImages: Array<{ path: string; context: string; lineNumber: number }>;
  /** External http(s) URLs (out of scope for local-first migration). */
  remoteImages: string[];
  /** Already-CDN URLs (left untouched). */
  cdnImages: string[];
  applied?: {
    uploaded: number;
    rewritten: boolean;
    childSectionsCreated: number;
    skipped: Array<{ path: string; reason: string }>;
  };
}

// ── Content Plan Articles (Flexible Layout article drafts) ──────────────────

export type CliArticleDraftStatus = 'draft' | 'published' | 'retired';
export type CliArticleRepresentationProfile = 'standard' | 'phone' | 'tablet' | 'laptop';

export interface CliArticleSummary {
  id: string;
  projectId: string;
  title: string;
  alias: string;
  status: CliArticleDraftStatus;
  defaultLanguage: string;
  representationProfile: CliArticleRepresentationProfile;
  createdAt: string;
  updatedAt: string;
  createdBy?: string | null;
  publishedAt?: string;
  /**
   * Set by `articles retire` (G3): the page was taken down and its URL redirects (301) or answers 410. A later
   * edit flips `status` back to `draft` but keeps this marker; a later publish makes `publishedAt` newer again.
   */
  retiredAt?: string;
  /** Customer-facing article URL. Prefer the branded article custom domain. */
  publishedUrl?: string;
  /** Direct static HTML asset URL under assets/articles, kept for verification/debugging. */
  assetUrl?: string;
  configUrl?: string;
  gatewayUrl?: string;
  customDomainUrl?: string;
  webShellVersion?: string;
  /** Folder the article is filed in (`projects/{pid}/articleFolders/{id}`); `null` = unfiled. `articles list` only. */
  folderId?: string | null;
  /** Display name of that folder; `null` when unfiled or the folder no longer exists. `articles list` only. */
  folderName?: string | null;
  /**
   * The live page's head meta, from the article site index. `articles list` only; present whenever the alias has
   * an index entry, whatever the record's current `status` — a Studio save or CLI apply flips a live article back
   * to `draft`; only `articles retire` unpublishes (it removes the index entry), so while an entry exists the page
   * it describes is live.
   */
  seo?: CliArticleSeoMeta;
}

export interface CliArticleDetail extends CliArticleSummary {
  flowDocument: Record<string, unknown>;
}

export interface CliArticleListData {
  fetchedAt: string;
  projectId: string;
  count: number;
  articles: CliArticleSummary[];
}

/**
 * What a published article's page says about itself — meta description, og:image, canonical and JSON-LD dates —
 * read from the article site index written at publish time (`projects/{pid}/articleSiteIndex/{alias}`).
 */
export interface CliArticleSeoMeta {
  /** The published page's title — what a card names the article, not the draft record's (possibly edited) title. */
  title: string;
  canonicalUrl: string;
  description: string;
  imageUrl?: string;
  locale: string;
  datePublished: string;
  dateModified: string;
  /** Topic ids of the published page (`metadata.topics` at publish, via the article site index); primary topic first. */
  topics?: string[];
}

/** One article folder (`projects/{pid}/articleFolders/{id}`), as authors see it in Studio. */
export interface CliArticleFolder {
  id: string;
  name: string;
  parentFolderId: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CliArticleFolderListData {
  fetchedAt: string;
  projectId: string;
  count: number;
  folders: CliArticleFolder[];
}

/** `internal articles folders create` (F3): ensure a folder by exact name under a parent (root = null). */
export interface CliArticleFolderCreateRequest {
  name: string;
  parentFolderId?: string | null;
}

export interface CliArticleFolderCreateData {
  projectId: string;
  /** false = a folder with this exact name already existed under the parent; nothing was written. */
  created: boolean;
  folder: CliArticleFolder;
}

/** `internal articles move` (F3): file articles into a folder (`folderId`) or unfile them (`null`). */
export interface CliArticleMoveRequest {
  articleIds: string[];
  folderId: string | null;
}

export interface CliArticleMoveResult {
  articleId: string;
  ok: boolean;
  alias?: string;
  previousFolderId?: string | null;
  /** `unchanged` = already in the target folder (no write). */
  action?: 'moved' | 'unchanged';
  error?: string;
}

export interface CliArticleMoveData {
  projectId: string;
  folderId: string | null;
  folderName: string | null;
  moved: number;
  unchanged: number;
  failed: number;
  results: CliArticleMoveResult[];
}

/** `internal articles folders rename` (F3): rename a folder in place; the id never changes. */
export interface CliArticleFolderRenameRequest {
  name: string;
}

export interface CliArticleFolderRenameData {
  projectId: string;
  folder: CliArticleFolder;
  previousName: string;
  /** false = the requested name equals the current name (idempotent no-op); nothing was written. */
  renamed: boolean;
}

/** `internal articles folders delete` (F3): delete an empty folder — no child folders, no articles of any status. */
export interface CliArticleFolderDeleteData {
  projectId: string;
  folderId: string;
  name: string;
  deleted: true;
}

/** Owned keys of projects/{pid}/articleSiteSettings/default (article template G4). */
export type CliArticleSiteSettingsKey = 'presentation' | 'author' | 'topics';
export type CliArticleSiteSettingsAction = 'created' | 'updated' | 'unchanged' | 'would_create' | 'would_update';

/** `content-plan articles site-settings get`: the stored owned keys, even when invalid, with every validation issue. */
export interface CliArticleSiteSettingsData {
  fetchedAt: string;
  projectId: string;
  exists: boolean;
  settings: ArticleSiteSettings | null;
  errors: CliArticleIssue[];
  warnings: CliArticleIssue[];
  updatedAt?: string;
}

/** `content-plan articles site-settings apply`: the WHOLE owned document; an omitted owned key is removed. */
export interface CliArticleSiteSettingsApplyRequest {
  settings: ArticleSiteSettings;
  /** Server-side dry-run: validate and report the change, write nothing. */
  dryRun?: boolean;
}

export interface CliArticleSiteSettingsApplyData {
  appliedAt: string;
  projectId: string;
  dryRun: boolean;
  action: CliArticleSiteSettingsAction;
  changedKeys: CliArticleSiteSettingsKey[];
  settings: ArticleSiteSettings;
  warnings: CliArticleIssue[];
}

export interface CliArticleDetailData {
  projectId: string;
  article: CliArticleDetail;
}

export interface CliArticleApplyRequest {
  id?: string;
  title?: string;
  alias?: string;
  defaultLanguage?: string;
  representationProfile?: CliArticleRepresentationProfile;
  createdBy?: string | null;
  flowDocument?: Record<string, unknown>;
}

export interface CliArticleIssue {
  field: string;
  message: string;
}

export interface CliArticleApplyData {
  appliedAt: string;
  projectId: string;
  article: CliArticleDetail;
  created: boolean;
  updatedFields: string[];
  /** Non-blocking validation findings (G2, D15): short-answer length, long meta description, unusable project article settings. */
  warnings?: CliArticleIssue[];
}

export interface CliArticleCloneRequest {
  title?: string;
  alias?: string;
  createdBy?: string | null;
}

export interface CliArticleCloneData {
  clonedAt: string;
  projectId: string;
  sourceArticleId: string;
  article: CliArticleDetail;
}

export interface CliArticleAddImageRequest {
  imageUrl?: string;
  assetUrl?: {
    origin?: string;
    original?: string;
    small?: string;
  };
  sectionId?: string;
  label?: string;
  locale?: string;
  order?: number;
  heightValue?: number;
}

export interface CliArticleAddImageData {
  appliedAt: string;
  projectId: string;
  articleId: string;
  sectionId: string;
  article: CliArticleDetail;
}

export interface CliArticlePublishData {
  publishedAt: string;
  projectId: string;
  articleId: string;
  alias: string;
  /** Customer-facing article URL. Prefer the branded article custom domain. */
  url: string;
  publishedUrl: string;
  /** Direct static HTML asset URL under assets/articles, kept for verification/debugging. */
  assetUrl: string;
  configUrl: string;
  gatewayUrl?: string;
  customDomainUrl?: string;
  webShellVersion?: string;
  /** Non-blocking SEO/AEO findings from publish (iframe embeds, missing h1/description, page weight, stale site index). */
  seoWarnings?: string[];
  article: CliArticleDetail;
}

// ── Article graph: related refresh (article template G3, spec §7.2) ─────────

export type CliArticleRelatedRefreshStatus = 'updated' | 'unchanged' | 'would_update' | 'skipped';

/** Why one article was not refreshed — every skip is reported, never silent. */
export type CliArticleRelatedRefreshSkipReason =
  /** No ArticleRelated block on the article's launch screen. Older clients only; the builder now reports `no_list_sections`. */
  | 'no_related_section'
  /** Neither an ArticleRelated nor an ArticleIndex block on the article's launch screen — nothing to fill. */
  | 'no_list_sections'
  /** The published page has no related (or index) markers (published before the renderer wrote them) — republish it. */
  | 'no_related_region'
  /** More than one marker pair, or markers out of order. */
  | 'region_ambiguous'
  /** The region holds markup without the related aside — never guessed. */
  | 'region_unrecognized'
  /** The renderer produced no related aside although the list is not empty. */
  | 'render_missing_related'
  /** The renderer produced no section for an ArticleIndex block (the component always renders one, hidden when empty). */
  | 'render_missing_index'
  /** Site-index entry without published files. */
  | 'not_published'
  /** The published config.json is not a JSON object. */
  | 'config_unreadable'
  /** A publish or another refresh wrote the files meanwhile; re-run the refresh. */
  | 'changed_during_refresh'
  /** Unexpected failure for this article (see `message`); the other articles were still processed. */
  | 'failed';

export interface CliArticleRelatedPinSkip {
  alias: string;
  reason: 'self' | 'same_canonical' | 'not_published' | 'duplicate';
}

/** One ArticleIndex section of an article: the aliases listed before and after the refresh (Spec Issue #17). */
export interface CliArticleRelatedRefreshIndexList {
  sectionId: string;
  before: string[];
  after: string[];
}

export interface CliArticleRelatedRefreshArticle {
  alias: string;
  status: CliArticleRelatedRefreshStatus;
  reason?: CliArticleRelatedRefreshSkipReason;
  message?: string;
  /** Non-blocking findings, e.g. the page bakes another web-shell version than the one that rendered the region (R18). */
  warnings?: string[];
  /** Aliases of the related list currently published (config.json `articleRuntime.related`). */
  before: string[];
  /** Aliases the article graph gives now (top-N, N = the ArticleRelated block's limit). */
  after: string[];
  /** Pins the graph could not honour (retired/unknown alias, self, same canonical, duplicate). */
  skippedPins: CliArticleRelatedPinSkip[];
  /** One entry per ArticleIndex section of the launch screen (absent when the article has none). */
  index?: CliArticleRelatedRefreshIndexList[];
}

export interface CliArticleRelatedRefreshRequest {
  /** Refresh one published article only (default: every published article of the project). */
  alias?: string;
  /** Report old and new lists without writing anything. */
  dryRun?: boolean;
}

export interface CliArticleRelatedRefreshData {
  refreshedAt: string;
  projectId: string;
  dryRun: boolean;
  /** The web-shell version whose SSR bundle rendered (or would render) the regions (R18). */
  webShellVersion: string;
  counts: { updated: number; unchanged: number; wouldUpdate: number; skipped: number };
  /** Run-level findings, e.g. unusable project article settings → cards without a topic label (R16). */
  warnings: string[];
  articles: CliArticleRelatedRefreshArticle[];
}

// ── Article retire (article template G3, spec §7.3) ──────────────────────────

export interface CliArticleRetireRequest {
  /** Published alias the retired URL answers 301 to; absent → 410. */
  redirectTo?: string;
  /** Report what would change without writing. */
  dryRun?: boolean;
}

export interface CliArticleRetireData {
  retiredAt: string;
  projectId: string;
  articleId: string;
  alias: string;
  dryRun: boolean;
  redirectTo: string | null;
  /** The target's canonical URL recorded in redirects.json (the 301 goes there on a same-project domain); null without a target. */
  redirectUrl: string | null;
  /** Bucket objects deleted (dry-run: that would be deleted). */
  deletedObjects: string[];
  indexEntryRemoved: boolean;
  /** Earlier retired aliases whose redirect now points at the new target (chains collapsed to one hop). */
  redirectsRewritten: string[];
  /** Published articles whose related list linked the retired alias, plus every ArticleIndex holder — run `articles related refresh`. */
  relatedToRefresh: string[];
  siteFilesRebuilt: boolean;
  warnings: string[];
  /** The draft after the retire (status `retired`); absent on a dry-run. */
  article?: CliArticleDetail;
}

// ── Variables (single-version + project-wide aggregate) ──────────────────────

export interface CliVariableSummary extends Omit<Partial<CliVariableInput>, 'type' | 'options'> {
  id: string;
  name?: string;
  type: string;
  scope?: 'screen' | 'global';
  required?: boolean;
  /** Explicit privacy review decision. true excludes the variable from analytics collection. */
  excludeFromAnalytics?: boolean;
  options?: Array<{ id: string; label?: string }>;
  boundScreenIds?: string[];
}

export interface CliVersionVariablesData {
  projectId: string;
  funnelId: string;
  versionId: string;
  variableCount: number;
  variables: CliVariableSummary[];
  /** Opaque compare-and-set receipt for the complete stored variable map. */
  expectedVariablesHash: string;
}

/** One place a variable id is defined in the project (a single funnel version). */
export interface CliProjectVariableLocation {
  funnelId: string;
  versionId: string;
  funnelName?: string;
  scope?: 'screen' | 'global';
  type: string;
  optionIds: string[];
  optionLabels: string[];
  boundScreenIds: string[];
  /** Type, scope, requiredness, task scope, id/label pairs, lineage and frozen collection contract. */
  semanticDefinitionHash?: string;
  collectionContractErrors?: string[];
}

/** A variable id aggregated across the whole project, with every place it is defined. */
export interface CliProjectVariableDefinition {
  id: string;
  occurrences: number;
  /** True when definitions share the same measurement semantics and no supplied collection contract is invalid. */
  consistent: boolean;
  locations: CliProjectVariableLocation[];
}

export interface CliProjectVariablesData {
  fetchedAt: string;
  projectId: string;
  summary: {
    onboardingCount: number;
    versionCount: number;
    versionsWithVariables: number;
    distinctVariableCount: number;
  };
  variables: CliProjectVariableDefinition[];
}

// ── Product Page CRUD (project tasks, variableSet, audiences) ────────────────

export interface CliProductTaskSummary {
  id: string;
  name: string;
  description?: string;
  priority?: number;
  activationAction?: string;
  funnelGoals?: Array<Record<string, unknown>>;
  requiredInfo?: unknown[];
  requiredTimeMinutes?: number;
  userTrustLevel?: number;
  competitorsForClients?: unknown[];
  userCondition?: unknown[];
  createdAt?: string | null;
  updatedAt?: string | null;
  raw?: Record<string, unknown>;
}

export type CliProductTaskDetail = CliProductTaskSummary;

export interface CliProductTaskWriteRequest {
  name?: string;
  description?: string;
  priority?: number;
  activationAction?: string;
  funnelGoals?: Array<Record<string, unknown>>;
  requiredInfo?: unknown[];
  requiredTimeMinutes?: number;
  userTrustLevel?: number;
  competitorsForClients?: unknown[];
  userCondition?: unknown[];
}

export interface CliProductTaskListData {
  projectId: string;
  tasks: CliProductTaskSummary[];
  taskCount: number;
}

export interface CliProductTaskData {
  projectId: string;
  task: CliProductTaskDetail;
  created?: boolean;
  updated?: boolean;
}

export interface CliProductTaskDeleteData {
  projectId: string;
  taskId: string;
  deleted: true;
}

export type CliProductVariableType = 'string' | 'number' | 'boolean' | 'date' | 'enum' | 'enum[]';

export interface CliProductVariableOption {
  id: string;
  label: string;
}

export interface CliProductVariable extends Partial<Omit<FunnelVariable, 'id' | 'name' | 'type' | 'required' | 'description' | 'taskIds' | 'options'>> {
  id: string;
  name: string;
  type: CliProductVariableType;
  required: boolean;
  description: string;
  taskIds: string[];
  options: CliProductVariableOption[];
}

export interface CliProductVariableWriteRequest extends Partial<CliProductVariable> {
  expectedVariablesHash?: string;
  dryRun?: boolean;
  id?: string;
  name?: string;
  type?: CliProductVariableType;
  required?: boolean;
  description?: string;
  taskIds?: string[];
  options?: CliProductVariableOption[];
}

export interface CliProductVariableApplyRequest {
  variables: CliProductVariableWriteRequest[] | Record<string, CliProductVariableWriteRequest>;
  replace?: boolean;
  expectedVariablesHash?: string;
  dryRun?: boolean;
}

export interface CliProductVariableListData {
  projectId: string;
  variables: CliProductVariable[];
  variableCount: number;
  expectedVariablesHash: string;
}

export interface CliProductVariableData {
  projectId: string;
  variable: CliProductVariable;
  created?: boolean;
  updated?: boolean;
  expectedVariablesHash: string;
  dryRun?: boolean;
}

export interface CliProductVariableApplyData {
  projectId: string;
  replace: boolean;
  upserted: number;
  variableCount: number;
  variables: CliProductVariable[];
  expectedVariablesHash: string;
  dryRun?: boolean;
}

export interface CliProductVariableDeleteData {
  projectId: string;
  variableId: string;
  deleted: true;
  variableCount: number;
  expectedVariablesHash: string;
}

export interface CliProductVariablePurgeInsightDerivedData {
  projectId: string;
  removedIds: string[];
  keptCount: number;
  dryRun: boolean;
  variablesHash: string;
}

export interface CliProductAudienceSummary {
  id: string;
  name: string;
  description?: string;
  weight?: number;
  taskId?: string;
  taskName?: string;
  dmuMode?: 'b2b' | 'b2c' | string;
  icpProfile?: Record<string, unknown>;
  creationDate?: string | null;
  updatedAt?: string | null;
  lastModified?: string | null;
  raw?: Record<string, unknown>;
}

export type CliProductAudienceDetail = CliProductAudienceSummary;

export interface CliProductAudienceWriteRequest {
  name?: string;
  description?: string;
  weight?: number;
  taskId?: string;
  taskName?: string;
  dmuMode?: 'b2b' | 'b2c' | string;
  icpProfile?: Record<string, unknown>;
}

export interface CliProductAudienceListData {
  projectId: string;
  audiences: CliProductAudienceSummary[];
  audienceCount: number;
}

export interface CliProductAudienceData {
  projectId: string;
  audience: CliProductAudienceDetail;
  created?: boolean;
  updated?: boolean;
}

export interface CliProductAudienceDeleteData {
  projectId: string;
  audienceId: string;
  deleted: true;
  cascade: boolean;
  deletedActors: number;
  deletedActorSubcollectionDocs: number;
}

/** Spec C (C2): managed actor write path — actors are an audience subcollection. */
export interface CliProductActorSummary {
  id: string;
  audienceId: string;
  name: string;
  description?: string;
  /** Actor importance 1-5 (the mutable research layer; snapshots pin values at generation). */
  weight?: number;
  role?: string;
  influence?: string;
  creationDate?: string | null;
  updatedAt?: string | null;
  lastModified?: string | null;
}

export type CliProductActorDetail = CliProductActorSummary;

export interface CliProductActorWriteRequest {
  name?: string;
  description?: string;
  /** Rounded then clamped to [1, 5] server-side (act-first, never a refusal). */
  weight?: number;
  role?: string;
  influence?: string;
}

export interface CliProductActorData {
  projectId: string;
  audienceId: string;
  actor: CliProductActorDetail;
  updated?: boolean;
}

export type CliProductAudienceModel = 'b2c' | 'b2b';

export interface CliProductModeData {
  projectId: string;
  audienceModel: CliProductAudienceModel;
}

export interface CliProductModeUpdateRequest {
  audienceModel: CliProductAudienceModel;
}

export interface CliProductInsightSummary {
  id: string;
  name: string;
  status?: string;
  mode?: 'b2c' | 'b2b' | 'mixed' | string;
  summary?: string;
  selectedActorCount: number;
  insightCount: number;
  /** Count provenance; zero with unavailable is not an empty analysis. */
  insightCountSource?: 'category-manifest' | 'legacy-inline' | 'unavailable';
  insightCountStatus?: 'complete' | 'empty' | 'partial' | 'unavailable';
  createdAt?: string | null;
  updatedAt?: string | null;
  clonedFrom?: Record<string, unknown>;
}

export interface CliProductInsightDetail extends CliProductInsightSummary {
  analysis: Record<string, unknown>;
}

export interface CliProductInsightListData {
  projectId: string;
  insights: CliProductInsightSummary[];
  insightCount: number;
}

export interface CliProductInsightData {
  projectId: string;
  insight: CliProductInsightDetail;
  hydratedExport?: CliProductInsightExport;
}

/** Consistent read-only evidence snapshot; completeness is not strategy readiness. */
export interface CliProductInsightExport {
  schemaVersion: 'segmently.insight-export.v1';
  exportedAt: string;
  projectId: string;
  analysisId: string;
  analysis: Record<string, unknown>;
  categoryPackages: Record<string, Record<string, unknown>>;
  perTaskPackages: Record<string, Record<string, unknown>>;
  inputTaskSnapshots: Record<string, Record<string, unknown>>;
  variableGenerations: Record<string, {
    generation: Record<string, unknown>;
    generatedVariables: Record<string, Record<string, unknown>>;
  }>;
  currentProjectVariableSet: unknown;
  provenance: {
    source: 'firestore-read-only-transaction';
    projectVariableSetScope: 'current-project-not-pinned';
    hashAlgorithm: 'sha256-canonical-json';
    payloadHash: string;
    documentHashes: Record<string, string>;
  };
  completeness: { status: 'complete' | 'partial' | 'unavailable'; issues: string[] };
  lineage: { requestedAnalysisId: string; resolvedHeadId?: string; chain: string[]; status: 'current' | 'superseded' | 'unresolved' };
}

export interface CliProductInsightPatchRequest {
  patch: Record<string, unknown>;
  dryRun?: boolean;
}

/**
 * Spec D2: when editing a referenced + committed analysis triggers copy-on-write
 * (R1), the mutation is redirected onto a fresh `draft` clone so the referenced
 * artifact stays byte-stable. This records the swap for the caller/UI.
 */
export interface CliInsightCopyOnWriteRedirect {
  sourceAnalysisId: string;
  draftAnalysisId: string;
}

export interface CliProductInsightPatchData {
  projectId: string;
  analysisId: string;
  dryRun: boolean;
  changedPaths: string[];
  insight: CliProductInsightDetail;
  updated?: boolean;
  /** Present when the edit was copy-on-write redirected to a draft (Spec D2 R1). */
  redirectedTo?: CliInsightCopyOnWriteRedirect;
}

export interface CliProductInsightItemPatchData {
  projectId: string;
  analysisId: string;
  category: string;
  insightId: string;
  dryRun: boolean;
  changedPaths: string[];
  item: Record<string, unknown>;
  insight: CliProductInsightDetail;
  updated?: boolean;
  /** Present when the edit was copy-on-write redirected to a draft (Spec D2 R1). */
  redirectedTo?: CliInsightCopyOnWriteRedirect;
}

export interface CliProductInsightSourceInput {
  actorId: string;
  actorName: string;
  audienceId: string;
  originalItemId: string;
  originalDescription: string;
  originalWeight: number;
  /**
   * W13-R3: the actor's verbatim interview phrase. Optional — when supplied it is
   * validated against the frozen actor snapshot, and the stored source always takes
   * the frozen phrase (an edit may echo it, never rewrite it).
   */
  originalPhrase?: string;
  similarityScore?: number;
  audienceName?: string;
  role?: string;
  forceTag?: string;
  pyramidLevel?: string;
}

export interface CliProductInsightSourceMutationData {
  projectId: string;
  analysisId: string;
  category: string;
  insightId: string;
  dryRun: boolean;
  item?: Record<string, unknown>;
  removedCluster?: boolean;
  insight: CliProductInsightDetail;
  updated?: boolean;
  /** Present when the edit was copy-on-write redirected to a draft (Spec D2 R1). */
  redirectedTo?: CliInsightCopyOnWriteRedirect;
}

export interface CliProductInsightCloneRequest {
  name?: string;
  /** Stable retry identity within the project; reuse resumes or returns the same clone. */
  requestId?: string;
}

export interface CliProductInsightCloneData {
  projectId: string;
  sourceAnalysisId: string;
  clonedAnalysisId: string;
  insight: CliProductInsightDetail;
  created: boolean;
  requestId?: string;
  resumed?: boolean;
  /** Spec C (C2): heavy artifacts deep-copied so recompute works on the clone. */
  copiedSubcollectionDocs?: number;
  /** Spec C (C2): analysisId-embedded packagePath pointers rewritten to the clone id. */
  rewrittenPointers?: number;
}

/* ── Spec D2: insights-analysis lineage chain (flat, keyed by lineageId) ── */

export interface CliProductInsightLineageNode {
  analysisId: string;
  /** Auto-derived label, e.g. "v3 — recompute: pricing". */
  versionLabel?: string;
  /** Edit state (draft = R1 working copy; committed = immutable snapshot). */
  lifecycle?: 'draft' | 'committed';
  /** Compute status (pending/running/partial/completed/failed). */
  status?: string;
  /** analysisId that superseded this node when its draft committed. */
  supersededBy?: string;
  /** Predecessor in the chain (clonedFrom / parentAnalysisId). */
  parentAnalysisId?: string;
  createdAt?: string;
  /** The chain tip — a committed node that nothing supersedes. */
  isHead: boolean;
}

export interface CliProductInsightLineageData {
  projectId: string;
  /** Root analysisId shared by every node in this chain. */
  lineageId: string;
  /** The analysisId the caller asked about (its node is in `nodes`). */
  requestedAnalysisId: string;
  /** Chain nodes ordered oldest → newest by createdAt. */
  nodes: CliProductInsightLineageNode[];
  nodeCount: number;
}

/* ── Spec D3: actor contribution shares + what-if projection (Layers A/B) ── */

export interface CliProductActorThesisShare {
  weightBasis?: 'weighted-evidence-mass';
  insightId: string;
  category: string;
  description: string;
  /** Actor contribution / total under weightBasis (legacy: raw item sum). */
  actorShare: number;
  actorWeightSum: number;
  totalWeightSum: number;
  sourceCount: number;
  topDonor: { actorId: string; actorName: string; share: number } | null;
  /** Matrix-written affinity for this actor, when the compute phase ran. */
  actorAffinity?: number;
}

export interface CliProductWhatIfProjection {
  /** Effective matrix mode after the recompute path's mixed→b2c mapping. */
  matrixMode: string;
  overrides: Record<string, number>;
  unknownOverrideActorIds: string[];
  priorShifts: Array<{ atomId: string; baseline: number; projected: number; delta: number }>;
  insightMetricDeltas: Array<{
    insightId: string;
    category: string;
    description: string;
    informationGain: { baseline: number; projected: number; delta: number };
    discriminativePower: { baseline: number; projected: number; delta: number };
    evidenceMass?: { baseline: number; projected: number; delta: number };
  }>;
  questionRankMoves: Array<{
    insightId: string;
    questionText: string;
    baselineRank: number;
    projectedRank: number;
    move: number;
  }>;
}

export interface CliProductInsightActorContributionData {
  projectId: string;
  analysisId: string;
  actor: { actorId: string; name: string; weight: number; audienceId?: string };
  /** weight ÷ Σ same-audience actor weights (D53 §9.1 input); null when not derivable. */
  impliedWithinAudienceShare: number | null;
  /** Per-thesis donor shares, sorted by actorShare desc (Layer A). */
  shares: CliProductActorThesisShare[];
  /** Present only when `whatIfWeight` was requested (Layer B, real downstream). */
  whatIf?: CliProductWhatIfProjection;
  /**
   * D53 §9.1 model-suggested weight. Computed from `estimate` (the caller's
   * proposal-row magnitude) when provided; `basis: 'unavailable'` otherwise —
   * copy is never synthesized from a direction enum.
   */
  suggestion: {
    suggestedWeight: number | null;
    sentence: string;
    deviation?: number;
    basis: 'estimate' | 'unavailable';
  };
}

/* ── Spec C (C3): insights content-diff + strategy reverse index ────────── */

export interface CliInsightWeightDelta {
  from?: number;
  to?: number;
  delta?: number;
}

export interface CliInsightContentDiffEntry {
  /** Insight id in the TO analysis (or the FROM id when removed). */
  insightId: string;
  /** FROM-side id when the thesis was matched by canonicalDescription, not id. */
  fromInsightId?: string;
  category: string;
  canonicalDescription: string;
  status: 'appeared' | 'removed' | 'changed';
  cumulativeWeight?: CliInsightWeightDelta;
  frequency?: CliInsightWeightDelta;
  avgWeight?: CliInsightWeightDelta;
  informationGain?: CliInsightWeightDelta;
}

export interface CliInsightQuestionOptionMove {
  category: string;
  insightId: string;
  label?: string;
  status: 'appeared' | 'removed' | 'changed';
  cumulativeWeight?: CliInsightWeightDelta;
  labelChanged?: boolean;
}

export interface CliInsightContentDiff {
  fromAnalysisId: string;
  toAnalysisId: string;
  entries: CliInsightContentDiffEntry[];
  questionOptionMoves: CliInsightQuestionOptionMove[];
  unchangedCount: number;
}

export interface CliInsightCitationRef {
  via: 'aiMeta.dataMapping' | 'variable.insightSource' | 'variable.dataUsage';
  blockId?: string;
  blockName?: string;
  blockOrder?: number;
  /** Block sits strictly before the first Paywall-bearing block (the value point). */
  critical: boolean;
  screenId?: string;
  screenType?: string;
  variableId?: string;
  variableName?: string;
  analysisId?: string;
  sourcePath?: string;
  /**
   * W13 (DM1): the `originalItemId` of the quoted source, when the citation is a
   * quote sub-path `…[insightId].sources[<originalItemId>]`. Detail only — the
   * citation is filed under its THESIS, never under the item.
   */
  sourceItemId?: string;
  /**
   * W13 (DM2, AC-W13-28): the quote as the citing entry carries it — the quoted
   * text (`value`), why it drove the decision (`justification`) and the entry's
   * derived actor name. Present ONLY on a quote citation (`sourceItemId` set),
   * and only for the fields the entry actually has, so a thesis-only chain's
   * payload is byte-identical to before.
   *
   * These are DISPLAY detail for the impact dialog's sub-row. The path stays
   * normative (W13-R17): nothing derives grounding from them.
   */
  quotedValue?: string;
  quoteJustification?: string;
  quoteActorName?: string;
}

export interface CliInsightReverseIndexEntry {
  insightId: string;
  citations: CliInsightCitationRef[];
}

export interface CliInsightReverseIndex {
  strategyId: string;
  entries: CliInsightReverseIndexEntry[];
  /** Loss-notes: unparseable or dangling citations — reported, never thrown. */
  losses: string[];
  valuePointBlockId?: string;
}

export type CliInsightImpactVerdict = 'material' | 'minor' | 'none';

export interface CliInsightImpactedInsight {
  insightId: string;
  status: 'appeared' | 'removed' | 'changed';
  category: string;
  canonicalDescription: string;
  citationCount: number;
  critical: boolean;
}

export interface CliInsightImpactData {
  projectId: string;
  strategyId: string;
  fromAnalysisId: string;
  toAnalysisId: string;
  diff: CliInsightContentDiff;
  index: CliInsightReverseIndex;
  impactedInsights: CliInsightImpactedInsight[];
  verdict: CliInsightImpactVerdict;
  losses: string[];
  /**
   * Spec D3 (§9.3): content-plan consumers pinning the FROM analysis — INFO
   * ONLY, never part of the verdict. Present only when the caller opted in
   * with `?consumers=1` (two extra collection scans).
   */
  contentPlan?: CliInsightImpactContentPlan;
}

/** Spec D3 (§9.3): info-only content-plan branch of the impact tree. */
export interface CliInsightImpactContentPlan {
  contentStrategies: Array<{ id: string; title?: string }>;
  topicBacklog: Array<{ id: string; title?: string; pinField: 'insightsAnalysisId' | 'analysisId' }>;
}

/** Spec C (C6): one affected block in the Mode 2 targeted-revise plan. */
export interface CliInsightReviseAffectedBlock {
  blockId: string;
  blockName?: string;
  /** Per-block operator prompt naming the insights this block must absorb. */
  prompt: string;
  insightIds: string[];
}

/**
 * Spec C (C6): the Mode 2 plan — dispatches flow through the EXISTING
 * block-update endpoint per affected block (single write surface); unaffected
 * blocks never receive a call, which is what keeps them byte-stable.
 */
export interface CliInsightRevisePlanData extends CliInsightImpactData {
  affectedBlocks: CliInsightReviseAffectedBlock[];
  /** Rendered what-changed summary (D52) to send as `changeDiff` with every dispatch. */
  changeDiff: string;
  /** Empty affected set → suggest the Mode 3 report instead of a no-op regen. */
  suggestion?: 'mode3';
}

/** Spec D4 (U11): one lineage hop covered by a block rebase-plan range. */
export interface CliStrategyBlockRebaseHop {
  analysisId: string;
  versionLabel?: string;
}

/** Spec D4 (U11): a citation whose target insight no longer exists at the pin. */
export interface CliStrategyBlockRebaseVanishedCitation {
  insightId: string;
  category: string;
  canonicalDescription: string;
}

/**
 * Spec D4 (U11): the single-block REBASE plan — from = the OLDEST analysis the
 * block's screens cite, to = the strategy's CURRENT pin
 * (`inputManifest.insightsAnalysisId`, never the lineage head). The UI forwards
 * `prompt` + `changeDiff` + `promptMode: 'rebase'` verbatim into the existing
 * block-update dispatch.
 */
export interface CliStrategyBlockRebasePlanData {
  projectId: string;
  strategyId: string;
  blockId: string;
  /** Every analysis id the block's screens cite (distinct, sorted). */
  citedAnalysisIds: string[];
  /** All cited ids equal the pin (or nothing is cited) — nothing to rebase. */
  upToDate: boolean;
  fromAnalysisId?: string;
  toAnalysisId?: string;
  /** Lineage nodes from the oldest cited analysis up to the pin (inclusive). */
  hops?: CliStrategyBlockRebaseHop[];
  /** Cited ids that belong to a DIFFERENT lineage than the pin — endpoint diff only. */
  crossLineageAnalysisIds?: string[];
  vanishedCitations?: CliStrategyBlockRebaseVanishedCitation[];
  /** Range preamble + rendered endpoint diff — send as `changeDiff` with the dispatch. */
  changeDiff?: string;
  /** Operator prompt for the rebase dispatch. */
  prompt?: string;
}

export interface CliProductInsightDeleteData {
  projectId: string;
  analysisId: string;
  deleted: true;
}

// ── Full variable management (create/update with options/bindings/scope) ──────

export interface CliVariableOptionInput {
  /** Stable option id (UUID). Generated when omitted. Used as the condition value (schema 2.2.0+). */
  id?: string;
  label: string;
  isDefault?: boolean;
}

export interface CliVariableInput extends Pick<FunnelVariable, 'taskIds' | 'insightSource' | 'collectionContract'> {
  id: string;
  name?: string;
  type: CliScreenTypeVariableType;
  required?: boolean;
  scope?: 'screen' | 'global';
  description?: string;
  /** Explicit privacy review decision. true excludes the variable from analytics collection. */
  excludeFromAnalytics?: boolean;
  /** Required for enum / enum[] types. */
  options?: CliVariableOptionInput[];
  boundScreenIds?: string[];
  /** Section this variable is collected from (e.g. a custom-screen SingleSelectionList data source). */
  boundSectionId?: string;
  screenBindings?: Record<string, Record<string, string>>;
  // numeric constraints
  min?: number;
  max?: number;
  integer?: boolean;
  // date constraints (ISO strings)
  minDate?: string;
  maxDate?: string;
  // enum[] constraints
  minSelections?: number;
  maxSelections?: number;
}

export interface CliVariableApplyRequest {
  variables: CliVariableInput[];
  /** Refuse with 409 if any variable changed since the corresponding list export. */
  expectedVariablesHash?: string;
  /** When true, the provided set REPLACES the version's variables (removes the rest). Default: merge/upsert. */
  replace?: boolean;
  /**
   * C9-S3: by default an update that OMITS boundScreenIds/screenBindings/boundSectionId
   * carries them over from the current definition (a list-projection rewrite must not
   * unbind collect screens). Set true to let omission clear bindings; an explicit
   * empty array in the manifest always clears regardless.
   */
  replaceBindings?: boolean;
  dryRun?: boolean;
}

export interface CliVariableApplyData {
  appliedAt: string;
  projectId: string;
  funnelId: string;
  versionId: string;
  dryRun: boolean;
  created: string[];
  updated: string[];
  removed: string[];
  variableCount: number;
}

export type CliLaunchPreflightStatus = 'ready' | 'warning' | 'blocked';
export type CliLaunchPreflightCheckStatus = 'passed' | 'warning' | 'failed' | 'skipped';
export type CliLaunchPreflightCheckSeverity = 'info' | 'warning' | 'blocker';

export interface CliLaunchPreflightRequest {
  funnelId?: string;
  versionId?: string;
  webPlacementId?: string;
  abTestId?: string;
  requiredVariables?: string[];
  attributionParams?: string[];
  requireActiveTheme?: boolean;
  requirePaywall?: boolean;
  /** @deprecated One-release alias of requirePaymentProvider. */
  requireStripeSandbox?: boolean;
  /** Require Stripe test or Paddle sandbox connection metadata. */
  requirePaymentProvider?: boolean;
  requireWebPlacement?: boolean;
  requirePublished?: boolean;
  requireFacebookAttribution?: boolean;
}

export interface CliLaunchPreflightCheck {
  id: string;
  label: string;
  status: CliLaunchPreflightCheckStatus;
  severity: CliLaunchPreflightCheckSeverity;
  message: string;
  evidence?: Record<string, unknown>;
  nextAction?: string;
}

export interface CliLaunchPreflightNextAction {
  checkId: string;
  reason: string;
  description: string;
  command?: string;
}

export interface CliLaunchPreflightData {
  checkedAt: string;
  projectId: string;
  status: CliLaunchPreflightStatus;
  summary: {
    passed: number;
    warnings: number;
    blockers: number;
    skipped: number;
    total: number;
  };
  checks: CliLaunchPreflightCheck[];
  nextActions: CliLaunchPreflightNextAction[];
}

// ── Launch activation (tier 1, latch read; contracts §2 CLI row) ─────────────

/** Accepted intent values — aliases of the parser's INTENT_* lists (one source, AC-041). */
export const LAUNCH_INTENT_GOALS: readonly IntentGoal[] = INTENT_GOALS;
export const LAUNCH_INTENT_CHANNELS: readonly IntentChannel[] = INTENT_CHANNELS;
export const LAUNCH_INTENT_PRODUCT_ANALYTICS: readonly IntentProductAnalytics[] = INTENT_PRODUCT_ANALYTICS;
export const LAUNCH_INTENT_PLATFORMS: readonly IntentPlatform[] = INTENT_PLATFORMS;

/** `GET /api/cli/v1/projects/:projectId/launch/activation` — the same projection the badge renders (AC-032). */
export type CliLaunchActivationData = ActivationProjection;

/** `POST /api/cli/v1/projects/:projectId/launch/activation/intent` body; `source` is forced to `'cli'` server-side. */
export type CliLaunchIntentSetRequest = Omit<ActivationIntent, 'updatedAt'>;

/** `POST /api/cli/v1/projects/:projectId/launch/next` — the single next open item, or null when complete. */
export interface CliLaunchNextData {
  projectId: string;
  earnedCount: number;
  totalCount: number;
  complete: boolean;
  next: ActivationItem | null;
  computedAt: string;
}

export interface CliFunnelVersionExportData {
  exportedAt: string;
  funnel: CliFunnelSummary;
  version: CliFunnelVersionSummary;
  flowDocument: CliFlowDocument;
}

export interface CliScreenTypeActionKey {
  key: string;
  description: string;
  pattern?: boolean;
}

export interface CliScreenTypeSummary {
  screenType: string;
  displayName: string;
  description: string;
  contentContract: CliScreenContentContract;
  sections: string[];
  minimalFields: string[];
  actionKeys: CliScreenTypeActionKey[];
  variableTypes: CliScreenTypeVariableType[];
  conditionOperators: CliScreenTypeConditionOperator[];
}

export interface CliScreenTypeDetail extends CliScreenTypeSummary {
  simplifiedContentExample: Record<string, unknown>;
  manifestExample: Record<string, unknown>;
}

export interface CliScreenTypeListData {
  screenTypes: CliScreenTypeSummary[];
}

export interface CliFirebaseClientConfig {
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  appId?: string;
}

export interface CliConfigData {
  environment: string;
  appUrl: string;
  apiBaseUrl: string;
  firebase: CliFirebaseClientConfig;
}

export interface CliCapabilityScope {
  scope: CliServiceTokenScope;
  description: string;
}

export interface CliCapabilityCommand {
  command: string;
  /** Every listed scope is required. */
  scopes: CliServiceTokenScope[];
  /** When present, at least one effective scope is required in addition to scopes. */
  anyOfScopes?: CliServiceTokenScope[];
  billingCapability?: string | null;
  description: string;
}

export interface CliBillingSubscriptionSummary {
  id: string;
  projectId: string;
  productId: string;
  productName?: string;
  status: string;
  fundingSource?: string;
  capabilities: string[];
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  trialEndsAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CliBillingSubscriptionListData {
  subscriptions: CliBillingSubscriptionSummary[];
}

export interface CliBillingManualSubscriptionCreateRequest {
  productId: string;
  status?: string;
  fundingSource?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string | null;
  trialEndsAt?: string | null;
}

export interface CliBillingSubscriptionCreateData {
  subscription: CliBillingSubscriptionSummary;
}

export interface CliBillingSubscriptionDeleteData {
  id: string;
}

// ── Internal payments observability (webhook → analytics chain) ───────────────

/** One webhook-derived payment event from projects/{id}/paymentEvents. */
export interface CliPaymentEventSummary {
  type: string;
  status: string;
  timestamp: string;
  stripeEventId: string;
  /** Present on provider-discriminated audit rows; legacy Stripe fields stay compatible. */
  provider?: 'stripe' | 'paddle';
  providerEventId?: string;
  /** Server-derived lifecycle identity; Paddle internal analytics joins use this, not providerEventId. */
  businessEventKey?: string;
  origin?: string;
  canonicalEventName?: string;
  paddleNotificationId?: string;
  paddleEnvironment?: 'sandbox' | 'production';
  mode?: 'test' | 'live';
  transactionId?: string;
  customerId?: string;
  subscriptionId?: string;
  paymentIntentId?: string;
  invoiceId?: string;
  amount?: number;
  currency?: string;
  subscriptionStatus?: string;
}

export interface CliPaymentEventsData {
  projectId: string;
  since: string;
  count: number;
  byType: Record<string, number>;
  events: CliPaymentEventSummary[];
}

/** Internal read-only snapshot. Receipt processing never implies sink delivery. */
export interface CliPaddleDeliveryStatusData {
  projectId: string;
  environment: 'sandbox' | 'production';
  providerEventId: string;
  receipt: { status: 'pending' | 'retrying' | 'failed' | 'exhausted' | 'processed'; attempts: number; nextAttemptAt?: string };
  canonical: { status: 'not_enrolled' | 'pending' | 'not_applicable' | 'normalized'; id?: string; businessEventKey?: string; reason?: string };
  jobs: Array<{
    id: string; sink: 'internal' | 'vendor'; destinationId: string;
    /** Absent for legacy/canonical jobs; raw has its own delivery acknowledgement. */
    representation?: 'raw';
    state: 'pending' | 'leased' | 'failed' | 'delivered' | 'skipped' | 'dead_letter';
    attempts: number; totalAttempts: number; replayCount: number;
    nextAttemptAt?: string; deliveredAt?: string; leaseExpiresAt?: string;
    skipReason?: 'disabled' | 'consent_denied' | 'consent_unknown' | 'not_applicable';
  }>;
}

/** Internal scoped operator replay. The execute result is an operation receipt,
 * not the current delivery status. No payloads, credentials or caller-supplied actor. */
export type CliPaddleDeliveryReplayData = {
  projectId: string; environment: 'sandbox' | 'production'; jobId: string; operationId: string;
  expectedReplayCount: number; generation: number;
} & ({ mode: 'preflight'; ready: boolean; blockers: string[]; alreadyRecorded: boolean }
  | { mode: 'execute'; status: 'requeued' });

export type CliAnalyticsServerEventSource = 'server-stripe' | 'server-paddle';

/** One de-duplicated server-side canonical payment analytics ingress event. */
export interface CliAnalyticsServerEventSummary {
  eventId: string;
  eventName: string;
  eventTimestamp: string;
  platformUserId?: string;
  source?: string;
}

export interface CliAnalyticsServerEventsData {
  projectId: string;
  since: string;
  /** @deprecated one release — use sources and bySource. */
  source: 'server-stripe';
  sources: CliAnalyticsServerEventSource[];
  bySource: Record<string, number>;
  /** Whether the project has the Segmently Analytics integration enabled — when false, zero events is expected, not a dead chain. */
  integrationEnabled: boolean;
  count: number;
  byEvent: Record<string, number>;
  platformUserIds: string[];
  events: CliAnalyticsServerEventSummary[];
}

// ── Internal analytics-strategy S7 accepted-source operator flow ─────────────

export interface CliS7EffectSourceAcceptRequest {
  experimentId: string;
  phase: 'S4' | 'S5';
  resultId?: string;
  releaseId?: string;
  evaluationId?: string;
  acceptanceArtifact: Record<string, unknown>;
  confirmProjectId: string;
  dryRun: boolean;
}

export interface CliS7EffectSourceAcceptData {
  projectId: string;
  experimentId: string;
  phase: 'S4' | 'S5';
  sourceRef: {
    schemaVersion: 1;
    sourceKind: 's4_normalized_decision' | 's5_release_evaluation';
    id: string;
    version: string;
  };
  dryRun: boolean;
  artifactHash: string;
  /** Present only after apply; importedAt/importedBy make it intentionally apply-specific. */
  recordHash?: string;
  artifactCreated: boolean;
  policyCreated: boolean;
  receiptCreated: boolean;
  envelopeCreated: boolean;
  envelopeHash?: string;
}

export interface CliCapabilitiesData {
  apiVersion: CliApiVersion;
  minCliVersion: string;
  environment: string;
  serverTime: string;
  authModes: Array<{
    type: 'browser_login' | 'service_token' | 'short_lived_bearer';
    description: string;
  }>;
  features: {
    serviceTokens: boolean;
    browserLogin: boolean;
    publishWeb: boolean;
    generateBlock: boolean;
    paddleRead: boolean;
    paddleWrite: boolean;
    stripeRead: boolean;
    stripeWrite: boolean;
    themeManagement: boolean;
    analyticsManagement?: boolean;
    domainReadiness?: boolean;
    contentPlanRead?: boolean;
    contentPlanWrite?: boolean;
    transactionalStrategyBake?: boolean;
    transactionalStrategyHeadWrites?: boolean;
    transactionalStrategyRestore?: boolean;
    transactionalInsightVariableImport?: boolean;
    transactionalContentConsumerWrites?: boolean;
  };
  serviceTokenScopes: CliCapabilityScope[];
  commands: CliCapabilityCommand[];
}

export interface CliAuthAuthorizeRequest {
  redirectUri: string;
  codeChallenge: string;
  codeChallengeMethod: 'S256';
}

export interface CliAuthAuthorizeData {
  code: string;
  expiresAt: string;
}

export interface CliAuthTokenRequest {
  code: string;
  codeVerifier: string;
  redirectUri: string;
}

export interface CliAuthTokenData {
  firebaseCustomToken: string;
  expiresInSeconds: number;
  firebase: CliFirebaseClientConfig;
}

export interface CliServiceTokenSummary {
  id: string;
  projectId: string;
  name: string;
  scopes: CliServiceTokenScope[];
  tokenPreview: string;
  createdAt: string;
  createdByUid?: string;
  createdByEmail?: string;
  expiresAt?: string | null;
  revokedAt?: string | null;
  revokedByUid?: string;
  lastUsedAt?: string | null;
}

export interface CliServiceTokenCreateRequest {
  name: string;
  scopes: CliServiceTokenScope[];
  expiresAt?: string | null;
}

export interface CliServiceTokenCreateData {
  token: CliServiceTokenSummary;
  tokenValue: string;
  expiresInSeconds?: number | null;
  expiresInDays?: number | null;
  expirationPolicy?: 'service_admin_unrestricted' | 'non_admin_30_day_max';
  expirationMessage?: string;
}

export interface CliServiceTokenListData {
  tokens: CliServiceTokenSummary[];
}

export interface CliServiceTokenRevokeData {
  token: CliServiceTokenSummary;
}

/**
 * Project-scoped ingest API key (J7) for POST /analytics/v1/product-usage.
 * Key format: `sgly_ingest.<env>.<base64url(projectId)>.<keyId>.<secret>` — stored hashed
 * at `projects/{pid}/ingestKeys/{keyId}`; the full key value is returned once at creation.
 */
export interface CliIngestKeySummary {
  id: string;
  projectId: string;
  label?: string | null;
  keyPreview: string;
  createdAt: string;
  createdByUid?: string;
  createdByEmail?: string;
  expiresAt?: string | null;
  revokedAt?: string | null;
  revokedByUid?: string;
  lastUsedAt?: string | null;
}

export interface CliIngestKeyCreateRequest {
  label?: string | null;
  expiresAt?: string | null;
}

export interface CliIngestKeyCreateData {
  key: CliIngestKeySummary;
  /** Full ingest key value — returned exactly once at creation, never persisted. */
  keyValue: string;
}

export interface CliIngestKeyListData {
  keys: CliIngestKeySummary[];
}

export interface CliIngestKeyRevokeData {
  key: CliIngestKeySummary;
}

export interface CliStripeAccountData {
  account: Record<string, unknown>;
}

export interface CliStripeProductsData {
  products: Array<Record<string, unknown>>;
  mode: 'test' | 'live';
}

export interface CliStripePricesData {
  prices: Array<Record<string, unknown>>;
  mode: 'test' | 'live';
}

export type CliStripePaywallProductType = 'one_time' | 'subscription';
export type CliStripePaywallCheckoutMode = 'embedded' | 'redirect';
export type CliStripePaywallInterval = 'day' | 'week' | 'month' | 'year';
export type CliStripeMode = 'test' | 'live';

export interface CliStripePaywallProductEnsureRequest {
  version?: 'cli.paywall-product.v1' | string;
  /** Default 'test'. 'live' additionally requires `confirmLive: true` (P7, AC-107). */
  mode?: CliStripeMode;
  /** Live-only acknowledgement — the CLI sets it from `--yes`, never by default. */
  confirmLive?: boolean;
  product: {
    name: string;
    description?: string;
    type: CliStripePaywallProductType;
    checkoutMode?: CliStripePaywallCheckoutMode;
    unitAmount: number;
    currency: string;
    interval?: CliStripePaywallInterval;
    intervalCount?: number;
    trialDays?: number;
  };
}

export interface CliStripePaywallProductSummary {
  id: string;
  name: string;
  description?: string | null;
  paymentProvider: 'stripe';
  stripeProductId: string;
  stripePriceId: string;
  mode: CliStripeMode;
  type: CliStripePaywallProductType;
  checkoutMode: CliStripePaywallCheckoutMode;
  subscription?: {
    interval: CliStripePaywallInterval;
    intervalCount: number;
    trialDays?: number;
  };
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CliStripePaywallProductEnsureData {
  created: boolean;
  mode: CliStripeMode;
  paywallProduct: CliStripePaywallProductSummary;
  stripeProduct: Record<string, unknown>;
  stripePrice: Record<string, unknown>;
}

/** CLI-side aggregate for `stripe paywall-product ensure` (one POST per manifest row, AC-105.6). */
export interface CliStripePaywallProductEnsureManyData {
  mode: CliStripeMode;
  results: Array<{
    index: number;
    name: string;
    status: 'created' | 'alreadyExisted' | 'failed';
    paywallProduct?: CliStripePaywallProductSummary;
    stripeProduct?: Record<string, unknown>;
    stripePrice?: Record<string, unknown>;
    error?: { code: string; message: string; status?: number };
  }>;
}

export interface CliStripePaywallProductPromoteRequest {
  confirmLive: true;
  dryRun?: boolean;
}

export interface CliStripePaywallProductPromoteData {
  created: boolean;
  alreadyExisted: boolean;
  source: CliStripePaywallProductSummary;
  /** The live twin: existing (alreadyExisted), created now, or the would-be summary on dryRun. */
  paywallProduct: CliStripePaywallProductSummary;
  stripeProduct?: Record<string, unknown>;
  stripePrice?: Record<string, unknown>;
}

export interface CliPublishWebRequest {
  alias?: string;
  funnelId?: string;
  versionId?: string;
  webPlacementId?: string;
  config?: Record<string, unknown>;
  webPlacementConfig?: Record<string, unknown>;
  projectAnalytics?: Record<string, unknown>;
}

export interface CliPublishWebData {
  success: boolean;
  alias: string;
  funnelId: string;
  versionId: string;
  webPlacementId?: string;
  webUrl?: string;
  gcsFiles?: string[];
  cdnFiles?: string[];
  webShellVersion?: string;
  renderTimeMs?: number;
  message?: string;
}

export interface CliPublishVerifyRequest {
  url: string;
  publicBaseUrl?: string;
  queryParams?: Record<string, string>;
  requiredVariantIds?: string[];
  expectedText?: string[];
  sampleCount?: number;
  retryCount?: number;
  retryDelayMs?: number;
}

export interface CliPublishVerifyData {
  success: boolean;
  publicUrl: string;
  sampleCount: number;
  statusCodes: number[];
  successfulResponses: number;
  observedVariantIds: string[];
  missingVariantIds: string[];
  matchedText: string[];
  missingText: string[];
}

export type CliAnalyticsProbeStatus = 'ready' | 'warning' | 'blocked';
export type CliAnalyticsProbeCheckStatus = 'passed' | 'warning' | 'failed';
export type CliAnalyticsProbeCheckSeverity = 'info' | 'warning' | 'blocker';

export interface CliAnalyticsIntegrationSummary {
  id: string;
  platform: string;
  name: string;
  enabled: boolean;
  side: 'client' | 'server' | 'both' | 'unknown';
  config: Record<string, unknown>;
  configKeys: string[];
  missingConfigFields: string[];
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CliAnalyticsSettingsSummary {
  total: number;
  enabled: number;
  clientSideEnabled: number;
  serverSideEnabled: number;
  integrationsWithMissingConfig: Array<{
    integrationId: string;
    platform: string;
    fields: string[];
  }>;
}

/** T-1 / AC-064: secret fields whose stored value survived a `[redacted]` manifest value. */
export interface CliAnalyticsPreservedSecretField {
  integrationId: string;
  platform: string;
  fields: string[];
}

export interface CliAnalyticsSettingsData {
  projectId: string;
  exists: boolean;
  settings: {
    consentManagement: Record<string, unknown>;
    integrations: CliAnalyticsIntegrationSummary[];
  };
  summary: CliAnalyticsSettingsSummary;
  preservedSecretFields?: CliAnalyticsPreservedSecretField[];
}

export interface CliAnalyticsSettingsApplyRequest {
  settings: {
    consentManagement?: Record<string, unknown>;
    integrations?: Array<Record<string, unknown>>;
  };
  merge?: boolean;
}

export interface CliAnalyticsProbeRequest {
  requiredPlatforms?: string[];
  requiredUrlParams?: string[];
  requireClientSide?: boolean;
  requireServerSide?: boolean;
  requireEnabledIntegrations?: boolean;
  requireConsentManagement?: boolean;
}

export interface CliAnalyticsProbeCheck {
  id: string;
  label: string;
  status: CliAnalyticsProbeCheckStatus;
  severity: CliAnalyticsProbeCheckSeverity;
  message: string;
  evidence?: Record<string, unknown>;
  nextAction?: string;
}

export interface CliAnalyticsProbeData {
  checkedAt: string;
  projectId: string;
  status: CliAnalyticsProbeStatus;
  summary: {
    passed: number;
    warnings: number;
    blockers: number;
    total: number;
  };
  settings: CliAnalyticsSettingsData;
  checks: CliAnalyticsProbeCheck[];
}

export type CliDomainCheckStatus = 'passed' | 'warning' | 'failed';
export type CliDomainCheckSeverity = 'info' | 'warning' | 'blocker';

/** Serving surface a custom domain is bound to (v2.20.0): funnel root vs article root. */
export type CliDomainSurface = 'web' | 'article';

export interface CliDomainDnsRecord {
  host?: string;
  name?: string;
  type: string;
  value?: string;
  target?: string;
}

export interface CliDomainCheck {
  id: string;
  label: string;
  status: CliDomainCheckStatus;
  severity: CliDomainCheckSeverity;
  message: string;
  evidence?: Record<string, unknown>;
  nextAction?: string;
}

export interface CliDomainStatusData {
  checkedAt: string;
  projectId: string;
  surface?: CliDomainSurface;
  hasDomain: boolean;
  ready: boolean;
  domainId?: string;
  domain?: string;
  status?: string;
  dnsRecords?: {
    aRecord?: CliDomainDnsRecord | null;
    cnameRecord?: CliDomainDnsRecord | null;
  } | null;
  dnsRecord?: CliDomainDnsRecord | null;
  certificate?: Record<string, unknown> | null;
  lastError?: Record<string, unknown> | null;
  createdAt?: string | null;
  activatedAt?: string | null;
  /**
   * Article surface only (absent on web). Path mode of the article site; `publicBaseUrl` is the article
   * public base URL — NOT the publish-probe publicBaseUrl.
   */
  mode?: 'subdomain' | 'path';
  publicBaseUrl?: string | null;
  homeAlias?: string | null;
  /** Article surface only: the project's path-mode switch job (running or last finished), null when none. */
  modeSwitchJob?: CliArticleModeSwitchJob | null;
  checks: CliDomainCheck[];
}

/** `domains status --surface all`: one entry per serving surface. */
export interface CliDomainStatusAllData {
  checkedAt: string;
  projectId: string;
  domains: CliDomainStatusData[];
}

export interface CliDomainVerifyRequest {
  requireActive?: boolean;
  surface?: CliDomainSurface;
}

export interface CliDomainVerifyData extends CliDomainStatusData {
  requireActive: boolean;
}

export interface CliDomainSetupRequest {
  domain: string;
  surface?: CliDomainSurface;
}

export interface CliDomainSetupData {
  domainId: string;
  domain: string;
  surface: CliDomainSurface;
  status: string;
  dnsRecords: {
    aRecord: CliDomainDnsRecord;
    cnameRecord: CliDomainDnsRecord;
  };
  dnsRecord: CliDomainDnsRecord;
  message: string;
}

export interface CliDomainVerifyDnsRequest {
  surface?: CliDomainSurface;
}

export interface CliDomainVerifyDnsData {
  status: string;
  message: string;
  certificate?: Record<string, unknown> | null;
}

export interface CliDomainRemoveData {
  success: boolean;
  message: string;
}

/** `domains home set` body: the live article alias served at "/" of the article domain (Articles path mode). */
export interface CliDomainHomeSetRequest {
  alias: string;
}

/** `domains base-url set|verify` body: the article public base URL (path mode) — NOT the publish-probe publicBaseUrl. */
export interface CliDomainBaseUrlRequest {
  publicBaseUrl: string;
}

/** One article of a path-mode switch report (mirrors builder ArticleModeSwitchResult). */
export interface CliArticleModeSwitchResult {
  articleId: string;
  alias: string;
  status: 'republished' | 'skipped' | 'failed';
  /** skipped: a live article with unpublished edits is never republished by the switch; or it was retired meanwhile. */
  reason?: 'pending edits' | 'retired';
  /** skipped: "canonical stays {url} until republished". */
  note?: string;
  /** failed: the server error text. */
  error?: string;
  /** republished: path-mode publish warnings (e.g. root-relative links). */
  warnings?: string[];
}

export type CliArticleModeSwitchRelatedRefresh =
  | { ok: true; updated: number; unchanged: number; skipped: number }
  | { ok: false; error: string };

/**
 * Path-mode switch job (mirrors builder ArticleModeSwitchJob): one per project, advanced by bounded `step` calls.
 * Live while `status: 'running'` and `heartbeatAt` is within the 2-minute lease; any client may step it.
 */
export interface CliArticleModeSwitchJob {
  jobId: string;
  kind: 'set' | 'clear';
  publicBaseUrl: string | null;
  status: 'running' | 'completed' | 'completed_with_failures' | 'failed';
  total: number;
  pending: string[];
  inFlight: { articleIds: string[]; claimedAt: string } | null;
  results: CliArticleModeSwitchResult[];
  relatedRefresh: CliArticleModeSwitchRelatedRefresh | null;
  warnings: string[];
  startedAt: string;
  heartbeatAt: string;
  finishedAt: string | null;
  finishingAt: string | null;
  startedBy: string | null;
}

/** Article-domain path-mode state returned by `domains home|base-url …` (mirrors builder ArticlePathModeState). */
export interface CliArticlePathModeData {
  projectId: string;
  hasArticleDomain: boolean;
  articleHost: string | null;
  domainStatus: string | null;
  mode: 'subdomain' | 'path';
  publicBaseUrl: string | null;
  homeAlias: string | null;
  job: CliArticleModeSwitchJob | null;
  /** clear only: "already in subdomain mode" when nothing had to change. */
  note?: string;
}

export type CliContentPlanAuditStatus = 'passed' | 'warning' | 'failed';
export type CliContentPlanAuditIssueSeverity = 'info' | 'warning' | 'error';

export interface CliContentPlanStrategySummary {
  id: string;
  projectId: string;
  name: string;
  status?: string | null;
  approvalStage?: string | null;
  authorId?: string | null;
  dateStart?: string | null;
  dateEnd?: string | null;
  targetPlatforms: string[];
  primaryGoal?: string | null;
  contentMixPreset?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  sprintCount?: number;
  publicationCount?: number;
}

export interface CliContentPlanSprintSummary {
  id: string;
  strategyId: string;
  name?: string | null;
  goal?: string | null;
  dateStart?: string | null;
  dateEnd?: string | null;
  assignedTopicIds: string[];
  audienceViolationCount: number;
}

export interface CliContentPlanPublicationSummary {
  id: string;
  strategyId: string;
  sprintId?: string | null;
  topicId?: string | null;
  aspectKey?: string | null;
  contentPostId?: string | null;
  platform?: string | null;
  scheduledAt?: string | null;
  status?: string | null;
  source?: string | null;
  weekIndex?: number | null;
  weekdayIndex?: number | null;
}

export interface CliContentPlanPostScheduleRequest {
  authorId?: string | null;
  strategyId: string;
  sprintId?: string | null;
  publicationId?: string | null;
  scheduledAt: string;
  platform?: string | null;
  topicId?: string | null;
  aspectKey?: string | null;
  status?: string | null;
  source?: string | null;
  dryRun?: boolean;
}

export interface CliContentPlanPostScheduleData {
  projectId: string;
  postId: string;
  dryRun: boolean;
  publication: CliContentPlanPublicationSummary;
  calendarUrl: string;
}

export interface CliContentPlanPostScheduleBatchRequest {
  dryRun?: boolean;
  items: Array<{
    postId: string;
    authorId?: string | null;
    strategyId: string;
    sprintId?: string | null;
    publicationId?: string | null;
    scheduledAt: string;
    platform?: string | null;
    topicId?: string | null;
    aspectKey?: string | null;
    status?: string | null;
    source?: string | null;
  }>;
}

export interface CliContentPlanPostScheduleBatchData {
  appliedAt: string;
  projectId: string;
  dryRun: boolean;
  scheduledCount: number;
  results: CliContentPlanPostScheduleData[];
}

export interface CliContentPlanPublicationListData {
  projectId: string;
  strategyId: string;
  publications: CliContentPlanPublicationSummary[];
}

export interface CliContentPlanPublicationData {
  projectId: string;
  strategyId: string;
  publication: CliContentPlanPublicationSummary & {
    raw?: Record<string, unknown>;
  };
}

export interface CliContentPlanPublicationApplyRequest {
  publicationId: string;
  authorId?: string | null;
  sprintId?: string | null;
  topicId?: string | null;
  aspectKey?: string | null;
  contentPostId?: string | null;
  platform: string;
  scheduledAt: string;
  weekIndex?: number | null;
  weekdayIndex?: number | null;
  source?: string | null;
  status?: string | null;
  dryRun?: boolean;
  fields?: Record<string, unknown>;
}

export interface CliContentPlanPublicationPatchRequest {
  dryRun?: boolean;
  patch: Record<string, unknown>;
}

export interface CliContentPlanPublicationMutationData extends CliContentPlanPublicationData {
  appliedAt: string;
  dryRun: boolean;
  changedFields: string[];
  action: 'created' | 'updated' | 'would_create' | 'would_update' | 'deleted' | 'would_delete';
}

export interface CliContentPlanTopicSummary {
  id: string;
  title: string;
  authorId?: string | null;
  angle?: string | null;
  keyMessage?: string | null;
  summary?: string | null;
  status?: string | null;
  targetPlatforms: string[];
  targetAudienceIds: string[];
  contentPostIds: string[];
  researchRunIds?: string[];
  themeIds: string[];
  plannedPostCount?: number | null;
  aspectCount?: number;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CliContentPlanPostSummary {
  id: string;
  authorId?: string | null;
  topicId?: string | null;
  strategyId?: string | null;
  sprintId?: string | null;
  kind?: string | null;
  lifecycleStage?: string | null;
  platforms: string[];
  hook?: string | null;
  hasBody: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export type CliContentPlanPostOwnerKind =
  | 'content_post_draft'
  | 'sprint_post'
  | 'content_post'
  | 'post_example';

export type CliContentPlanPostStorage =
  | 'post_generation_draft'
  | 'sprint_post'
  | 'content_posts'
  | 'post_examples';

export interface CliContentPlanPostLocation {
  storage: CliContentPlanPostStorage;
  ownerKind: CliContentPlanPostOwnerKind;
  ownerId: string;
  firestorePath: string;
  generationId?: string | null;
  strategyId?: string | null;
  sprintId?: string | null;
}

export interface CliContentPlanPostAssetExperimentSummary {
  experimentId: string;
  status?: string | null;
  generatedUrl?: string | null;
  generationModel?: string | null;
  generatedAt?: string | null;
  error?: string | null;
}

export interface CliContentPlanPostAssetSlotSummary {
  slotKey: string;
  role?: string | null;
  promptTemplate?: string | null;
  selectedExperimentId?: string | null;
  selectedGeneratedUrl?: string | null;
  status?: string | null;
  experimentCount: number;
  experiments: CliContentPlanPostAssetExperimentSummary[];
}

export interface CliContentPlanPostAssetSlotMutationRequest {
  authorId?: string | null;
  dryRun?: boolean;
  patch?: Record<string, unknown>;
  selectedExperimentId?: string | null;
}

export interface CliContentPlanPostAssetSlotMutationData {
  appliedAt: string;
  projectId: string;
  postId: string;
  slotKey: string;
  dryRun: boolean;
  changedFields: string[];
  location: CliContentPlanPostLocation;
  slot: CliContentPlanPostAssetSlotSummary & {
    raw?: Record<string, unknown>;
  };
}

export type CliContentPlanPostAssetReferenceRole =
  | 'object'
  | 'character'
  | 'style'
  | 'layout'
  | 'typography'
  | 'logo'
  | 'approved'
  | 'anchor'
  | 'motif';

export interface CliContentPlanPostAssetReferenceImage {
  url: string;
  mimeType?: string | null;
  name?: string | null;
  role?: CliContentPlanPostAssetReferenceRole | null;
  instruction?: string | null;
}

export interface CliContentPlanPostAssetSlotGenerateOverrides {
  prompt?: string | null;
  negativePrompt?: string | null;
  provider?: string | null;
  model?: string | null;
  quality?: string | null;
  openaiSize?: string | null;
  aspectRatio?: string | null;
  imageSize?: string | null;
  style?: string | null;
  referenceImages?: CliContentPlanPostAssetReferenceImage[];
}

export interface CliContentPlanPostAssetSlotGenerateSource {
  mode?: 'inherit_slot_package' | 'manual_overrides';
  experimentId?: string | null;
}

export interface CliContentPlanPostAssetSlotGenerateRequest {
  authorId?: string | null;
  userId?: string | null;
  experimentId?: string | null;
  inheritFromSlotPackage?: boolean;
  source?: CliContentPlanPostAssetSlotGenerateSource | null;
  overrides?: CliContentPlanPostAssetSlotGenerateOverrides;
  allowFailedPackage?: boolean | null;
}

export interface CliContentPlanPostAssetSlotGenerationPackage {
  prompt: string;
  provider: string;
  model: string;
  aspectRatio: string;
  negativePrompt?: string | null;
  quality?: string | null;
  openaiSize?: string | null;
  imageSize?: string | null;
  style?: string | null;
  referenceImages: CliContentPlanPostAssetReferenceImage[];
  sourceExperimentId?: string | null;
  inheritedFromSlotPackage: boolean;
  overriddenFields: string[];
}

export type CliContentPlanPostAssetExperimentMutationAction = 'delete' | 'mark_rejected';

export interface CliContentPlanPostAssetExperimentMutationRequest {
  authorId?: string | null;
  dryRun?: boolean;
  action: CliContentPlanPostAssetExperimentMutationAction;
  reason?: string | null;
}

export interface CliContentPlanPostAssetExperimentMutationData extends CliContentPlanPostAssetSlotMutationData {
  experimentId: string;
  action: CliContentPlanPostAssetExperimentMutationAction;
}

export interface CliContentPlanPostAssetBriefData {
  projectId: string;
  postId: string;
  location: CliContentPlanPostLocation;
  designAssetBrief?: Record<string, unknown> | null;
  storyboard?: unknown[] | null;
  slots: CliContentPlanPostAssetSlotSummary[];
  slotCount: number;
}

export interface CliContentPlanPostAssetPreviewData {
  projectId: string;
  postId: string;
  location: CliContentPlanPostLocation;
  slots: CliContentPlanPostAssetSlotSummary[];
  selectedUrls: string[];
}

export interface CliContentPlanPostAssetSlotPromptRegenerateRequest {
  authorId?: string | null;
  dryRun?: boolean;
  userInstruction?: string | null;
  model?: string | null;
}

export interface CliContentPlanPostAssetSlotPromptRegenerateData extends CliContentPlanPostAssetSlotMutationData {
  model?: string | null;
  provider?: string | null;
  rationale?: string | null;
  usage?: Record<string, unknown> | null;
}

export type CliContentPlanTopicResourceType =
  | 'stat'
  | 'quote'
  | 'case_study'
  | 'example'
  | 'link'
  | 'image'
  | 'note';

export type CliContentPlanTopicResourceReliability = 'verified' | 'unverified' | 'internal';

export interface CliContentPlanTopicResourceSummary {
  id: string;
  topicId: string;
  type: CliContentPlanTopicResourceType;
  content: string;
  sourceUrl?: string | null;
  reliability?: CliContentPlanTopicResourceReliability | null;
  title?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  raw?: Record<string, unknown>;
}

export interface CliContentPlanTopicResourceListData {
  projectId: string;
  topicId: string;
  resources: CliContentPlanTopicResourceSummary[];
  resourceCount: number;
}

export interface CliContentPlanTopicResourceData {
  projectId: string;
  topicId: string;
  resource: CliContentPlanTopicResourceSummary;
}

export interface CliContentPlanTopicResourceCreateRequest {
  authorId?: string | null;
  dryRun?: boolean;
  resourceId?: string | null;
  type?: CliContentPlanTopicResourceType | null;
  content?: string | null;
  sourceUrl?: string | null;
  reliability?: CliContentPlanTopicResourceReliability | null;
  title?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface CliContentPlanTopicResourcePatchRequest {
  authorId?: string | null;
  dryRun?: boolean;
  patch: Record<string, unknown>;
}

export interface CliContentPlanTopicResourceMutationData extends CliContentPlanTopicResourceData {
  appliedAt: string;
  dryRun: boolean;
  changedFields: string[];
  deleted?: boolean;
}

export interface CliContentPlanTopicGenerateRequest {
  authorId?: string | null;
  brief?: Record<string, unknown>;
  sourceData?: Record<string, unknown>;
  sourceDataIds?: Record<string, unknown>;
  models?: Record<string, unknown>;
  searchProvider?: string | null;
  researchDepth?: string | null;
  reasoningEffort?: string | null;
  provider?: string | null;
  selectedPillarIds?: string[];
  includeAuthor?: boolean;
  pillarInfluence?: Record<string, unknown>;
}

export interface CliContentPlanTopicGenerateData {
  success: boolean;
  taskId?: string;
  briefId?: string;
  error?: string;
}

export interface CliContentPlanTopicDraftSummary extends CliContentPlanTopicSummary {
  generationId: string;
  firestorePath: string;
  accepted?: boolean | null;
  rejectedByDedup?: boolean | null;
  raw?: Record<string, unknown>;
}

export interface CliContentPlanTopicDraftListData {
  projectId: string;
  generationId?: string | null;
  authorId?: string | null;
  topics: CliContentPlanTopicDraftSummary[];
}

export interface CliContentPlanTopicAspect {
  aspectKey?: string;
  key?: string;
  label?: string;
  angle?: string;
  goal?: string;
  [key: string]: unknown;
}

export interface CliContentPlanStrategyListData {
  strategies: CliContentPlanStrategySummary[];
}

export interface CliContentPlanStrategyApplyRequest {
  strategyId: string;
  authorId?: string | null;
  name?: string | null;
  status?: string | null;
  approvalStage?: string | null;
  targetPlatforms?: string[];
  dateStart?: string | null;
  dateEnd?: string | null;
  primaryGoal?: string | null;
  contentMixPreset?: string | null;
  postsPerWeekPerPlatform?: Record<string, number>;
  platformCadences?: Array<Record<string, unknown>>;
  dryRun?: boolean;
  fields?: Record<string, unknown>;
  sprints?: Array<Record<string, unknown> & { id: string }>;
}

export interface CliContentPlanStrategyApplyData {
  appliedAt: string;
  projectId: string;
  strategyId: string;
  dryRun: boolean;
  changedFields: string[];
  sprintIds: string[];
  action: 'created' | 'updated' | 'would_create' | 'would_update';
  strategy: CliContentPlanStrategySummary & {
    raw?: Record<string, unknown>;
  };
  uiUrl: string;
}

export interface CliContentPlanStrategyExportData {
  exportedAt: string;
  projectId: string;
  strategy: CliContentPlanStrategySummary & {
    raw?: Record<string, unknown>;
  };
  sprints: CliContentPlanSprintSummary[];
  publications: CliContentPlanPublicationSummary[];
  topics: CliContentPlanTopicSummary[];
  posts?: CliContentPlanPostSummary[];
}

export interface CliContentPlanAuditRequest {
  requiredPlatforms?: string[];
  requireSprints?: boolean;
  requirePublications?: boolean;
  requireGeneratedPosts?: boolean;
  requireNoAudienceViolations?: boolean;
}

export interface CliContentPlanAuditIssue {
  id: string;
  severity: CliContentPlanAuditIssueSeverity;
  message: string;
  path?: string;
  evidence?: Record<string, unknown>;
}

export interface CliContentPlanStrategyAuditData {
  auditedAt: string;
  projectId: string;
  strategyId: string;
  status: CliContentPlanAuditStatus;
  summary: {
    sprintCount: number;
    publicationCount: number;
    topicCount: number;
    postCount: number;
    missingTopicRefCount: number;
    missingPostRefCount: number;
    audienceViolationCount: number;
    issueCount: number;
    errors: number;
    warnings: number;
  };
  requiredPlatforms: string[];
  observedPlatforms: string[];
  missingPlatforms: string[];
  missingTopicRefs: string[];
  missingPostRefs: string[];
  issues: CliContentPlanAuditIssue[];
}

export interface CliContentPlanTopicListData {
  projectId: string;
  authorId?: string | null;
  topics: CliContentPlanTopicSummary[];
}

export interface CliContentPlanTopicDetailData {
  projectId: string;
  topic: CliContentPlanTopicSummary & {
    postBreakdown: CliContentPlanTopicAspect[];
    raw?: Record<string, unknown>;
  };
}

export interface CliContentPlanTopicPatchRequest {
  authorId?: string | null;
  dryRun?: boolean;
  patch: Record<string, unknown>;
}

export interface CliContentPlanTopicMutationData {
  appliedAt: string;
  projectId: string;
  topicId: string;
  dryRun: boolean;
  changedFields: string[];
  topic: CliContentPlanTopicSummary & {
    postBreakdown?: CliContentPlanTopicAspect[];
    raw?: Record<string, unknown>;
  };
}

export interface CliContentPlanTopicPlatformsRequest {
  authorId?: string | null;
  dryRun?: boolean;
  mode: 'set' | 'add' | 'remove';
  platforms: string[];
}

export interface CliContentPlanTopicAspectsData {
  projectId: string;
  topicId: string;
  aspects: CliContentPlanTopicAspect[];
  aspectCount: number;
}

export interface CliContentPlanTopicAspectsMutationRequest {
  authorId?: string | null;
  dryRun?: boolean;
  mode: 'set' | 'add' | 'patch' | 'remove';
  aspectKey?: string;
  aspect?: CliContentPlanTopicAspect;
  patch?: Record<string, unknown>;
  postBreakdown?: CliContentPlanTopicAspect[];
}

export interface CliContentPlanTopicAspectsMutationData extends CliContentPlanTopicAspectsData {
  appliedAt: string;
  dryRun: boolean;
  changedFields: string[];
}

export type CliContentPlanResearchPhase = 'brief' | 'research' | 'analysis';

export interface CliContentPlanResearchRunSummary {
  id: string;
  projectId?: string | null;
  topicId?: string | null;
  authorId?: string | null;
  title?: string | null;
  status?: string | null;
  isActiveForPosts?: boolean;
  phases?: Record<string, unknown>;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CliContentPlanResearchRunListData {
  projectId: string;
  topicId?: string | null;
  authorId?: string | null;
  runs: CliContentPlanResearchRunSummary[];
}

export interface CliContentPlanResearchRunDetailData {
  projectId: string;
  run: CliContentPlanResearchRunSummary & {
    raw?: Record<string, unknown>;
  };
}

export interface CliContentPlanResearchPhaseData {
  projectId: string;
  runId: string;
  phase: CliContentPlanResearchPhase;
  value: unknown;
}

export interface CliContentPlanResearchPhasePatchRequest {
  authorId?: string | null;
  dryRun?: boolean;
  mode?: 'merge' | 'replace';
  patch: Record<string, unknown>;
}

export interface CliContentPlanResearchPhaseMutationData extends CliContentPlanResearchPhaseData {
  appliedAt: string;
  dryRun: boolean;
  changedFields: string[];
}

export interface CliContentPlanPostListData {
  projectId: string;
  authorId?: string | null;
  posts: CliContentPlanPostSummary[];
}

export interface CliContentPlanPostCreateRequest {
  postId: string;
  authorId?: string | null;
  topicId?: string | null;
  strategyId?: string | null;
  sprintId?: string | null;
  platform?: string | null;
  platforms?: string[];
  format?: string | null;
  kind?: string | null;
  sourcePostId?: string | null;
  generationMode?: string | null;
  lifecycleStage?: string | null;
  source?: string | null;
  goal?: string | null;
  hook?: string | null;
  body?: string | null;
  cta?: string | null;
  sourceUrl?: string | null;
  status?: string | null;
  dryRun?: boolean;
  fields?: Record<string, unknown>;
}

export interface CliContentPlanPostCreateData {
  appliedAt: string;
  projectId: string;
  postId: string;
  dryRun: boolean;
  changedFields: string[];
  action: 'created' | 'updated' | 'would_create' | 'would_update';
  post: CliContentPlanPostSummary & {
    raw?: Record<string, unknown>;
  };
}

export interface CliContentPlanPostDetailData {
  projectId: string;
  post: CliContentPlanPostSummary & {
    location?: CliContentPlanPostLocation;
    assetSlots?: CliContentPlanPostAssetSlotSummary[];
    raw?: Record<string, unknown>;
  };
}

export interface CliContentPlanPostResolveData {
  projectId: string;
  post: CliContentPlanPostSummary & {
    location: CliContentPlanPostLocation;
    assetSlots: CliContentPlanPostAssetSlotSummary[];
    raw?: Record<string, unknown>;
  };
}

export interface CliContentPlanPostDraftListData {
  projectId: string;
  generationId?: string | null;
  topicId?: string | null;
  authorId?: string | null;
  posts: Array<CliContentPlanPostSummary & {
    location: CliContentPlanPostLocation;
    assetSlots?: CliContentPlanPostAssetSlotSummary[];
  }>;
}

export interface CliContentPlanPostAssetSlotListData {
  projectId: string;
  postId: string;
  location: CliContentPlanPostLocation;
  slots: CliContentPlanPostAssetSlotSummary[];
  slotCount: number;
}

export interface CliContentPlanPostAssetSlotData {
  projectId: string;
  postId: string;
  location: CliContentPlanPostLocation;
  slot: CliContentPlanPostAssetSlotSummary & {
    raw?: Record<string, unknown>;
  };
}

export type CliContentPlanAuthorBasePath = 'authors' | 'external_authors';
export type CliContentPlanAuthorKind = 'self' | 'external';

export interface CliContentPlanAuthorPlatformSummary {
  id: string;
  platformId: string;
  displayName?: string | null;
  isActive: boolean;
  uiUrl: string;
}

export interface CliContentPlanAuthorSummary {
  id: string;
  projectId: string;
  basePath: CliContentPlanAuthorBasePath;
  kind: CliContentPlanAuthorKind;
  displayName?: string | null;
  name?: string | null;
  isDefault: boolean;
  isComplete: boolean;
  enabledPlatformIds: string[];
  platformCount: number;
  platforms?: CliContentPlanAuthorPlatformSummary[];
  uiUrl: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  raw?: Record<string, unknown>;
}

export interface CliContentPlanAuthorListData {
  projectId: string;
  basePath: CliContentPlanAuthorBasePath;
  defaultAuthorId?: string | null;
  authors: CliContentPlanAuthorSummary[];
}

export type CliContentPlanDefaultAuthorResolution =
  | 'explicit_default'
  | 'sole_author_fallback'
  | 'multiple_defaults'
  | 'none';

export interface CliContentPlanDefaultAuthorData {
  projectId: string;
  basePath: CliContentPlanAuthorBasePath;
  found: boolean;
  resolution: CliContentPlanDefaultAuthorResolution;
  author?: CliContentPlanAuthorSummary | null;
  candidateCount: number;
  defaultCandidateCount: number;
}

export interface CliContentPlanAuthorApplyRequest {
  authorId: string;
  basePath?: CliContentPlanAuthorBasePath;
  displayName?: string | null;
  name?: string | null;
  defaultLocale?: string | null;
  positioning?: string | null;
  expertise?: string | string[] | null;
  originStory?: string | null;
  values?: string[];
  uniqueValueProposition?: string | null;
  targetAudienceIds?: string[];
  isDefault?: boolean;
  isComplete?: boolean;
  dryRun?: boolean;
  fields?: Record<string, unknown>;
}

export interface CliContentPlanAuthorMutationData {
  appliedAt: string;
  projectId: string;
  authorId: string;
  basePath: CliContentPlanAuthorBasePath;
  dryRun: boolean;
  changedFields: string[];
  action: 'created' | 'updated' | 'would_create' | 'would_update';
  author: CliContentPlanAuthorSummary;
}

export interface CliContentPlanPlatformSummary {
  id: string;
  projectId: string;
  authorId: string;
  basePath: CliContentPlanAuthorBasePath;
  platformId: string;
  displayName?: string | null;
  isActive: boolean;
  hasAlgorithm?: boolean | null;
  maxPostLength?: number | null;
  uiUrl: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  raw?: Record<string, unknown>;
}

export interface CliContentPlanPlatformListData {
  projectId: string;
  authorId: string;
  basePath: CliContentPlanAuthorBasePath;
  platforms: CliContentPlanPlatformSummary[];
}

export interface CliContentPlanPlatformData {
  projectId: string;
  authorId: string;
  basePath: CliContentPlanAuthorBasePath;
  platform: CliContentPlanPlatformSummary;
}

export interface CliContentPlanPlatformApplyRequest {
  platformId: string;
  basePath?: CliContentPlanAuthorBasePath;
  displayName?: string | null;
  isActive?: boolean;
  hasAlgorithm?: boolean;
  maxPostLength?: number | null;
  optimalPostFrequency?: Record<string, unknown> | null;
  optimalSlots?: Array<Record<string, unknown>>;
  dryRun?: boolean;
  fields?: Record<string, unknown>;
}

export interface CliContentPlanPlatformMutationData extends CliContentPlanPlatformData {
  appliedAt: string;
  dryRun: boolean;
  changedFields: string[];
  action: 'created' | 'updated' | 'would_create' | 'would_update';
}

// ---------------------------------------------------------------------------
// Content Plan flow-readiness contract (D12, Content Plan Flow Polish program).
// Defined ONCE here: the backend computes it for `content-plan doctor` via the
// CLI facade; the M2 wizard's frontend hook mirrors the SAME predicates over
// live Firestore subscriptions. A parity contract test locks both sides.
// ---------------------------------------------------------------------------

/** The canonical 8-step readiness chain (po.md "Full sequential scenario"). */
export type ContentPlanFlowStep =
  | 'author'
  | 'platforms'
  | 'pillars'
  | 'backlog'
  | 'strategy'
  | 'publications'
  | 'posts'
  | 'assets';

export const CONTENT_PLAN_FLOW_STEPS: readonly ContentPlanFlowStep[] = [
  'author',
  'platforms',
  'pillars',
  'backlog',
  'strategy',
  'publications',
  'posts',
  'assets',
];

/** Backlog is "ready" at ≥ this many ready topics (po.md gate). */
export const CONTENT_PLAN_BACKLOG_READY_THRESHOLD = 10;

/**
 * Step predicates (both sides implement EXACTLY these):
 * - author: a default (or sole) author document exists.
 * - platforms: the resolved author has ≥1 platform.
 * - pillars: ≥1 confirmed (non-draft) pillar for the author.
 * - backlog: ≥ CONTENT_PLAN_BACKLOG_READY_THRESHOLD topics with status "ready".
 * - strategy: ≥1 content strategy for the author.
 * - publications: the newest strategy has ≥1 publication.
 * - posts: ≥1 canonical post for the author.
 * - assets: NOT required; mirrors posts (per-post asset state is inspected via
 *   `posts assets brief get`, not aggregated here).
 */
export interface ContentPlanStepReadiness {
  step: ContentPlanFlowStep;
  ok: boolean;
  /** Required steps gate overall readiness (assets is advisory). */
  required: boolean;
  /** Observed count backing the predicate (authors, platforms, pillars, …). */
  count?: number;
  detail?: string;
  /** Exact runnable CLI command that advances this step; present when !ok. */
  nextCommand?: string;
}

export interface CliContentPlanFlowReadinessData {
  projectId: string;
  authorId: string | null;
  /** True when every required step is ok. */
  ready: boolean;
  steps: ContentPlanStepReadiness[];
}

/** Raw observations the readiness predicates evaluate — gathered server-side
 * (Firestore admin reads for `doctor`) or client-side (live subscriptions for
 * the M2 wizard). */
export interface ContentPlanReadinessCounts {
  /** Total authors in the project. */
  authorCount: number;
  /** True when a default (or sole) author resolved. */
  authorResolved: boolean;
  platformCount: number;
  /** Total pillars (drafts + confirmed). */
  pillarCount: number;
  /** Non-draft pillars. */
  confirmedPillarCount: number;
  /** Topics with status "ready". */
  readyTopicCount: number;
  strategyCount: number;
  /** Publications of the newest strategy (0 when no strategy). */
  publicationCount: number;
  /** Canonical posts for the author. */
  postCount: number;
}

/**
 * THE single implementation of the D12 step predicates. Both consumers call
 * this function — `content-plan doctor` (backend snapshot) and the M2 wizard
 * hook (live counts) — so CLI and UI verdicts cannot diverge by construction.
 */
export function evaluateContentPlanFlowReadiness(
  counts: ContentPlanReadinessCounts,
): Pick<CliContentPlanFlowReadinessData, 'ready' | 'steps'> {
  const steps: ContentPlanStepReadiness[] = [];

  const authorOk = counts.authorResolved;
  steps.push({
    step: 'author',
    ok: authorOk,
    required: true,
    count: counts.authorCount,
    ...(authorOk ? {} : {
      detail: counts.authorCount === 0
        ? 'No Content Plan author exists yet.'
        : 'No default author is set (multiple authors, none marked default).',
      nextCommand: counts.authorCount === 0
        ? 'segmently content-plan authors onboard --file <onboard-request.json>'
        : 'segmently content-plan authors set-current <authorId>',
    }),
  });

  const platformsOk = counts.platformCount > 0;
  steps.push({
    step: 'platforms',
    ok: platformsOk,
    required: true,
    count: counts.platformCount,
    ...(platformsOk ? {} : {
      detail: authorOk ? 'The author has no platforms enabled.' : 'Blocked: resolve the author step first.',
      nextCommand: 'segmently content-plan platforms apply --author <authorId> --file <platform.json>',
    }),
  });

  const pillarsOk = counts.confirmedPillarCount > 0;
  steps.push({
    step: 'pillars',
    ok: pillarsOk,
    required: true,
    count: counts.confirmedPillarCount,
    ...(pillarsOk ? {} : {
      detail: counts.pillarCount > 0
        ? `${counts.pillarCount} pillar(s) exist but none is confirmed (all drafts).`
        : 'No content pillars exist yet.',
      nextCommand: 'segmently content-plan pillars apply --file <pillars.json>',
    }),
  });

  const backlogOk = counts.readyTopicCount >= CONTENT_PLAN_BACKLOG_READY_THRESHOLD;
  steps.push({
    step: 'backlog',
    ok: backlogOk,
    required: true,
    count: counts.readyTopicCount,
    ...(backlogOk ? {} : {
      detail: `${counts.readyTopicCount}/${CONTENT_PLAN_BACKLOG_READY_THRESHOLD} ready topics in the backlog.`,
      nextCommand: 'segmently content-plan topics generate --author <authorId>',
    }),
  });

  const strategyOk = counts.strategyCount > 0;
  steps.push({
    step: 'strategy',
    ok: strategyOk,
    required: true,
    count: counts.strategyCount,
    ...(strategyOk ? {} : {
      detail: 'No content strategy exists for the author.',
      nextCommand: 'segmently content-plan strategies create --author <authorId>',
    }),
  });

  const publicationsOk = counts.publicationCount > 0;
  steps.push({
    step: 'publications',
    ok: publicationsOk,
    required: true,
    count: counts.publicationCount,
    ...(publicationsOk ? {} : {
      detail: strategyOk
        ? 'The strategy has no calendar publications.'
        : 'Blocked: create a strategy first.',
      nextCommand: 'segmently content-plan publications apply --strategy <strategyId> --file <publication.json>',
    }),
  });

  const postsOk = counts.postCount > 0;
  steps.push({
    step: 'posts',
    ok: postsOk,
    required: true,
    count: counts.postCount,
    ...(postsOk ? {} : {
      detail: 'No canonical posts exist yet.',
      nextCommand: 'segmently content-plan posts generate --author <authorId> --scope sprint --sprint <sprintId>',
    }),
  });

  steps.push({
    step: 'assets',
    ok: postsOk,
    required: false,
    count: counts.postCount,
    detail: postsOk
      ? 'Inspect per-post asset readiness with `posts assets brief get <postId>`.'
      : 'Blocked: generate posts first.',
    ...(postsOk ? {} : {
      nextCommand: 'segmently content-plan posts assets plan <postId>',
    }),
  });

  return {
    ready: steps.every((step) => !step.required || step.ok),
    steps,
  };
}

/** One `system_platforms/{platformId}` catalog entry projected for CLI preflight. */
export interface CliContentPlanSystemPlatformCatalogEntry {
  platformId: string;
  exists: boolean;
  isActive: boolean;
  formatIds: string[];
}

export interface CliContentPlanSystemPlatformCatalogData {
  platforms: CliContentPlanSystemPlatformCatalogEntry[];
}

export interface CliContentPlanPlatformVoiceData {
  projectId: string;
  authorId: string;
  basePath: CliContentPlanAuthorBasePath;
  platformId: string;
  voice?: Record<string, unknown> | null;
}

export interface CliContentPlanPlatformVoiceApplyRequest {
  basePath?: CliContentPlanAuthorBasePath;
  dryRun?: boolean;
  voice: Record<string, unknown>;
}

export interface CliContentPlanPlatformVoiceMutationData extends CliContentPlanPlatformVoiceData {
  appliedAt: string;
  dryRun: boolean;
  changedFields: string[];
  action: 'created' | 'updated' | 'would_create' | 'would_update';
}

export type CliContentPlanProfileApplyAction =
  | 'created'
  | 'updated'
  | 'would_create'
  | 'would_update';

export interface CliContentPlanPillarThesis {
  thesis: string;
  confidence?: number;
  sourcePostIds?: string[];
}

export interface CliContentPlanPillarSourceHighlight {
  postId: string;
  platformId?: string;
  quoteOrParaphrase: string;
  relevance: string;
}

export interface CliContentPlanPillarSummary {
  id: string;
  name: string;
  description?: string | null;
  summary?: string | null;
  knowledgeContext?: string | null;
  keyTheses?: CliContentPlanPillarThesis[];
  sourcePostIds?: string[];
  sourceHighlights?: CliContentPlanPillarSourceHighlight[];
  guardrails?: string[];
  weight?: number | null;
  funnelStage?: string | null;
  authorStance?: string | null;
  authorIds?: string[];
  isDraft?: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CliContentPlanPillarInput {
  id?: string;
  name: string;
  description?: string | null;
  summary?: string | null;
  knowledgeContext?: string | null;
  keyTheses?: CliContentPlanPillarThesis[];
  sourcePostIds?: string[];
  sourceHighlights?: CliContentPlanPillarSourceHighlight[];
  guardrails?: string[];
  weight?: number | null;
  funnelStage?: string | null;
  authorStance?: string | null;
  authorIds?: string[];
  isDraft?: boolean;
}

export interface CliContentPlanPillarListData {
  projectId: string;
  authorId?: string | null;
  pillars: CliContentPlanPillarSummary[];
}

export interface CliContentPlanPillarApplyRequest {
  authorId?: string | null;
  dryRun?: boolean;
  pillars: CliContentPlanPillarInput[];
}

export interface CliContentPlanPillarApplyItem {
  id: string;
  name: string;
  action: CliContentPlanProfileApplyAction;
}

export interface CliContentPlanPillarApplyData {
  appliedAt: string;
  projectId: string;
  authorId?: string | null;
  dryRun: boolean;
  pillars: CliContentPlanPillarApplyItem[];
}

export interface CliContentPlanPostTemplateRecommendedFormat {
  formatType: string;
  weight?: number;
  reason?: string;
  bestForGoals?: string[];
  bestForAssetIntents?: string[];
}

export interface CliContentPlanPostTemplateVariable {
  key?: string;
  name?: string;
  description?: string;
  required?: boolean;
  [key: string]: unknown;
}

export interface CliContentPlanPostTemplateSummary {
  id: string;
  name?: string | null;
  description?: string | null;
  structure?: string | null;
  funnelStage?: string | null;
  tag?: string | null;
  goal?: string | null;
  platform?: string | null;
  platformId?: string | null;
  tone?: string | null;
  lengthBucket?: string | null;
  scaffold: string;
  variables?: CliContentPlanPostTemplateVariable[];
  elements?: Record<string, unknown>;
  hashtagPolicy?: string | null;
  emojiPolicy?: string | null;
  exampleEnglish?: string | null;
  exampleRu?: string | null;
  proofObjectHints?: string[];
  version?: number | null;
  baseTemplateId?: string | null;
  compatibleFormatTypes?: string[];
  recommendedFormats?: CliContentPlanPostTemplateRecommendedFormat[];
  scope?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CliContentPlanPostTemplateInput {
  id: string;
  name?: string | null;
  description?: string | null;
  structure?: string | null;
  funnelStage?: string | null;
  tag?: string | null;
  goal?: string | null;
  platform?: string | null;
  tone?: string | null;
  lengthBucket?: string | null;
  scaffold: string;
  variables?: CliContentPlanPostTemplateVariable[];
  elements?: Record<string, unknown>;
  hashtagPolicy?: string | null;
  emojiPolicy?: string | null;
  exampleEnglish?: string | null;
  exampleRu?: string | null;
  proofObjectHints?: string[];
  version?: number | null;
  baseTemplateId?: string | null;
  compatibleFormatTypes?: string[];
  recommendedFormats?: CliContentPlanPostTemplateRecommendedFormat[];
  scope?: string | null;
}

export interface CliContentPlanPostTemplateListData {
  projectId: string;
  authorId: string;
  basePath: CliContentPlanAuthorBasePath;
  platformId: string;
  templates: CliContentPlanPostTemplateSummary[];
}

export interface CliContentPlanPostTemplateApplyRequest {
  authorId: string;
  basePath?: CliContentPlanAuthorBasePath;
  platformId: string;
  dryRun?: boolean;
  templates: CliContentPlanPostTemplateInput[];
}

export interface CliContentPlanPostTemplateApplyItem {
  id: string;
  name?: string | null;
  action: CliContentPlanProfileApplyAction;
}

export interface CliContentPlanPostTemplateApplyData {
  appliedAt: string;
  projectId: string;
  authorId: string;
  basePath: CliContentPlanAuthorBasePath;
  platformId: string;
  dryRun: boolean;
  templates: CliContentPlanPostTemplateApplyItem[];
}

export interface CliContentPlanProfileExportData {
  exportedAt: string;
  projectId: string;
  authorId: string;
  basePath: CliContentPlanAuthorBasePath;
  pillars: CliContentPlanPillarSummary[];
  platforms: Array<{
    platformId: string;
    templates: CliContentPlanPostTemplateSummary[];
  }>;
}

export interface CliContentPlanProfileApplyRequest {
  authorId: string;
  basePath?: CliContentPlanAuthorBasePath;
  dryRun?: boolean;
  pillars?: CliContentPlanPillarInput[];
  postTemplates?: Array<{
    platformId: string;
    templates: CliContentPlanPostTemplateInput[];
  }>;
}

export interface CliContentPlanProfileApplyData {
  appliedAt: string;
  projectId: string;
  authorId: string;
  basePath: CliContentPlanAuthorBasePath;
  dryRun: boolean;
  pillars?: CliContentPlanPillarApplyItem[];
  postTemplates: Array<{
    platformId: string;
    templates: CliContentPlanPostTemplateApplyItem[];
  }>;
}

export type CliContentPlanDesignProfileKey =
  | 'design'
  | 'single_image_landscape'
  | 'single_image_square'
  | 'single_image_portrait'
  | 'carousel_square'
  | 'carousel_portrait';

export type CliContentPlanDesignReferenceRole =
  | 'style'
  | 'layout'
  | 'typography'
  | 'logo'
  | 'approved'
  | 'motif'
  | 'anchor'
  | 'object'
  | 'character';

export type CliContentPlanDesignReferencePolicy = 'keep' | 'upload';

export interface CliContentPlanDesignReferenceTextBudget {
  slot: string;
  textRole?: string | null;
  containerSlot?: string | null;
  label?: string | null;
  recommendedChars?: number | null;
  maxChars: number;
  maxLines?: number | null;
  notes?: string | null;
}

export interface CliContentPlanDesignReference {
  url: string;
  name?: string | null;
  instruction?: string | null;
  role?: CliContentPlanDesignReferenceRole | string | null;
  mimeType?: string | null;
  textBudgets?: CliContentPlanDesignReferenceTextBudget[];
}

export interface CliContentPlanDesignSystemExport extends Record<string, unknown> {
  _schema: 'segmently-design-system/v1';
  design_description?: string;
  palette_primary?: string;
  palette_accent?: string;
  palette_neutral?: string[];
  palette_forbidden?: string[];
  typography_primary_font?: string;
  typography_heading_style?: string;
  typography_body_style?: string;
  mood_keywords?: string[];
  illustration_style?: string;
  illustration_line_weight?: string;
  illustration_shadow?: string;
  illustration_texture?: string;
  photo_camera?: string;
  photo_lighting?: string;
  photo_background?: string;
  composition_grid?: string;
  composition_spacing?: string;
  composition_hierarchy?: string;
  brand_motifs?: string[];
  forbidden_visuals?: string[];
  text_policy_mode?: string;
  text_policy_exact_text_required?: boolean;
  visual_references?: CliContentPlanDesignReference[];
}

export interface CliContentPlanDesignAssetManifestItem {
  name?: string | null;
  role?: string | null;
  sourceUrl: string;
  sourceEnv?: string | null;
  targetUrl?: string | null;
  smallUrl?: string | null;
  sha256?: string | null;
  action?: 'keep' | 'upload' | 'block' | 'skip';
  reason?: string | null;
}

export interface CliContentPlanDesignProfileManifest {
  schemaVersion: 'segmently.cli.content-plan-design-profile.v1';
  exportedAt: string;
  source: {
    env?: string | null;
    projectId: string;
    authorId: string;
    basePath: CliContentPlanAuthorBasePath;
    platformId: string;
    profileKey: CliContentPlanDesignProfileKey;
    profileId?: string | null;
  };
  profile: {
    profileId?: string | null;
    name?: string | null;
    description?: string | null;
    isComplete?: boolean;
    setCurrent?: boolean;
  };
  designSystem: CliContentPlanDesignSystemExport;
  visualReferences: CliContentPlanDesignReference[];
  assetManifest?: {
    items: CliContentPlanDesignAssetManifestItem[];
  };
  contentHash: string;
}

export interface CliContentPlanDesignProfileSummary {
  id: string;
  profileId: string;
  profileKey: CliContentPlanDesignProfileKey;
  platformId: string;
  authorId?: string | null;
  name?: string | null;
  description?: string | null;
  designDescription?: string | null;
  referenceCount: number;
  isComplete: boolean;
  source?: string | null;
  current: boolean;
  updatedAt?: string | null;
  raw?: Record<string, unknown>;
}

export interface CliContentPlanDesignProfileSetSummary {
  profileKey: CliContentPlanDesignProfileKey;
  displayName?: string | null;
  description?: string | null;
  currentProfileId?: string | null;
  isActive?: boolean;
  profileCount?: number;
  profiles?: CliContentPlanDesignProfileSummary[];
}

export interface CliContentPlanDesignProfileListData {
  projectId: string;
  authorId: string;
  basePath: CliContentPlanAuthorBasePath;
  platformId: string;
  sets: CliContentPlanDesignProfileSetSummary[];
}

export interface CliContentPlanDesignProfileExportData {
  exportedAt: string;
  projectId: string;
  authorId: string;
  basePath: CliContentPlanAuthorBasePath;
  platformId: string;
  profileKey: CliContentPlanDesignProfileKey;
  profileId: string;
  current: boolean;
  set?: Record<string, unknown>;
  profile: Record<string, unknown>;
}

export interface CliContentPlanDesignProfileApplyRequest {
  authorId: string;
  basePath?: CliContentPlanAuthorBasePath;
  platformId: string;
  profileKey: CliContentPlanDesignProfileKey;
  profileId?: string;
  name?: string | null;
  description?: string | null;
  setCurrent?: boolean;
  dryRun?: boolean;
  profile: {
    designDescription?: string | null;
    designSystem?: Record<string, unknown>;
    visualReferences?: CliContentPlanDesignReference[];
    forbiddenVisuals?: string[];
    isComplete?: boolean;
    source?: string | null;
  };
}

export interface CliContentPlanDesignProfileApplyData {
  appliedAt: string;
  projectId: string;
  authorId: string;
  basePath: CliContentPlanAuthorBasePath;
  platformId: string;
  profileKey: CliContentPlanDesignProfileKey;
  profileId: string;
  dryRun: boolean;
  action: CliContentPlanProfileApplyAction;
  setCurrent: boolean;
  runtimeUpdated: boolean;
  referenceCount: number;
  changedFields: string[];
}

export interface CliContentPlanDesignProfileMakeCurrentRequest {
  authorId: string;
  basePath?: CliContentPlanAuthorBasePath;
  platformId: string;
  profileKey: CliContentPlanDesignProfileKey;
  profileId: string;
  dryRun?: boolean;
}

export interface CliContentPlanDesignProfileMakeCurrentData {
  appliedAt: string;
  projectId: string;
  authorId: string;
  basePath: CliContentPlanAuthorBasePath;
  platformId: string;
  profileKey: CliContentPlanDesignProfileKey;
  profileId: string;
  previousProfileId?: string | null;
  dryRun: boolean;
  changed: boolean;
  runtimeUpdated: boolean;
}

export type CliContentPlanDesignSystemPackageScope = 'author' | 'system';
export type CliContentPlanDesignSystemPackageStatus = 'draft' | 'reviewed' | 'archived';

export interface CliContentPlanDesignSystemPackageProfileInput {
  profileKey: CliContentPlanDesignProfileKey;
  profileId?: string | null;
  name?: string | null;
  description?: string | null;
  designDescription?: string | null;
  designSystem?: Record<string, unknown>;
  visualReferences?: CliContentPlanDesignReference[];
  forbiddenVisuals?: string[];
  isComplete?: boolean;
  source?: string | null;
  contentHash?: string | null;
}

export interface CliContentPlanDesignSystemPackageManifest {
  schemaVersion: 'segmently.cli.content-plan-design-system-package.v1';
  exportedAt: string;
  source: {
    env?: string | null;
    projectId: string;
    authorId: string;
    basePath: CliContentPlanAuthorBasePath;
    platformId: string;
    packageId: string;
  };
  package: {
    packageId?: string | null;
    name?: string | null;
    description?: string | null;
    status?: CliContentPlanDesignSystemPackageStatus;
    setCurrent?: boolean;
    targetProfileKeys: CliContentPlanDesignProfileKey[];
    visualFoundation?: Record<string, unknown>;
  };
  profiles: Partial<Record<CliContentPlanDesignProfileKey, CliContentPlanDesignSystemPackageProfileInput>>;
  assetManifest?: {
    items: CliContentPlanDesignAssetManifestItem[];
  };
  contentHash: string;
}

export interface CliContentPlanDesignSystemPackageProfileSummary {
  profileKey: CliContentPlanDesignProfileKey;
  profileId: string;
  name?: string | null;
  description?: string | null;
  designDescription?: string | null;
  referenceCount: number;
  isComplete: boolean;
  contentHash?: string | null;
  updatedAt?: string | null;
  raw?: Record<string, unknown>;
}

export interface CliContentPlanDesignSystemPackageSummary {
  packageId: string;
  name?: string | null;
  description?: string | null;
  status: CliContentPlanDesignSystemPackageStatus;
  current: boolean;
  targetProfileKeys: CliContentPlanDesignProfileKey[];
  completeProfileKeys: CliContentPlanDesignProfileKey[];
  profileCount: number;
  contentHash?: string | null;
  updatedAt?: string | null;
  profiles?: CliContentPlanDesignSystemPackageProfileSummary[];
  raw?: Record<string, unknown>;
}

export interface CliContentPlanDesignSystemPackageListData {
  projectId: string;
  authorId: string;
  basePath: CliContentPlanAuthorBasePath;
  platformId: string;
  packages: CliContentPlanDesignSystemPackageSummary[];
}

export interface CliContentPlanDesignSystemPackageExportData {
  exportedAt: string;
  projectId: string;
  authorId: string;
  basePath: CliContentPlanAuthorBasePath;
  platformId: string;
  packageId: string;
  current: boolean;
  package: Record<string, unknown>;
  profiles: Partial<Record<CliContentPlanDesignProfileKey, Record<string, unknown>>>;
}

export interface CliContentPlanDesignSystemPackageInspectReference {
  id?: string | null;
  name?: string | null;
  role?: string | null;
  mimeType?: string | null;
  hasInstruction: boolean;
}

export interface CliContentPlanDesignSystemPackageInspectFormat {
  formatId: string;
  formatType?: string | null;
  assetIntent: string;
  profileKey: string;
  styleId: string;
  sourceChain: string[];
  designPackage?: {
    packageId: string;
    name?: string | null;
    contentHash?: string | null;
    profileKeys: string[];
    profileContentHashes: Record<string, string>;
  } | null;
  referenceCount: number;
  providerReferenceCount: number;
  warnings: string[];
  palette?: Record<string, unknown>;
  motifs?: string[];
  forbidden?: string[];
  textPolicy?: Record<string, unknown>;
  promptExcerpt?: string | null;
  stylePromptBlock?: string | null;
  references: CliContentPlanDesignSystemPackageInspectReference[];
}

export interface CliContentPlanDesignSystemPackageInspectData {
  inspectedAt: string;
  projectId: string;
  authorId: string;
  basePath: CliContentPlanAuthorBasePath;
  platformId: string;
  packageId: string;
  current: boolean;
  defaultFormatIds: string[];
  formats: CliContentPlanDesignSystemPackageInspectFormat[];
}

export type CliAiProviderType = 'anthropic' | 'openai' | 'google' | 'perplexity';

export interface CliAiModelInfo {
  id: string;
  provider: CliAiProviderType;
  displayName: string;
  maxInputTokens?: number;
  maxOutputTokens?: number;
  costPer1kInput?: number;
  costPer1kOutput?: number;
  supportsStructuredOutput?: boolean;
  supportsStreaming?: boolean;
  supportsVision?: boolean;
}

export interface CliAiImageModelInfo {
  id: string;
  provider: CliAiProviderType;
  displayName: string;
  supportsReferenceImages: boolean;
  maxReferenceImages: number;
}

export interface CliContentPlanModelProviderAvailability {
  configured: boolean;
  source?: 'user';
  textModels: boolean;
  imageModels: boolean;
  error?: string;
}

export interface CliContentPlanModelPreferences {
  platformPost?: string | null;
  assetPlanner?: string | null;
  imageGen?: string | null;
}

export interface CliContentPlanModelAvailabilityData {
  projectId: string;
  sourcePolicy: 'project_user_keys_only';
  providers: Partial<Record<CliAiProviderType, CliContentPlanModelProviderAvailability>>;
  textModels: CliAiModelInfo[];
  imageModels: CliAiImageModelInfo[];
  preferences: CliContentPlanModelPreferences;
}

export interface CliContentPlanModelPreferencesData {
  projectId: string;
  sourcePolicy: 'project_user_keys_only';
  preferences: CliContentPlanModelPreferences;
  updatedAt?: string | null;
}

export interface CliContentPlanModelPreferencesApplyRequest {
  platformPost?: string | null;
  assetPlanner?: string | null;
  imageGen?: string | null;
  clear?: boolean;
}

export interface CliContentPlanModelPreferencesApplyData extends CliContentPlanModelPreferencesData {
  changed: boolean;
}

export interface CliContentPlanDesignSystemPackageApplyRequest {
  authorId: string;
  basePath?: CliContentPlanAuthorBasePath;
  platformId: string;
  packageId?: string;
  dryRun?: boolean;
  setCurrent?: boolean;
  package: {
    packageId?: string | null;
    name?: string | null;
    description?: string | null;
    status?: CliContentPlanDesignSystemPackageStatus;
    targetProfileKeys?: CliContentPlanDesignProfileKey[];
    visualFoundation?: Record<string, unknown>;
    contentHash?: string | null;
  };
  profiles: Partial<Record<CliContentPlanDesignProfileKey, CliContentPlanDesignSystemPackageProfileInput>>;
}

export interface CliContentPlanDesignSystemPackageProfileApplyItem {
  profileKey: CliContentPlanDesignProfileKey;
  profileId: string;
  action: CliContentPlanProfileApplyAction;
  setCurrent: boolean;
  runtimeUpdated: boolean;
  referenceCount: number;
  changedFields: string[];
}

export interface CliContentPlanDesignSystemPackageApplyData {
  appliedAt: string;
  projectId: string;
  authorId: string;
  basePath: CliContentPlanAuthorBasePath;
  platformId: string;
  packageId: string;
  dryRun: boolean;
  action: CliContentPlanProfileApplyAction;
  setCurrent: boolean;
  runtimeUpdated: boolean;
  profileResults: CliContentPlanDesignSystemPackageProfileApplyItem[];
  referenceCount: number;
  changedFields: string[];
}

export interface CliContentPlanDesignSystemPackageMakeCurrentRequest {
  authorId: string;
  basePath?: CliContentPlanAuthorBasePath;
  platformId: string;
  packageId: string;
  dryRun?: boolean;
}

export interface CliContentPlanDesignSystemPackageMakeCurrentData {
  appliedAt: string;
  projectId: string;
  authorId: string;
  basePath: CliContentPlanAuthorBasePath;
  platformId: string;
  packageId: string;
  previousPackageId?: string | null;
  dryRun: boolean;
  changed: boolean;
  runtimeUpdated: boolean;
  profileResults: CliContentPlanDesignSystemPackageProfileApplyItem[];
}

export type CliWebPlacementParameterDataType = 'string' | 'number' | 'boolean';
export type CliWebPlacementParameterSource = 'url' | 'server' | 'custom' | 'variable';

export interface CliWebPlacementFunnelRef {
  funnelId: string;
  versionId: string;
}

export interface CliWebPlacementApplyRequest {
  version?: 'cli.web-placement.v1' | string;
  webPlacement?: {
    id?: string;
    name: string;
    alias: string;
    description?: string;
    funnelRef: CliWebPlacementFunnelRef;
    saveUserProgress?: boolean;
    rememberPaymentMethod?: boolean;
    paddleCheckout?: PaddleCheckoutPolicy;
    redirectUrl?: string;
    fallbackUrl?: string;
    urlEncode?: boolean;
    seo?: Record<string, unknown>;
    customAnalytics?: Record<string, unknown>;
    consentManagement?: Record<string, unknown>;
  };
  sources?: {
    urlParams?: Array<{
      key: string;
      urlParam?: string;
      label?: string;
      dataType?: CliWebPlacementParameterDataType;
      description?: string;
    }>;
    serverData?: Array<{
      key: string;
      path: string;
      label?: string;
      dataType?: CliWebPlacementParameterDataType;
      description?: string;
    }>;
    variableInputs?: Array<{
      key: string;
      variable: string;
      label?: string;
      dataType?: CliWebPlacementParameterDataType;
      description?: string;
    }>;
    customVariables?: Array<{
      key: string;
      label?: string;
      dataType?: CliWebPlacementParameterDataType;
      description?: string;
    }>;
  };
  destinations?: {
    redirectMappings?: Array<{ sourceType: CliWebPlacementParameterSource; sourceKey: string }>;
    stripeMappings?: Array<{ sourceType: CliWebPlacementParameterSource; sourceKey: string }>;
    stripeEmail?: { variable: string };
    variableBindings?: Array<{
      sourceType: 'url' | 'server';
      sourceKey: string;
      variable: string;
    }>;
    webhooks?: Array<{
      id?: string;
      name: string;
      url: string;
      method?: 'GET' | 'POST';
      trigger: string;
      triggerScreens?: string[];
      bearerToken?: string;
      headers?: Record<string, string>;
      sendAllParameters?: boolean;
      parameterKeys?: string[];
    }>;
  };
  entryScreenRules?: Array<{
    id?: string;
    paramKey: string;
    operator?: 'eq' | 'neq' | 'in' | 'notIn' | 'contains' | 'regex';
    value: string | string[];
    targetScreen: string;
    isActive?: boolean;
  }>;
}

export interface CliWebPlacementSummary {
  id: string;
  projectId: string;
  name: string;
  alias: string;
  type: 'single' | 'ab_test';
  status: 'draft' | 'published';
  funnelId?: string | null;
  versionId?: string | null;
  publishedUrl?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CliWebPlacementApplyData {
  created: boolean;
  webPlacement: CliWebPlacementSummary;
  config: Record<string, unknown>;
}

export interface CliWebPlacementListData {
  webPlacements: CliWebPlacementSummary[];
}

export interface CliFunnelApplyRequest {
  version?: 'cli.funnel-apply.v1' | string;
  funnel: CliFunnelCreateRequest;
  graph: CliFunnelGraphApplyRequest;
  webPlacement?: CliWebPlacementApplyRequest;
}

export interface CliFunnelApplyData extends CliFunnelCreateData {
  graph: CliFunnelGraphApplyData;
  webPlacement?: CliWebPlacementApplyData;
}

export interface CliABTestApplyRequest {
  version?: 'cli.ab-test.v1' | string;
  abTest?: {
    id?: string;
    name: string;
    alias: string;
    description?: string;
    tags?: string[];
    baselineVariantId?: string;
    abSalt?: string;
    iteration?: {
      id?: string;
      label?: string;
      description?: string;
      tags?: string[];
      baselineVariantId?: string;
    };
  };
  variants?: Array<{
    id: string;
    name?: string;
    description?: string;
    role?: 'control' | 'treatment' | 'baseline' | 'holdout';
    tags?: string[];
    weight: number;
    webPlacementId?: string;
    webPlacementAlias?: string;
  }>;
}

export interface CliABTestVariantSummary extends WebABPublicationVariantSummary {}

export interface CliABTestPublicationSummary extends WebABPublicationRecord {}

export interface CliABTestPublicationListData {
  publications: CliABTestPublicationSummary[];
}

export interface CliABTestSummary {
  id: string;
  projectId: string;
  name: string;
  alias: string;
  description?: string | null;
  tags?: string[];
  status: 'draft' | 'published';
  abSalt?: string | null;
  baselineVariantId?: string | null;
  activeIterationId?: string | null;
  publishRevision?: number | null;
  latestPublicationId?: string | null;
  latestPublication?: CliABTestPublicationSummary | null;
  variants: CliABTestVariantSummary[];
  publishedUrl?: string | null;
  publishedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CliABTestApplyData {
  created: boolean;
  abTest: CliABTestSummary;
}

export interface CliABTestListData {
  abTests: CliABTestSummary[];
}

export interface CliABTestPublishData {
  success: boolean;
  alias: string;
  abTestId: string;
  publishId?: string;
  publishRevision?: number;
  iterationId?: string | null;
  /** Canonical hash of routing-critical allocation state. */
  routingHash?: string;
  /** Delivery-only means fewer than two variants receive positive traffic. */
  allocationMode?: 'delivery_only' | 'experiment';
  /** Whether this publish activated a new immutable epoch or reused the current one. */
  epochAction?: 'created' | 'reused';
  variantCount: number;
  gcsPath?: string;
  webUrl?: string;
  message?: string;
}

export type CliABTestAllocationAuditClassification =
  | 'valid_immutable'
  | 'delivery_only'
  | 'missing_metadata'
  | 'mixed_epoch';

export type CliABTestAllocationEvidenceSource =
  | 'immutable_iteration_snapshot'
  | 'publication_history'
  | 'routing_artifact'
  | 'rollout_audit_record';

export interface CliABTestAllocationVariantEvidence {
  variantId: string;
  weight: number;
  targetAlias: string;
}

export interface CliABTestAllocationEvidence {
  source: CliABTestAllocationEvidenceSource;
  evidenceId: string;
  iterationId: string;
  routingHash?: string | null;
  activatedAt?: string | null;
  supersededAt?: string | null;
  variants?: CliABTestAllocationVariantEvidence[] | null;
}

export interface CliABTestAllocationAuditTarget {
  iterationId: string;
  configuredVariantIds?: string[];
}

/** Supplemental evidence for the read-only audit; stored evidence is collected server-side. */
export interface CliABTestAllocationAuditRequest {
  targets?: CliABTestAllocationAuditTarget[];
  evidence?: CliABTestAllocationEvidence[];
}

export interface CliABTestAllocationEpochCandidate {
  iterationId: string;
  routingHash: string;
  allocationMode: 'delivery_only' | 'experiment';
  activatedAt: string;
  supersededAt: string | null;
  variants: CliABTestAllocationVariantEvidence[];
  authoritativeEvidenceIds: string[];
  authoritativeSources: CliABTestAllocationEvidenceSource[];
}

export interface CliABTestAllocationIterationAudit {
  iterationId: string;
  classification: CliABTestAllocationAuditClassification;
  configuredVariantIds: string[];
  candidates: CliABTestAllocationEpochCandidate[];
  authoritativeEvidenceIds: string[];
  incompleteEvidence: Array<{
    evidenceId: string;
    source: CliABTestAllocationEvidenceSource;
    issues: string[];
  }>;
  reason:
    | 'immutable_experiment_epoch_proven'
    | 'single_effective_variant_proven'
    | 'authoritative_allocation_metadata_missing'
    | 'multiple_authoritative_routing_states';
}

export interface CliABTestAllocationRepairProposalItem {
  iterationId: string;
  classification: CliABTestAllocationAuditClassification;
  proposedAction:
    | 'no_change'
    | 'backfill_immutable_epoch'
    | 'collect_authoritative_metadata'
    | 'split_legacy_epoch_after_review';
  proposedEpochs: CliABTestAllocationEpochCandidate[];
  rationale: string;
}

export interface CliABTestAllocationAuditData {
  projectId: string;
  abTestId: string;
  publicAlias: string;
  classification: CliABTestAllocationAuditClassification;
  iterations: CliABTestAllocationIterationAudit[];
  repairManifest: {
    schemaVersion: 'legacy-web-ab-repair-manifest/v1';
    manifestId: string;
    dryRun: true;
    reviewStatus: 'pending_review';
    reviewRequired: true;
    mutationAllowed: false;
    mutationCount: 0;
    writes: never[];
    projectId: string;
    abTestId: string;
    publicAlias: string;
    items: CliABTestAllocationRepairProposalItem[];
  };
}

export type CliABTestRolloutConflictPolicy = 'fail' | 'update-existing';

export interface CliABTestRolloutWebPlacementInput {
  id?: string;
  name: string;
  alias: string;
  description?: string;
  saveUserProgress?: boolean;
  rememberPaymentMethod?: boolean;
  redirectUrl?: string;
  fallbackUrl?: string;
  urlEncode?: boolean;
  seo?: Record<string, unknown>;
  customAnalytics?: Record<string, unknown>;
  consentManagement?: Record<string, unknown>;
}

export interface CliABTestRolloutExistingWebPlacementSource {
  type: 'existingWebPlacement';
  webPlacementId?: string;
  webPlacementAlias?: string;
}

export interface CliABTestRolloutExistingFunnelVersionSource {
  type: 'existingFunnelVersion';
  funnelId: string;
  versionId: string;
  webPlacement: CliABTestRolloutWebPlacementInput;
  sources?: CliWebPlacementApplyRequest['sources'];
  destinations?: CliWebPlacementApplyRequest['destinations'];
  entryScreenRules?: CliWebPlacementApplyRequest['entryScreenRules'];
}

export interface CliABTestRolloutFunnelManifestSource {
  type: 'funnelManifest';
  funnel: CliFunnelCreateRequest;
  graph: CliFunnelGraphApplyRequest;
  webPlacement: CliABTestRolloutWebPlacementInput;
  sources?: CliWebPlacementApplyRequest['sources'];
  destinations?: CliWebPlacementApplyRequest['destinations'];
  entryScreenRules?: CliWebPlacementApplyRequest['entryScreenRules'];
}

export interface CliABTestRolloutPaywallProductInput {
  productId: string;
  title?: string;
  subtitle?: string;
  badge?: string;
  isDefault?: boolean;
}

export interface CliABTestRolloutPaywallMutation {
  products: CliABTestRolloutPaywallProductInput[];
  purchaseAction?: {
    content: string;
    action?: Record<string, unknown>;
  };
}

export interface CliABTestRolloutScreenMutation {
  screenId: string;
  paywall: CliABTestRolloutPaywallMutation;
}

export interface CliABTestRolloutCloneInput {
  name: string;
  versionName?: string;
  folderId?: string | null;
}

export interface CliABTestRolloutClonedFunnelVersionSource {
  type: 'clonedFunnelVersion';
  sourceProjectId?: string;
  funnelId: string;
  versionId: string;
  clone: CliABTestRolloutCloneInput;
  /** Single-screen mutation (legacy shape). Provide this OR `mutations`. */
  mutation?: CliABTestRolloutScreenMutation;
  /**
   * Multi-paywall funnels (pricing AC-022): one mutation per paywall screen.
   * Every paywall-classed screen of the clone must be covered — a variant that
   * mutates only a subset is rejected with the unmutated screens named.
   */
  mutations?: CliABTestRolloutScreenMutation[];
  webPlacement: CliABTestRolloutWebPlacementInput;
  sources?: CliWebPlacementApplyRequest['sources'];
  destinations?: CliWebPlacementApplyRequest['destinations'];
  entryScreenRules?: CliWebPlacementApplyRequest['entryScreenRules'];
}

export type CliABTestRolloutVariantSource =
  | CliABTestRolloutExistingWebPlacementSource
  | CliABTestRolloutExistingFunnelVersionSource
  | CliABTestRolloutFunnelManifestSource
  | CliABTestRolloutClonedFunnelVersionSource;

export interface CliABTestRolloutVariantInput {
  id: string;
  name?: string;
  description?: string;
  role?: 'control' | 'treatment' | 'baseline';
  tags?: string[];
  weight: number;
  source: CliABTestRolloutVariantSource;
}

export interface CliABTestRolloutApplyRequest {
  version?: 'cli.ab-test-rollout.v1' | string;
  operationId?: string;
  dryRun?: boolean;
  /**
   * Known-event linkage (cadence-program C3-I3, C3-AC-006): a rollout that
   * mutates paywall menus (a price/menu/paywall change) is admitted ONLY when
   * bound to a registered experiment in THE experiments registry (D47 — one
   * registry). The registry entry is the exact object the anomaly lane
   * derives known events from (C2-AC-016), so an admitted rollout can never
   * fire a cancellation anomaly as an unexplained incident.
   */
  experimentRef?: { experimentId?: string };
  defaults?: {
    language?: string;
    platform?: 'web';
    conflictPolicy?: CliABTestRolloutConflictPolicy;
    publishVariants?: boolean;
  };
  abTest?: NonNullable<CliABTestApplyRequest['abTest']>;
  variants?: CliABTestRolloutVariantInput[];
  publish?: {
    variants?: boolean;
    abTest?: boolean;
    probe?: {
      enabled?: boolean;
      sampleCount?: number;
      requiredVariantIds?: string[];
      publicBaseUrl?: string;
      retryCount?: number;
      retryDelayMs?: number;
    };
  };
}

export type CliABTestRolloutStepStatus = 'planned' | 'completed' | 'skipped' | 'failed';

export interface CliABTestRolloutStep {
  id: string;
  status: CliABTestRolloutStepStatus;
  reason?: string;
  [key: string]: unknown;
}

export interface CliABTestRolloutVariantResult {
  id: string;
  sourceType: CliABTestRolloutVariantSource['type'];
  webPlacementId?: string;
  webPlacementAlias?: string;
  funnelId?: string;
  versionId?: string;
  published?: boolean;
  publicUrl?: string;
}

export interface CliABTestRolloutProbeData {
  success: boolean;
  publicUrl: string;
  sampleCount: number;
  observedVariantIds: string[];
  missingVariantIds: string[];
}

export interface CliABTestRolloutApplyData {
  operationId: string;
  dryRun: boolean;
  projectId: string;
  steps: CliABTestRolloutStep[];
  variants: CliABTestRolloutVariantResult[];
  abTest?: CliABTestApplyData;
  publish?: CliABTestPublishData;
  probe?: CliABTestRolloutProbeData;
}

export interface CliProjectListData {
  projects: CliProjectSummary[];
}

export interface CliFunnelListData {
  funnels: CliFunnelSummary[];
}

export type CliFunnelCreateResponseData = CliFunnelCreateData;

export type CliFunnelGraphDryRunResponseData = CliFunnelGraphDryRunData;

export type CliFunnelGraphApplyResponseData = CliFunnelGraphApplyData;

export interface CliFunnelVersionListData {
  versions: CliFunnelVersionSummary[];
}

export type CliFunnelVersionExportResponseData = CliFunnelVersionExportData;

export type CliFunnelDiffResponseData = CliFunnelDiffData;

export type CliDesignSnapshotSourceKind =
  | 'funnel-theme'
  | 'project-theme'
  | 'global-theme'
  | 'funnel-screens';

export interface CliDesignSnapshotRequest {
  source: CliDesignSnapshotSourceKind;
  funnelId?: string;
  versionId?: string;
  /** funnel-screens only: subset of screen ids; omitted/empty = all screens. */
  screenIds?: string[];
  /** project-theme only: explicit project theme id; omitted = active theme. */
  projectThemeId?: string;
  /** global-theme only. */
  globalThemeId?: string;
  /** Render locale; defaults to the source flowDocument defaultLanguage. */
  locale?: string;
  /**
   * Per-screen token emission: 'basic' (default) = Wave 1 byte-compatible
   * (solid canvas color only); 'full' = kind-aware Wave 2 registry vars.
   */
  screenTokens?: 'basic' | 'full';
}

export interface CliDesignSnapshotFileEntry {
  name: string;
  contentBase64: string;
}

export interface CliDesignSnapshotScreenResult {
  screenId: string;
  kind: string;
  subtype?: string;
  fileName: string;
}

export type CliDesignApplySourceKind = 'funnel-theme' | 'project-theme' | 'global-theme';

export interface CliDesignApplyRequest {
  source: CliDesignApplySourceKind;
  funnelId?: string;
  projectThemeId?: string;
  globalThemeId?: string;
  /** --tv-* var name → new value (from `design extract`). */
  set: Record<string, string | number>;
  dryRun?: boolean;
}

export interface CliDesignApplyChange {
  varName: string;
  status: 'applied' | 'skipped';
  reason?: string;
  /** Firestore dot-notation field path inside the target document. */
  fieldPath?: string;
  /** 'theme' or the template screen key the var writes into. */
  target?: string;
  before?: string | number;
  after?: string | number;
}

export interface CliDesignApplyData {
  sourceKind: CliDesignApplySourceKind;
  projectId: string;
  themeId?: string;
  funnelId?: string;
  dryRun: boolean;
  appliedCount: number;
  skippedCount: number;
  changes: CliDesignApplyChange[];
  warnings: string[];
}

export interface CliDesignSnapshotData {
  sourceKind: CliDesignSnapshotSourceKind;
  sourceName: string;
  projectId: string;
  funnelId?: string;
  versionId?: string;
  themeId?: string;
  locale: string;
  screenCount: number;
  screens: CliDesignSnapshotScreenResult[];
  files: CliDesignSnapshotFileEntry[];
  warnings: string[];
}

// ============================================================
// Meta Ads integration CLI verbs (Package B8)
// `segmently integrations meta-ads <verb>` ↔
// /api/cli/v1/projects/{id}/integrations/meta-ads/*
// ============================================================

export interface CliMetaAdsAdAccount {
  id: string;
  accountId?: string;
  name?: string;
  currency?: string;
  timezoneId?: number;
  timezoneName?: string;
  timezoneOffsetHoursUtc?: number;
  accountStatus?: number;
}

export interface CliMetaAdsStatusData {
  connected: boolean;
  providerConfigured?: boolean;
  needsReconnect?: boolean;
  metaUserId?: string;
  displayName?: string;
  scopes?: string[];
  selectedAdAccountIds?: string[];
  accessibleAdAccounts?: CliMetaAdsAdAccount[];
  connectedAt?: string;
  updatedAt?: string;
  accessTokenExpiresAt?: string;
  disconnectedAt?: string;
  graphApiVersion?: string;
  lastConnectionError?: string;
}

export interface CliMetaAdsAdAccountsData {
  adAccounts: CliMetaAdsAdAccount[];
}

export interface CliMetaAdsInsightsPreviewRequest {
  adAccountId: string;
  since: string;
  until: string;
  limit?: number;
}

export interface CliMetaAdsInsightsPreviewData {
  adAccountId: string;
  rowCount: number;
  rows: Array<Record<string, unknown>>;
}

export interface CliMetaAdsMetadataPreviewRequest {
  adAccountId: string;
  limit?: number;
  maxPages?: number;
  namePrefix?: string;
}

export interface CliMetaAdsMetadataPreviewData {
  adAccountId: string;
  counts: {
    campaigns: number;
    adsets: number;
    ads: number;
    creatives: number;
  };
  pagesRead: {
    campaigns: number;
    adsets: number;
    ads: number;
    creatives: number;
  };
}

export interface CliMetaAdsSyncDeliveryRequest {
  adAccountId: string;
  since: string;
  until: string;
  limit?: number;
}

export interface CliMetaAdsSyncDeliveryData {
  adAccountId: string;
  syncRunId: string;
  rowsRead: number;
  rowsWritten: number;
  deliveryRowsWritten: number;
  actionRowsWritten: number;
  pagesRead: number;
  /** True when the per-run page cap cut the read — re-sync narrower slices. */
  truncated: boolean;
  tableId: string;
  actionTableId: string;
  datasetId: string;
}

export interface CliMetaAdsSyncMetadataRequest {
  adAccountId: string;
  limit?: number;
  maxPages?: number;
  namePrefix?: string;
  /**
   * Fetch ONLY the ad-referenced creatives missing from the creative dim
   * (anti-join), by id — closes the archived/dynamic-creative gap the
   * account-level listing cannot see. Ignores limit/maxPages/namePrefix.
   */
  creativesByIds?: boolean;
}

export interface CliMetaAdsSyncCreativesByIdsData {
  adAccountId: string;
  syncRunId: string;
  missingCreativeIds: number;
  fetchedCreatives: number;
  unfetchableCreativeIds: string[];
  rowsWritten: { creatives: number };
  datasetId: string;
}

export interface CliMetaAdsSyncMetadataData {
  adAccountId: string;
  syncRunId: string;
  rowsRead: {
    campaigns: number;
    adsets: number;
    ads: number;
    creatives: number;
  };
  rowsWritten: {
    campaigns: number;
    adsets: number;
    ads: number;
    creatives: number;
  };
  datasetId: string;
}

/** Feature B (meta-utm-mapping-coverage) — project-declared UTM→Meta-entity mapping. */
export type CliMetaAdsUtmMappingField = 'utm_campaign' | 'utm_term' | 'utm_content' | 'utm_id';
export type CliMetaAdsUtmEntity = 'campaign' | 'adset' | 'ad';

export interface CliMetaAdsUtmMapping {
  campaign?: CliMetaAdsUtmMappingField | null;
  adset?: CliMetaAdsUtmMappingField | null;
  ad?: CliMetaAdsUtmMappingField | null;
  confirmedAt?: string;
  confirmedBy?: string;
  detectedCoverage?: { campaign?: number; adset?: number; ad?: number };
}

export interface CliMetaAdsUtmMappingData {
  utmMapping: CliMetaAdsUtmMapping | null;
}

export interface CliMetaAdsUtmMappingDetectRequest {
  adAccountId?: string;
}

export interface CliMetaAdsUtmMappingDetectData {
  analyzedSessions: number;
  windowDays: number;
  fields: Record<CliMetaAdsUtmMappingField, {
    tagged: number;
    idShare: Record<CliMetaAdsUtmEntity, number>;
    nameShare: Record<CliMetaAdsUtmEntity, number>;
  }>;
  suggestion: Partial<Record<CliMetaAdsUtmEntity, { field: CliMetaAdsUtmMappingField; share: number }>>;
  nameEvidence: Partial<Record<CliMetaAdsUtmEntity, { field: CliMetaAdsUtmMappingField; share: number }>>;
  thresholds: { suggest: number; crossTypeMax: number };
  utmMapping: CliMetaAdsUtmMapping | null;
}

export interface CliMetaAdsUtmMappingSetRequest {
  mapping: CliMetaAdsUtmMapping | null;
}

/** A reviewed external result; source provenance and audit data are owned by the server. */
export interface CliInsightVariableImportRequest {
  collectionPolicyVersion?: 'measurement-v1';
  questionPolicies?: Record<string, InsightQuestionCollectionPolicy>;
  requestId: string;
  analysisId: string;
  analysisName?: string;
  categories: string[];
  taskScope: 'global' | 'task';
  taskId?: string;
  variables: Array<{ variable: Record<string, unknown>; optionMappings?: Array<Record<string, unknown>>;
    dataUsage?: { items: Array<Record<string, unknown>> } }>;
  explanation: { overallStrategy: string; identificationFlow: string; confidenceEstimate: string; perVariable: Array<Record<string, unknown>> };
  source?: string;
}

export type CliPaymentProvider = 'stripe' | 'paddle';
export type CliPaddleEnvironment = 'sandbox' | 'production';

/** P6 owns these counters. Status omits them until verified counter projection is implemented. */
/** Shared wire health contract; missing coverage never implies zero counters. */
export type CliPaddleWebhookHealth = import('@segmently/schema-models/entities').PaddleWebhookHealth;

/** CLI deliberately omits the public browser token and requires the secret-presence flag. */
export type CliPaddleEnvironmentStatus = Omit<
  import('@segmently/schema-models/entities').PaddleEnvironmentStatus,
  'clientToken' | 'hasWebhookSecret'
> & {
  hasWebhookSecret: boolean;
  /** Saved reference/token presence only, not Secret Manager availability or credential validity.
   * Optional for older servers; omitted means unknown, never false. */
  hasApiKeySecretRef?: boolean;
  hasClientToken?: boolean;
};

export type CliPaddleStatusData = Pick<
  import('@segmently/schema-models/entities').PaddleConnectionStatus,
  'connected' | 'activeEnvironment' | 'webhookDiagnostic'
> & {
  sandbox: CliPaddleEnvironmentStatus;
  production: CliPaddleEnvironmentStatus;
  /** Optional live, exact-domain observation requested separately from saved status. */
  checkoutDomain?: import('./paddleCheckoutReadiness.types').PaddleCheckoutDomainEvidence;
};

/** Mirrors builder-service PaddleService.PaddleProduct (structural compatibility is load-bearing). */
export interface CliPaddleProductSummary {
  id: string;
  name: string;
  description?: string;
  taxCategory: string;
  imageUrl?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

/** Keeps paid trials, payment-method requirements and country overrides from the shared catalog. */
export type CliPaddlePriceSummary = import('./paddleCatalog.types').PaddleCatalogPrice;

export interface CliPaddleProductsData {
  environment: CliPaddleEnvironment;
  products: CliPaddleProductSummary[];
}

export interface CliPaddlePricesData {
  environment: CliPaddleEnvironment;
  productId?: string;
  prices: CliPaddlePriceSummary[];
}

export interface CliPaddlePaywallProductEnsureRequest {
  version?: 'cli.paddle-paywall-product.v1' | string;
  name: string;
  description?: string;
  type?: CliStripePaywallProductType;
  paddlePriceId: string;
  /** Optional — derived from the price when omitted; validated against it when given. */
  paddleProductId?: string;
  /** CLI writes are sandbox-only (Stripe parity: test-mode-only writes). */
  paddleEnvironment: 'sandbox';
  /** Legacy stored mode. P1/P4 own the explicit Paddle checkout-mode migration. */
  checkoutMode?: CliStripePaywallCheckoutMode;
}

export interface CliPaddlePaywallProductSummary {
  id: string;
  name: string;
  description?: string | null;
  paymentProvider: Extract<CliPaymentProvider, 'paddle'>;
  paddleProductId: string;
  paddlePriceId: string;
  paddleEnvironment: CliPaddleEnvironment;
  type: CliStripePaywallProductType;
  checkoutMode: CliStripePaywallCheckoutMode;
  subscription?: {
    interval: CliStripePaywallInterval;
    intervalCount: number;
    trialDays?: number;
  };
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** D-9 fix: the CLI type system can express a non-Stripe provider. */
export type CliPaywallProductSummary = CliStripePaywallProductSummary | CliPaddlePaywallProductSummary;

export interface CliPaddlePaywallProductEnsureData {
  created: boolean;
  paddleEnvironment: 'sandbox';
  paywallProduct: CliPaddlePaywallProductSummary;
  /** The verified sandbox price (present on creation; omitted on idempotent reuse). */
  paddlePrice?: CliPaddlePriceSummary;
}
